<template>
  <div>
    <div class="page-header">
      <button class="btn btn-secondary back-button" @click="goBack">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <h4 class="table-page-header client-name">{{ clientName }}</h4>

      <!-- Tab Buttons in Header -->
      <div class="button-toggle-row">
        <el-button
          :type="activeTab === 'overview' ? 'primary' : 'default'"
          @click="activeTab = 'overview'"
          class="toggle-button"
        >
          Overview
        </el-button>
        <el-button
          v-if="isAdmin"
          :type="activeTab === 'authorizations' ? 'primary' : 'default'"
          @click="activeTab = 'authorizations'"
          class="toggle-button"
        >
          Authorizations
        </el-button>
        <el-button
          :type="activeTab === 'calendar' ? 'primary' : 'default'"
          @click="activeTab = 'calendar'"
          class="toggle-button"
        >
          Calendar
        </el-button>
        <el-button
          :type="activeTab === 'notes' ? 'primary' : 'default'"
          @click="activeTab = 'notes'"
          class="toggle-button"
        >
          Notes
        </el-button>
        <el-button
          v-if="client && (client.payer_type === 'DSPD' || client.payer_type === 'Private')"
          :type="activeTab === 'goals' ? 'primary' : 'default'"
          @click="activeTab = 'goals'"
          class="toggle-button"
        >
          Goals
        </el-button>
        <el-button
          v-if="hasDSPDAuth"
          :type="activeTab === 'medications' ? 'primary' : 'default'"
          @click="activeTab = 'medications'"
          class="toggle-button"
        >
          Medications
        </el-button>
        <el-button
          :type="activeTab === 'training' ? 'primary' : 'default'"
          @click="activeTab = 'training'"
          class="toggle-button"
        >
          Training
        </el-button>
        <el-button
          :type="activeTab === 'details' ? 'primary' : 'default'"
          @click="activeTab = 'details'"
          class="toggle-button"
        >
          Details
        </el-button>
        <el-button
          v-if="client && (client.payer_type === 'DSPD' || client.payer_type === 'Private') && !isCaregiver"
          :type="activeTab === 'intake' ? 'primary' : 'default'"
          @click="activeTab = 'intake'"
          class="toggle-button"
        >
          Intake
        </el-button>
        <el-button
          v-if="client && client.payer_type === 'DSPD'"
          :type="activeTab === 'supportStrategies' ? 'primary' : 'default'"
          @click="activeTab = 'supportStrategies'"
          class="toggle-button"
        >
          Support Strategies
        </el-button>
        <el-button
          v-if="client && (client.payer_type === 'DSPD' || client.payer_type === 'Private')"
          :type="activeTab === 'cst' ? 'primary' : 'default'"
          @click="activeTab = 'cst'"
          class="toggle-button"
        >
          CST
        </el-button>
        <el-button
          v-if="!isCaregiver"
          :type="activeTab === 'compliance' ? 'primary' : 'default'"
          @click="activeTab = 'compliance'; loadClientComplianceDocs(); loadQuarterlyReports(); loadComplianceStrategies(); loadClientDocuments();"
          class="toggle-button"
        >
          Compliance
        </el-button>
        <el-button
          v-if="!isCaregiver"
          :type="activeTab === 'units' ? 'primary' : 'default'"
          @click="activeTab = 'units'"
          class="toggle-button"
        >
          Units
        </el-button>
        <el-button
          v-if="isAdmin"
          :type="activeTab === 'access' ? 'primary' : 'default'"
          @click="activeTab = 'access'; loadUserAccess();"
          class="toggle-button"
        >
          Access
        </el-button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="client-tab-content">
      <!-- Overview / Face Sheet Tab -->
      <div v-if="activeTab === 'overview'" class="client-tab-pane">
        <div v-if="loadingClient" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>
        <div v-else-if="client" class="face-sheet">
          <!-- Top Section: Photo + Key Info -->
          <div class="face-sheet-header">
            <div class="face-sheet-photo" @click="triggerPhotoUpload" title="Click to change photo">
              <img
                v-if="clientPhotoUrl"
                :src="clientPhotoUrl"
                alt="Client photo"
                class="client-photo"
              />
              <div v-else class="client-photo-placeholder">
                <i class="fa fa-user"></i>
              </div>
              <div class="photo-overlay">
                <i class="fa fa-camera"></i>
              </div>
              <input
                type="file"
                ref="photoInput"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style="display: none"
                @change="handlePhotoUpload"
              />
            </div>
            <div class="face-sheet-name">
              <h2>{{ client.first_name }} {{ client.last_name }}</h2>
              <span v-if="client.goes_by" class="goes-by">"{{ client.goes_by }}"</span>
              <div class="face-sheet-badges">
                <span class="badge badge-info">{{ client.payer_type || 'N/A' }}</span>
                <span v-if="client.dspd_pid" class="badge badge-secondary">PID: {{ client.dspd_pid }}</span>
                <span v-if="client.vr_client_id" class="badge badge-secondary">VR ID: {{ client.vr_client_id }}</span>
              </div>
            </div>
          </div>

          <!-- Info Cards Grid -->
          <div class="face-sheet-grid">
            <!-- Demographics Card -->
            <div class="face-sheet-card">
              <h5><i class="fa fa-id-card"></i> Demographics</h5>
              <div class="face-sheet-field">
                <label>Date of Birth</label>
                <span>{{ formatDateToMDY(client.dob) || '—' }}</span>
              </div>
              <div class="face-sheet-field">
                <label>Gender</label>
                <span>{{ client.gender || '—' }}</span>
              </div>
              <div v-if="client.height_inches" class="face-sheet-field">
                <label>Height</label>
                <span>{{ client.height_inches }}"</span>
              </div>
              <div v-if="client.weight_lbs" class="face-sheet-field">
                <label>Weight</label>
                <span>{{ client.weight_lbs }} lbs</span>
              </div>
              <div v-if="client.spouse" class="face-sheet-field">
                <label>Spouse</label>
                <span>{{ client.spouse }}</span>
              </div>
            </div>

            <!-- Contact Card -->
            <div class="face-sheet-card">
              <h5><i class="fa fa-phone"></i> Contact</h5>
              <div v-if="client.best_contact_phone" class="face-sheet-field">
                <label>Mobile Phone</label>
                <span>{{ client.best_contact_phone }}</span>
              </div>
              <div v-if="client.secondary_phone" class="face-sheet-field">
                <label>Home Phone</label>
                <span>{{ client.secondary_phone }}</span>
              </div>
              <div v-if="client.client_email || client.best_contact_email" class="face-sheet-field">
                <label>Email</label>
                <span>{{ client.client_email || client.best_contact_email }}</span>
              </div>
              <div v-if="client.best_contact_name" class="face-sheet-field">
                <label>Best Contact</label>
                <span>{{ client.best_contact_name }} <template v-if="client.best_contact_phone">– {{ client.best_contact_phone }}</template></span>
              </div>
            </div>

            <!-- Address Card -->
            <div class="face-sheet-card">
              <h5><i class="fa fa-map-marker"></i> Address</h5>
              <template v-if="client.addresses && client.addresses.length > 0">
                <div v-for="(addr, idx) in client.addresses" :key="'addr-' + idx" class="face-sheet-address">
                  <div v-if="client.addresses.length > 1" class="address-label">{{ idx === 0 ? 'Primary' : 'Secondary' }}</div>
                  <div>{{ addr.address1 }}</div>
                  <div v-if="addr.address2">{{ addr.address2 }}</div>
                  <div>{{ addr.city }}<template v-if="addr.city && addr.state">,</template> {{ addr.state }} {{ addr.zip }}</div>
                </div>
              </template>
              <div v-else class="text-muted">No address on file</div>
            </div>

            <!-- Counselor, Support Coordinator & Admin Card -->
            <div class="face-sheet-card">
              <h5><i class="fa fa-user-md"></i> Counselor, Support Coordinator &amp; Admin</h5>
              <div class="face-sheet-field">
                <label>Counselor</label>
                <span v-if="client.counselor">{{ client.counselor.name }}<template v-if="client.counselor.phone"> – {{ client.counselor.phone }}</template></span>
                <span v-else class="text-muted">Not assigned</span>
              </div>
              <div class="face-sheet-field">
                <label>Support Coordinator</label>
                <span v-if="client.support_coordinator">{{ client.support_coordinator.first_name }} {{ client.support_coordinator.last_name }}<template v-if="client.support_coordinator.email"> – {{ client.support_coordinator.email }}</template></span>
                <span v-else class="text-muted">Not assigned</span>
              </div>
              <div class="face-sheet-field">
                <label>Administrator</label>
                <span v-if="client.admin">{{ client.admin.first_name }} {{ client.admin.last_name }}</span>
                <span v-else class="text-muted">Not assigned</span>
              </div>
            </div>

            <!-- Emergency Contacts / Guardians Card -->
            <div class="face-sheet-card face-sheet-card-wide">
              <h5><i class="fa fa-exclamation-triangle"></i> Emergency Contacts &amp; Guardians</h5>
              <template v-if="emergencyAndGuardianContacts.length > 0">
                <div class="face-sheet-contacts-grid">
                  <div
                    v-for="contact in emergencyAndGuardianContacts"
                    :key="'contact-' + contact.id"
                    class="face-sheet-contact"
                  >
                    <div class="contact-name">{{ contact.name }}</div>
                    <div v-if="contact.relationship" class="contact-detail">{{ contact.relationship }}</div>
                    <div class="contact-type-badge">{{ formatContactType(contact.contact_type) }}</div>
                    <div v-if="contact.phone" class="contact-detail"><i class="fa fa-phone"></i> {{ contact.phone }}</div>
                    <div v-if="contact.email" class="contact-detail"><i class="fa fa-envelope"></i> {{ contact.email }}</div>
                  </div>
                </div>
              </template>
              <div v-else class="text-muted">No emergency contacts or guardians on file</div>
            </div>

            <!-- Administrative Details Card -->
            <div v-if="client.medicaid_number || client.initiated_services" class="face-sheet-card">
              <h5><i class="fa fa-briefcase"></i> Administrative Details</h5>
              <div v-if="client.medicaid_number" class="face-sheet-field">
                <label>Medicaid Number</label>
                <span>{{ client.medicaid_number }}</span>
              </div>
              <div v-if="client.initiated_services" class="face-sheet-field">
                <label>Services Started</label>
                <span>{{ formatDateToMDY(client.initiated_services) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4">
          <p>No client details available</p>
        </div>
      </div>

      <div v-if="activeTab === 'authorizations'" class="client-tab-pane">
        <div class="row search-wrapper">
          <div class="col-sm-6 mb-4">
            <el-input
              v-model="searchQuery"
              placeholder="Search"
              @input="onSearch"
            >
              <i
                slot="prepend"
                class="fa fa-search"
                style="padding-left: 4px; padding-right: 4px"
              ></i>
            </el-input>
          </div>
          <div class="col-sm-3 mb-4">
            <AIVSelect
              v-model="activeStatusFilter"
              :options="activeStatusOptions"
              placeholder="Filter by status"
              @change="onStatusFilterChange"
            />
          </div>
        </div>
        <AIVTable
          :columns="authorizationColumns"
          :data="authorizations"
          :pagination="pagination"
          :item-name="'authorizations'"
          :row-clickable="true"
          @pageChange="fetchAuthorizations"
          @pageSizeChange="handlePageSizeChange"
          @sort-change="handleSortChange"
          @row-click="viewAuthorizationDetails"
        >
          <template #cell-actions="{ row }">
            <button
              class="btn btn-sm btn-info mr-2"
              @click.stop="viewApprovedShifts(row)"
              title="View Approved Shifts"
            >
              <i class="fa-solid fa-calendar-check"></i>
            </button>
            <button
              class="btn btn-sm btn-warning mr-2"
              @click.stop="editAuthorization(row)"
              title="Edit"
            >
              <i class="fa-solid fa-pen-to-square"></i>
            </button>
            <button
              v-if="row.status !== 'inactive'"
              class="btn btn-sm btn-danger mr-2"
              @click.stop="
                $confirmAction(
                  () => deactivateAuthorization(row),
                  `Are you sure you want to deactivate authorization ${row.auth_number}?`,
                )
              "
              title="Deactivate"
            >
              <i class="fa-solid fa-ban"></i>
            </button>
            <button
              v-if="row.status === 'inactive'"
              class="btn btn-sm btn-success mr-2"
              @click.stop="
                $confirmAction(
                  () => activateAuthorization(row),
                  `Are you sure you want to reactivate authorization ${row.auth_number}?`,
                )
              "
              title="Reactivate"
            >
              <i class="fa-solid fa-check-circle"></i>
            </button>
            <button
              class="btn btn-sm btn-danger"
              @click.stop="
                $confirmAction(
                  () => deleteAuthorization(row),
                  `Are you sure you want to permanently delete authorization ${row.auth_number}? This action cannot be undone.`,
                )
              "
              title="Delete Permanently"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
          </template>
        </AIVTable>
      </div>
      <div v-if="activeTab === 'calendar'" class="client-tab-pane">
        <div class="client-schedule-container">
          <div class="schedule-content">
            <div class="modern-calendar-wrapper">
              <!-- Add New Shift Button -->
              <div class="calendar-header-actions">
                <el-button
                  v-if="canAddShift"
                  type="primary"
                  size="small"
                  @click="createShift"
                  class="add-shift-button"
                >
                  <i class="fa-solid fa-plus"></i> Add New Shift
                </el-button>
              </div>
              <!-- Modern Legend -->
              <div class="modern-legend-row">
                <div class="legend-groups">
                  <div class="legend-group">
                    <div class="legend-group-header">
                      <i class="fas fa-info-circle"></i>
                      <span>Status:</span>
                    </div>
                    <div
                      class="legend-item"
                      v-if="userRole === 'Admin' || userRole === 'Lead'"
                    >
                      <div class="legend-color status-unassigned"></div>
                      <span>Unassigned</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-scheduled"></div>
                      <span>Scheduled</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-clocked-in"></div>
                      <span>Clocked In</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-clocked-out"></div>
                      <span>Clocked Out</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-approved"></div>
                      <span>Approved</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-missed"></div>
                      <span>Missed</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Month Navigation Controls -->
              <div class="month-navigation">
                <button @click="goToPreviousMonth" class="nav-btn">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="current-month-display">
                  {{ currentMonthYear }}
                </div>
                <button @click="goToNextMonth" class="nav-btn">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
              <div class="calendar-view">
                <MonthView
                  v-if="!loadingShifts"
                  :currentMonth="currentMonth"
                  :daysInMonth="daysInMonth"
                  :dayNames="dayNames"
                  :eventsForDate="shiftsForDate"
                  :isToday="isToday"
                  :showWeeklySummary="true"
                  pageType="client"
                  @show-details="viewShiftDetails"
                />
                <div v-else class="text-center p-4">
                  <i class="fa fa-spinner fa-spin"></i> Loading schedule...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="activeTab === 'notes'" class="client-tab-pane">
        <div class="client-notes-container">
          <!-- Add New General Note Button -->
          <div class="notes-header-actions">
            <el-button
              type="primary"
              size="small"
              @click="openAddNoteModal"
              class="add-note-button"
            >
              <i class="fa-solid fa-plus"></i> Add General Note
            </el-button>
          </div>

          <!-- Search -->
          <div class="row search-wrapper">
            <div class="col-sm-6 mb-4">
              <el-input
                v-model="notesSearch"
                placeholder="Search"
                clearable
                @clear="clearNotesSearch"
                @input="filterAllNotes"
              >
                <i
                  slot="prepend"
                  class="fa fa-search"
                  style="padding-left: 4px; padding-right: 4px"
                ></i>
              </el-input>
            </div>
            <el-button class="mb-4" @click="clearNotesSearch">Clear</el-button>
          </div>

          <div v-if="loadingAllNotes" class="text-center">
            <i class="fa fa-spinner fa-spin"></i> Loading notes...
          </div>

          <div v-else-if="filteredAllNotes.length === 0" class="no-notes">
            <p>No notes found for this client.</p>
          </div>

          <div v-else class="all-notes-list">
            <div
              v-for="note in filteredAllNotes"
              :key="note.id"
              class="note-card"
            >
              <div class="note-header">
                <div class="note-date-time">
                  <div class="date-badge-row">
                    <strong>{{
                      formatDate(note.date) || note.date || "No Date Available"
                    }}</strong>
                    <span
                      v-if="note.type === 'general'"
                      class="note-type-badge general-badge"
                      >General Note</span
                    >
                    <span
                      v-else-if="note.type === 'shift'"
                      class="note-type-badge shift-badge"
                      >Shift Note</span
                    >
                    <span
                      v-else-if="note.type === 'schedule'"
                      class="note-type-badge schedule-badge"
                      >Schedule Note</span
                    >
                  </div>
                  <span
                    v-if="note.actual_start && note.actual_end"
                    class="time-range"
                    >{{
                      formatTimeRange(note.actual_start, note.actual_end)
                    }}</span
                  >
                </div>
                <span v-if="note.caregiver" class="caregiver-name">{{
                  note.caregiver
                }}</span>
                <span v-else-if="note.createdBy" class="caregiver-name"
                  >Created by: {{ note.createdBy }}</span
                >
              </div>

              <div v-if="note.serviceCode" class="note-meta">
                <strong>Service Code:</strong> {{ note.serviceCode }}
              </div>

              <div v-if="note.has_jc_activities" class="activities-section">
                <div><strong>Job Coaching Activities:</strong></div>
                <div>{{ note.jc_activities.join(", ") }}</div>
              </div>

              <div
                v-if="note.activities && note.activities.length"
                class="activities-section"
              >
                <div
                  v-for="(act, idx) in Array.isArray(note.activities)
                    ? note.activities
                    : [note.activities]"
                  :key="'act-' + note.id + '-' + idx"
                >
                  {{ formatActivityItem(act) }}
                </div>
              </div>

              <div v-if="note.mileage && formatMileage(note.mileage).length" class="notes-section">
                <div><strong>Mileage:</strong></div>
                <ul class="qa-list">
                  <li
                    v-for="(m, idx) in formatMileage(note.mileage)"
                    :key="'m-' + note.id + '-' + idx"
                  >
                    {{ m }}
                  </li>
                </ul>
              </div>

              <div v-if="note.jd_activity" class="notes-section">
                <strong>Job Development Activity:</strong> {{ note.jd_activity }}
              </div>
              <div v-if="note.jd_outcome" class="notes-section">
                <strong>Outcome:</strong> {{ note.jd_outcome }}
              </div>
              <div v-if="note.jd_next_steps" class="notes-section">
                <strong>Next Steps:</strong> {{ note.jd_next_steps }}
              </div>

              <div
                v-if="note.questionAnswers && note.questionAnswers.length"
                class="notes-section"
              >
                <div><strong>Shift Note Questions:</strong></div>
                <ul class="qa-list">
                  <li
                    v-for="(qa, idx) in note.questionAnswers"
                    :key="'q-' + note.id + '-' + idx"
                  >
                    {{ qa.questionText }}: {{ qa.answer }}
                  </li>
                </ul>
              </div>

              <div
                v-if="note.goalAnswers && note.goalAnswers.length"
                class="notes-section"
              >
                <div><strong>Client Goal Questions:</strong></div>
                <ul class="qa-list">
                  <li
                    v-for="(ga, idx) in note.goalAnswers"
                    :key="'g-' + note.id + '-' + idx"
                  >
                    {{ ga.goalText }}: {{ ga.answer }}
                  </li>
                </ul>
              </div>

              <div
                v-if="note.type !== 'shift' && note.notes"
                class="notes-section"
              >
                <p class="shift-notes">{{ note.notes }}</p>
              </div>

              <div v-if="note.type === 'shift' && note.shiftNotes" class="notes-section">
                <strong>
                  {{
                    (note.questionAnswers && note.questionAnswers.length) ||
                    (note.goalAnswers && note.goalAnswers.length)
                      ? "Concerns:"
                      : "Shift Notes:"
                  }}
                </strong>
                <p class="shift-notes">{{ note.shiftNotes }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add General Note Modal -->
      <el-dialog
        title="Add General Note"
        :visible.sync="showAddNoteModal"
        width="600px"
        @close="closeAddNoteModal"
      >
        <el-form class="note-form">
          <el-form-item>
            <el-input
              v-model="newNoteText"
              type="textarea"
              :rows="6"
              placeholder="Enter a general note for this client..."
              autofocus
            ></el-input>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="closeAddNoteModal">Cancel</el-button>
          <el-button
            type="primary"
            @click="handleCreateNote"
            :disabled="!newNoteText || !newNoteText.trim()"
          >
            Add Note
          </el-button>
        </span>
      </el-dialog>

      <!-- Add/Edit Goal Modal -->
      <AIVDialog
        v-model="$modal.state.add_edit_goal"
        :title="editingGoal ? 'Edit Goal Question' : 'Add Goal Question'"
        width="600px"
      >
        <el-form class="goal-form" label-position="top">
          <el-form-item label="Question Type">
            <div class="question-type-toggle-section">
              <div class="button-toggle-row">
                <el-button
                  :type="
                    goalFormData.question_type === 'free_response'
                      ? 'primary'
                      : 'default'
                  "
                  @click="goalFormData.question_type = 'free_response'"
                  class="toggle-button"
                >
                  Free Response
                </el-button>
                <el-button
                  :type="
                    goalFormData.question_type === 'yes_no'
                      ? 'primary'
                      : 'default'
                  "
                  @click="goalFormData.question_type = 'yes_no'"
                  class="toggle-button"
                >
                  Yes/No Question
                </el-button>
              </div>
            </div>
          </el-form-item>
          <el-form-item label="Question Text">
            <el-input
              v-model="goalFormData.question_text"
              type="textarea"
              :rows="4"
              :placeholder="
                goalFormData.question_type === 'yes_no'
                  ? 'Enter a yes/no question (e.g., Has the client shown improvement in communication?)'
                  : 'Enter a free response question (e.g., What progress has the client made this month?)'
              "
              autofocus
            ></el-input>
          </el-form-item>
        </el-form>
        <template slot="footer">
          <div class="text-center">
            <el-button @click="closeGoalModal">Cancel</el-button>
            <el-button
              type="primary"
              round
              @click="handleSaveGoal"
              :disabled="
                !goalFormData.question_text ||
                !goalFormData.question_text.trim()
              "
            >
              {{ editingGoal ? "Update" : "Add" }} Question
            </el-button>
          </div>
        </template>
      </AIVDialog>

      <!-- Training Tab -->
      <div v-if="activeTab === 'training'" class="client-tab-pane">
        <div class="training-content">
          <h5>Caregiver Training Status</h5>
          <p class="text-muted mb-3">
            View and manage training records for caregivers trained on this
            client.
          </p>

          <div v-if="loadingTraining" class="text-center p-4">
            <i class="fa fa-spinner fa-spin"></i> Loading training records...
          </div>

          <div v-else-if="trainingRecords.length === 0" class="text-center p-4">
            <p>No caregivers have been trained on this client yet.</p>
          </div>

          <div v-else class="training-records">
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Caregiver Name</th>
                    <th>Status</th>
                    <th>Submitted Date</th>
                    <th>Reviewed By</th>
                    <th>Reviewed Date</th>
                    <th v-if="isAdmin">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in trainingRecords" :key="record.id">
                    <td>
                      <strong
                        >{{ record.caregiver?.first_name }}
                        {{ record.caregiver?.last_name }}</strong
                      >
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="{
                          'badge-warning': record.status === 'pending',
                          'badge-success': record.status === 'approved',
                          'badge-danger': record.status === 'rejected',
                        }"
                      >
                        {{
                          record.status.charAt(0).toUpperCase() +
                          record.status.slice(1)
                        }}
                      </span>
                    </td>
                    <td>{{ formatDate(record.submitted_at) }}</td>
                    <td>
                      <span v-if="record.approver">
                        {{ record.approver.first_name }}
                        {{ record.approver.last_name }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="record.approved_at">{{
                        formatDate(record.approved_at)
                      }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td v-if="isAdmin">
                      <button
                        v-if="record.status === 'pending'"
                        class="btn btn-sm btn-success mr-1"
                        @click="approveTraining(record)"
                        title="Approve"
                      >
                        <i class="fa fa-check"></i>
                      </button>
                      <button
                        v-if="record.status === 'pending'"
                        class="btn btn-sm btn-danger"
                        @click="rejectTraining(record)"
                        title="Reject"
                      >
                        <i class="fa fa-times"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Details Tab -->
      <div v-if="activeTab === 'details'" class="client-tab-pane">
        <div v-if="loadingClient" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading client details...
        </div>
        <div v-else-if="client" class="client-details-content">
          <!-- Details Sub-Tabs -->
          <div class="button-toggle-row mb-3">
            <el-button
              :type="detailsSubTab === 'payer' ? 'primary' : 'default'"
              @click="detailsSubTab = 'payer'"
              class="toggle-button"
            >
              Payer
            </el-button>
            <el-button
              :type="detailsSubTab === 'demographics' ? 'primary' : 'default'"
              @click="detailsSubTab = 'demographics'"
              class="toggle-button"
            >
              Demographics
            </el-button>
            <el-button
              :type="detailsSubTab === 'addresses' ? 'primary' : 'default'"
              @click="detailsSubTab = 'addresses'"
              class="toggle-button"
            >
              Addresses
            </el-button>
            <el-button
              :type="detailsSubTab === 'service' ? 'primary' : 'default'"
              @click="detailsSubTab = 'service'"
              class="toggle-button"
            >
              Service Information
            </el-button>
            <el-button
              v-if="client.payer_type === 'DSPD' || client.payer_type === 'Private'"
              :type="detailsSubTab === 'medical' ? 'primary' : 'default'"
              @click="detailsSubTab = 'medical'"
              class="toggle-button"
            >
              Medical Information
            </el-button>
            <el-button
              :type="detailsSubTab === 'contacts' ? 'primary' : 'default'"
              @click="detailsSubTab = 'contacts'"
              class="toggle-button"
            >
              Contacts
            </el-button>
          </div>

          <el-form
            label-position="top"
            label-width="120px"
            class="full-width-form"
            :disabled="!canEditClient"
          >
            <!-- Payer Sub-Tab -->
            <div v-if="detailsSubTab === 'payer'">
              <AIVRow
                :record.sync="client"
                :disabled="!canEditClient"
                :fields="
                  client.payer_type === 'DSPD' || client.payer_type === 'Private'
                    ? [
                        {
                          label: 'Payer Type',
                          key: 'payer_type',
                          type: 'select',
                          options: payerTypeOptions,
                          placeholder: 'Select Payer Type',
                        },
                        { label: client.payer_type === 'Private' ? 'PP Client ID' : 'DSPD PID', key: 'dspd_pid' },
                        { label: '', key: '_placeholder', disabled: true },
                      ]
                    : [
                        {
                          label: 'Payer Type',
                          key: 'payer_type',
                          type: 'select',
                          options: payerTypeOptions,
                          placeholder: 'Select Payer Type',
                        },
                        { label: 'VR Client ID', key: 'vr_client_id' },
                        {
                          label: 'Office',
                          key: 'id_offices',
                          type: 'select',
                          options: officeOptions,
                          placeholder: 'Select Office',
                        },
                      ]
                "
              />
            </div>

            <!-- Demographics Sub-Tab -->
            <div v-if="detailsSubTab === 'demographics'">
              <AIVRow
                :record.sync="client"
                :fields="[
                  { label: 'First Name', key: 'first_name' },
                  { label: 'Last Name', key: 'last_name' },
                  { label: 'Date of Birth', key: 'dob', type: 'date' },
                ]"
              />
              <!-- DSPD/PP Client Contacts -->
              <template v-if="client.payer_type === 'DSPD' || client.payer_type === 'Private'">
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    { label: 'Client Email', key: 'client_email' },
                    { label: 'Home Phone', key: 'secondary_phone' },
                    { label: 'Mobile Phone', key: 'best_contact_phone' },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Gender',
                      key: 'gender',
                      type: 'select',
                      options: [
                        { id: 'Male', name: 'Male' },
                        { id: 'Female', name: 'Female' },
                        { id: 'Other', name: 'Other' },
                      ],
                    },
                    { label: 'Goes By', key: 'goes_by' },
                    { label: 'Spouse', key: 'spouse' },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    { label: 'Height', key: 'height_inches' },
                    { label: 'Weight', key: 'weight_lbs' },
                    { label: 'Pets', key: 'pets' },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    { label: 'Hobbies', key: 'hobbies', type: 'textarea' },
                  ]"
                />
              </template>
              <!-- VR Client Fields -->
              <template v-else>
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    { label: 'Best Contact', key: 'best_contact' },
                    { label: 'Best Contact Name', key: 'best_contact_name' },
                    { label: 'Best Contact Phone', key: 'best_contact_phone' },
                    { label: 'Best Contact Email', key: 'best_contact_email' },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    { label: 'Client Email', key: 'client_email' },
                    { label: 'Secondary Phone', key: 'secondary_phone' },
                    {
                      label: 'Secondary Phone Owner',
                      key: 'secondary_phone_owner',
                    },
                  ]"
                />
              </template>
            </div>

            <!-- Addresses Sub-Tab -->
            <div v-if="detailsSubTab === 'addresses'" class="address-section">
              <div
                v-for="(addr, index) in client.addresses"
                :key="index"
                class="address-block mb-3"
              >
                <div
                  class="address-header d-flex justify-content-between align-items-center mb-2"
                >
                  <h6>
                    Address {{ index + 1 }}
                    <el-tag
                      v-if="addr.geocode && addr.geocode.trim()"
                      size="mini"
                      type="success"
                      effect="plain"
                      style="margin-left: 8px; vertical-align: middle;"
                    >
                      Geocoded
                    </el-tag>
                    <el-tag
                      v-else
                      size="mini"
                      type="warning"
                      effect="plain"
                      style="margin-left: 8px; vertical-align: middle;"
                    >
                      Not Geocoded
                    </el-tag>
                    <el-button
                      v-if="!addr.geocode || !addr.geocode.trim()"
                      type="text"
                      size="mini"
                      style="margin-left: 6px; color: #6ba6cd;"
                      @click="openMapPinPicker(index)"
                    >
                      <i class="fa-solid fa-map-location-dot"></i> Set on Map
                    </el-button>
                  </h6>
                  <el-button
                    v-if="client.addresses.length > 1 && canEditClient"
                    type="danger"
                    size="small"
                    @click="removeAddress(index)"
                  >
                    Remove
                  </el-button>
                </div>
                <div class="row mb-3 w-100 mx-0">
                  <div class="col-md-6 px-2">
                    <label class="d-block">Address1</label>
                    <AIVAddressAutocomplete
                      :value="addr.address1"
                      @input="val => $set(client.addresses[index], 'address1', val)"
                      @place-selected="place => applyPlaceToAddress(index, place)"
                    />
                  </div>
                  <div class="col-md-6 px-2">
                    <label class="d-block">Address2</label>
                    <el-input
                      v-model="client.addresses[index].address2"
                      placeholder="Address2"
                      class="w-100"
                    />
                  </div>
                </div>
                <AIVRow
                  :record.sync="client.addresses[index]"
                  :fields="[
                    { label: 'City', key: 'city' },
                    { label: 'State', key: 'state' },
                    { label: 'Zip', key: 'zip' },
                  ]"
                />
              </div>
              <div class="add-address-button-container">
                <el-button v-if="canEditClient" type="primary" size="small" @click="addAddress">
                  Add Another Address
                </el-button>
              </div>

              <div v-if="isAdmin" class="d-flex align-items-center mt-3 mb-2" style="gap: 8px; flex-wrap: wrap;">
                <el-switch
                  v-model="client.bypass_geofence"
                  :disabled="!canEditClient"
                  active-text="Bypass location check"
                  inactive-text="Enforce location check"
                />
                <el-tooltip content="When on, any caregiver clocking in to a shift for this client skips the GPS distance check. Use for community-based or transient clients." placement="top">
                  <i class="fa fa-info-circle" style="color: #909399; font-size: 13px;"></i>
                </el-tooltip>
              </div>

              <AIVMapPinPicker
                :visible.sync="showMapPinPicker"
                :geocode="mapPinPickerGeocode"
                :address="mapPinPickerAddress"
                @location-selected="handleMapPinSelected"
              />
            </div>

            <!-- Service Information Sub-Tab -->
            <div v-if="detailsSubTab === 'service'">
              <template v-if="client.payer_type === 'DSPD' || client.payer_type === 'Private'">
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Administrator',
                      key: 'id_admin',
                      type: 'select',
                      options: adminOptions,
                      placeholder: 'Select Administrator',
                      disabled: !canEditClient,
                    },
                    {
                      label: 'Preferred Caregiver',
                      key: 'id_preferred_caregiver',
                      type: 'select',
                      options: caregiverOptions,
                      placeholder: 'Select Preferred Caregiver',
                      disabled: !canEditClient,
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Counselor',
                      key: 'id_counselor',
                      type: 'select',
                      options: counselorOptions,
                      placeholder: 'Select Counselor',
                      disabled: !canEditClient,
                    },
                    {
                      label: 'Support Coordinator',
                      key: 'id_support_coordinator',
                      type: 'select',
                      options: supportCoordinatorOptions,
                      placeholder: 'Select Support Coordinator',
                      disabled: !canEditClient,
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Initiated Services',
                      key: 'initiated_services',
                      type: 'date',
                    },
                    {
                      label: 'Initial Assessment Date',
                      key: 'initial_assessment_date',
                      type: 'date',
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'PCSP Start Date',
                      key: 'pcsp_start_date',
                      type: 'date',
                    },
                    {
                      label: 'PCSP End Date',
                      key: 'pcsp_end_date',
                      type: 'date',
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    { label: 'Medicaid Number', key: 'medicaid_number' },
                  ]"
                />
<!-- Directions field deprecated: legacy spreadsheet column with no real use case; hidden to avoid inconsistent data entry. Column retained in DB/model. -->
                <!-- <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Directions',
                      key: 'directions',
                      type: 'textarea',
                    },
                  ]"
                /> -->
                <div class="d-flex align-items-center mt-2 mb-3" style="gap: 8px;">
                  <el-checkbox
                    v-model="client.requires_abi"
                    :disabled="!canEditClient"
                  >
                    <span style="color: #e0e0e0; font-size: 14px;">Requires ABI/TBI Certified Caregiver</span>
                  </el-checkbox>
                  <el-tooltip content="When enabled, only caregivers with an approved ABI/TBI certification can be assigned to this client's shifts." placement="top">
                    <i class="fa fa-info-circle" style="color: #909399; font-size: 13px;"></i>
                  </el-tooltip>
                </div>
              </template>
              <!-- VR clients: show counselor/support coordinator -->
              <template v-else>
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Counselor',
                      key: 'id_counselor',
                      type: 'select',
                      options: counselorOptions,
                      placeholder: 'Select Counselor',
                      disabled: !canEditClient,
                    },
                    {
                      label: 'Support Coordinator',
                      key: 'id_support_coordinator',
                      type: 'select',
                      options: supportCoordinatorOptions,
                      placeholder: 'Select Support Coordinator',
                      disabled: !canEditClient,
                    },
                  ]"
                />
              </template>
            </div>

            <!-- Medical Information Sub-Tab -->
            <div v-if="detailsSubTab === 'medical'">
              <template v-if="client.payer_type === 'DSPD' || client.payer_type === 'Private'">
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'DNR',
                      key: 'dnr',
                      type: 'select',
                      options: [
                        { id: true, name: 'Yes' },
                        { id: false, name: 'No' },
                      ],
                    },
                    {
                      label: 'Advance Directive',
                      key: 'advance_directive',
                      type: 'select',
                      options: [
                        { id: true, name: 'Yes' },
                        { id: false, name: 'No' },
                      ],
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Diagnosis 1',
                      key: 'diagnosis_1',
                      type: 'textarea',
                      rows: 2,
                    },
                    {
                      label: 'Diagnosis 2',
                      key: 'diagnosis_2',
                      type: 'textarea',
                      rows: 2,
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Diagnosis 3',
                      key: 'diagnosis_3',
                      type: 'textarea',
                      rows: 2,
                    },
                    {
                      label: 'Diagnosis 4',
                      key: 'diagnosis_4',
                      type: 'textarea',
                      rows: 2,
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="client"
                  :fields="[
                    {
                      label: 'Allergies',
                      key: 'allergies',
                      type: 'textarea',
                      rows: 2,
                    },
                    {
                      label: 'Pharmacy/Medication Information',
                      key: 'pharmacy_info',
                      type: 'textarea',
                      rows: 2,
                    },
                  ]"
                />

                <!-- Primary Physician -->
                <div class="contact-section mt-4">
                  <h6 class="contact-section-header">Primary Physician</h6>
                  <template v-if="getPhysician()">
                    <AIVRow
                      :record="getPhysician()"
                      @update:record="updatePhysician"
                      :fields="[
                        { label: 'Name', key: 'name' },
                        { label: 'Phone', key: 'phone' },
                        { label: 'Email', key: 'email' },
                      ]"
                    />
                    <AIVRow
                      :record="getPhysician()"
                      @update:record="updatePhysician"
                      :fields="[
                        { label: 'Address 1', key: 'address1' },
                        { label: 'Address 2', key: 'address2' },
                      ]"
                    />
                    <AIVRow
                      :record="getPhysician()"
                      @update:record="updatePhysician"
                      :fields="[
                        { label: 'City', key: 'city' },
                        { label: 'State', key: 'state' },
                        { label: 'Zip', key: 'zip' },
                      ]"
                    />
                  </template>
                  <div v-else>
                    <p class="text-muted">No primary physician on file</p>
                    <el-button
                      type="primary"
                      size="small"
                      @click="addPhysician"
                    >
                      Add Primary Physician
                    </el-button>
                  </div>
                </div>
              </template>
            </div>

            <!-- Contacts Sub-Tab -->
            <div v-if="detailsSubTab === 'contacts'" class="address-section">
              <!-- Emergency Contacts -->
              <div class="contact-section">
                <h6 class="contact-section-header">Emergency Contacts</h6>
                <template v-if="emergencyContactsList.length > 0">
                  <div v-for="(contact, index) in emergencyContactsList" :key="`emergency-${contact.id || index}`" class="address-block mb-3">
                    <div class="address-header d-flex justify-content-between align-items-center mb-2">
                      <h6>Emergency Contact #{{ index + 1 }}</h6>
                      <el-button type="danger" size="small" @click="removeContact('emergency', index)">Remove</el-button>
                    </div>
                    <AIVRow :record="contact" @update:record="(data) => updateContact('emergency', index, data)" :fields="[{ label: 'Name', key: 'name' }, { label: 'Relationship', key: 'relationship' }, { label: 'Email', key: 'email' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('emergency', index, data)" :fields="[{ label: 'Phone', key: 'phone' }, { label: 'Alt Phone', key: 'phone_alt' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('emergency', index, data)" :fields="[{ label: 'Address 1', key: 'address1' }, { label: 'Address 2', key: 'address2' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('emergency', index, data)" :fields="[{ label: 'City', key: 'city' }, { label: 'State', key: 'state' }, { label: 'Zip', key: 'zip' }]" />
                  </div>
                </template>
                <div v-else class="mb-3"><p class="text-muted">No emergency contacts on file</p></div>
                <div v-if="canEditClient" class="add-address-button-container">
                  <el-button type="primary" size="small" @click="addContact('emergency')">Add Emergency Contact</el-button>
                </div>
              </div>
              <!-- Guardians -->
              <div class="contact-section mt-4">
                <h6 class="contact-section-header">Guardians</h6>
                <template v-if="guardianContactsList.length > 0">
                  <div v-for="(contact, index) in guardianContactsList" :key="`guardian-${contact.id || index}`" class="address-block mb-3">
                    <div class="address-header d-flex justify-content-between align-items-center mb-2">
                      <h6>Guardian #{{ index + 1 }}</h6>
                      <el-button type="danger" size="small" @click="removeContact('guardian', index)">Remove</el-button>
                    </div>
                    <AIVRow :record="contact" @update:record="(data) => updateContact('guardian', index, data)" :fields="[{ label: 'Name', key: 'name' }, { label: 'Relationship', key: 'relationship' }, { label: 'Email', key: 'email' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('guardian', index, data)" :fields="[{ label: 'Phone', key: 'phone' }, { label: 'Alt Phone', key: 'phone_alt' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('guardian', index, data)" :fields="[{ label: 'Address 1', key: 'address1' }, { label: 'Address 2', key: 'address2' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('guardian', index, data)" :fields="[{ label: 'City', key: 'city' }, { label: 'State', key: 'state' }, { label: 'Zip', key: 'zip' }]" />
                    <AIVRow :record="contact" @update:record="(data) => updateContact('guardian', index, data)" :fields="[{ label: 'HIPAA Disclosure Auth', key: 'hipaa_disclosure_auth', type: 'select', options: [{ id: true, name: 'Yes' }, { id: false, name: 'No' }] }, { label: 'Medical Decisions', key: 'medical_decisions', type: 'select', options: [{ id: true, name: 'Yes' }, { id: false, name: 'No' }] }]" />
                  </div>
                </template>
                <div v-else class="mb-3"><p class="text-muted">No guardians on file</p></div>
                <div v-if="canEditClient" class="add-address-button-container">
                  <el-button type="primary" size="small" @click="addContact('guardian')">Add Guardian</el-button>
                </div>
              </div>
              <!-- Responsible Parties -->
              <div class="contact-section mt-4" v-if="client.payer_type === 'DSPD' || client.payer_type === 'Private'">
                <h6 class="contact-section-header">Responsible Parties</h6>
                <template v-if="responsibleParties.length > 0">
                  <div v-for="(party, index) in responsibleParties" :key="`resp-party-${party.id || index}`" class="address-block mb-3">
                    <div class="address-header d-flex justify-content-between align-items-center mb-2">
                      <h6>Responsible Party #{{ index + 1 }}</h6>
                      <el-button type="danger" size="small" @click="removeContact('responsible_party', index)">Remove</el-button>
                    </div>
                    <AIVRow :record="party" @update:record="(data) => updateContact('responsible_party', index, data)" :fields="[{ label: 'Name', key: 'name' }, { label: 'Relationship', key: 'relationship' }, { label: 'Email', key: 'email' }]" />
                    <AIVRow :record="party" @update:record="(data) => updateContact('responsible_party', index, data)" :fields="[{ label: 'Phone', key: 'phone' }, { label: 'Phone Type', key: 'phone_type' }, { label: 'Alt Phone', key: 'phone_alt' }]" />
                    <AIVRow :record="party" @update:record="(data) => updateContact('responsible_party', index, data)" :fields="[{ label: 'Address 1', key: 'address1' }, { label: 'Address 2', key: 'address2' }]" />
                    <AIVRow :record="party" @update:record="(data) => updateContact('responsible_party', index, data)" :fields="[{ label: 'City', key: 'city' }, { label: 'State', key: 'state' }, { label: 'Zip', key: 'zip' }]" />
                    <AIVRow :record="party" @update:record="(data) => updateContact('responsible_party', index, data)" :fields="[{ label: 'HIPAA Disclosure Auth', key: 'hipaa_disclosure_auth', type: 'select', options: [{ id: true, name: 'Yes' }, { id: false, name: 'No' }] }, { label: 'Medical Decisions', key: 'medical_decisions', type: 'select', options: [{ id: true, name: 'Yes' }, { id: false, name: 'No' }] }]" />
                  </div>
                </template>
                <div v-else class="mb-3"><p class="text-muted">No responsible parties on file</p></div>
                <div v-if="canEditClient" class="add-address-button-container">
                  <el-button type="primary" size="small" @click="addContact('responsible_party')">Add Responsible Party</el-button>
                </div>
              </div>
            </div>

            <div v-if="canEditClient" class="text-center mt-4">
              <el-button type="primary" round @click="saveClientDetails">
                Save Changes
              </el-button>
            </div>
          </el-form>
        </div>
        <div v-else class="text-center p-4">
          <p>No client details available</p>
        </div>
      </div>

      <!-- Goals Tab -->
      <div v-if="activeTab === 'goals'" class="client-tab-pane">
        <div class="goals-section">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5>Client Goal Questions</h5>
            <el-button
              v-if="isAdmin || isLead"
              type="primary"
              size="small"
              @click="openAddGoalModal"
            >
              <i class="fa fa-plus mr-2"></i>Add Question
            </el-button>
          </div>

          <div v-if="loadingGoals" class="text-center p-4">
            <i class="fa fa-spinner fa-spin"></i> Loading goal questions...
          </div>

          <div v-else-if="goals.length === 0" class="no-goals text-center p-4">
            <p>No goal questions have been set for this client yet.</p>
          </div>

          <div v-else class="goals-list">
            <div v-for="goal in goals" :key="goal.id" class="goal-card">
              <div class="d-flex align-items-start justify-content-between">
                <div class="flex-1">
                  <div class="d-flex align-items-center mb-2 goal-header">
                    <span class="goal-type-badge" :class="goal.question_type">
                      {{
                        goal.question_type === "yes_no"
                          ? "Yes/No"
                          : "Free Response"
                      }}
                    </span>
                    <span class="goal-meta text-muted ml-2">
                      <small>Added on {{ formatDate(goal.assignedAt) }}</small>
                    </span>
                  </div>
                  <div class="goal-question mb-2">{{ goal.question_text }}</div>
                </div>
                <div v-if="isAdmin || isLead" class="goal-actions ml-3">
                  <el-button
                    size="mini"
                    type="text"
                    @click="editGoal(goal)"
                    title="Edit Question"
                  >
                    <i class="fa fa-edit"></i>
                  </el-button>
                  <el-button
                    size="mini"
                    type="text"
                    class="text-danger"
                    @click="confirmDeleteGoal(goal)"
                    title="Delete Question"
                  >
                    <i class="fa fa-trash"></i>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Medications Tab -->
      <div v-if="activeTab === 'medications'" class="client-tab-pane">
        <div class="medications-section">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5>Client Medications</h5>
            <div class="d-flex align-items-center" style="gap: 8px">
              <el-button
                type="info"
                size="small"
                @click="showMarModal = true"
              >
                <i class="fa fa-file-pdf mr-2"></i>Download MAR
              </el-button>
              <el-button
                v-if="isAdmin || isLead"
                type="primary"
                size="small"
                @click="openAddMedicationModal"
              >
                <i class="fa fa-plus mr-2"></i>Add Medication
              </el-button>
            </div>
          </div>

          <AIVTable
            :columns="medicationColumns"
            :data="medications"
            :pagination="medicationPagination"
            :item-name="'medications'"
            @pageChange="loadClientMedications"
            @pageSizeChange="handleMedicationPageSizeChange"
            @sort-change="handleMedicationSortChange"
          >
            <template #cell-schedule="{ row }">
              <div class="med-schedule-cell">
                <div v-if="row.schedule_days && row.schedule_days.length > 0 && row.schedule_days.length < 7" class="med-schedule-days">
                  <span
                    v-for="day in medWeekdayOptions"
                    :key="day.value"
                    class="med-day-dot"
                    :class="{ active: row.schedule_days.includes(day.value) }"
                    :title="day.label"
                  >{{ day.label.charAt(0) }}</span>
                </div>
                <span v-else class="text-muted" style="font-size: 12px">Every day</span>
                <div v-if="row.administration_times && row.administration_times.length > 0" class="med-schedule-times">
                  <span v-for="(t, i) in row.administration_times" :key="i" class="med-time-badge">{{ formatMedTime(t) }}</span>
                </div>
                <div v-else-if="row.frequency" class="text-muted" style="font-size: 12px">{{ row.frequency }}</div>
              </div>
            </template>
            <template #cell-amount_remaining="{ row }">
              {{ row.amount_remaining != null ? row.amount_remaining : '-' }}
            </template>
            <template #cell-lastGiven="{ row }">
              <div v-if="row.lastGivenAt">
                {{ formatDate(row.lastGivenAt) }}<br />
                <small class="text-muted">by {{ row.lastGivenBy }}</small>
              </div>
              <span v-else class="text-muted">Never</span>
            </template>
            <template #cell-history="{ row }">
              <el-button size="mini" type="text" @click="openMedicationHistory(row)" title="View History">
                <i class="fa fa-history"></i>
              </el-button>
            </template>
            <template #cell-actions="{ row }">
              <el-button size="mini" type="text" @click="editMedication(row)" title="Edit">
                <i class="fa fa-edit"></i>
              </el-button>
              <el-button size="mini" type="text" class="text-danger" @click="confirmDeleteMedication(row)" title="Delete">
                <i class="fa fa-trash"></i>
              </el-button>
            </template>
          </AIVTable>
        </div>
      </div>

      <!-- Add/Edit Medication Modal -->
      <AIVDialog
        :visible.sync="showMedicationModal"
        :title="editingMedication ? 'Edit Medication' : 'Add Medication'"
        width="600px"
        height="80vh"
        class="medication-edit-modal"
        @close="closeMedicationModal"
      >
        <div class="medication-form">
          <div class="row mb-3">
            <div class="col-md-6">
              <label>Name <span class="text-danger">*</span></label>
              <el-input v-model="medicationFormData.name" placeholder="Medication name" />
            </div>
            <div class="col-md-6">
              <label>Dosage</label>
              <el-input v-model="medicationFormData.dosage" placeholder="e.g. 50mg" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <label>Route</label>
              <AIVSelect
                v-model="medicationFormData.route"
                :options="routeOptions"
                :searchable="false"
                placeholder="Select"
              />
            </div>
            <div class="col-md-6">
              <label>Prescribed Date</label>
              <el-date-picker
                v-model="medicationFormData.prescribed_date"
                type="date"
                placeholder="Select date"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <label>Amount Remaining</label>
              <el-input-number
                v-model="medicationFormData.amount_remaining"
                :min="0"
                :precision="2"
                placeholder="Pill/dose count"
                style="width: 100%"
              />
            </div>
          </div>
          <div class="mb-3">
            <label>Schedule</label>
            <p class="text-muted small mb-1">Click a day to add it, then set the time(s) for each day.</p>
            <div class="med-schedule-days">
              <div
                v-for="day in medWeekdayOptions"
                :key="day.value"
                class="med-day-pill"
                :class="{ 'med-day-pill--active': medicationFormData.daySchedule[day.value] && medicationFormData.daySchedule[day.value].length > 0 }"
                @click="toggleMedDay(day.value)"
              >
                {{ day.label }}
              </div>
            </div>
            <div class="med-day-times-list mt-2">
              <div
                v-for="day in activeMedDays"
                :key="day.value"
                class="med-day-times-row"
              >
                <span class="med-day-label">{{ day.label }}</span>
                <div class="med-times">
                  <div v-for="(time, idx) in medicationFormData.daySchedule[day.value]" :key="idx" class="med-time-entry">
                    <AIVTimeSelect
                      :value="medicationFormData.daySchedule[day.value][idx]"
                      @input="$set(medicationFormData.daySchedule[day.value], idx, $event)"
                      placeholder="Select time"
                    />
                    <el-button
                      type="text"
                      class="text-danger"
                      size="small"
                      @click="removeMedDayTime(day.value, idx)"
                    ><i class="fa fa-times"></i></el-button>
                  </div>
                  <el-button type="text" size="small" @click="addMedDayTime(day.value)">
                    <i class="fa fa-plus mr-1"></i>Add Time
                  </el-button>
                </div>
              </div>
            </div>
          </div>
          <div class="mb-3">
            <label>Instructions</label>
            <el-input
              v-model="medicationFormData.instructions"
              type="textarea"
              :rows="2"
              placeholder="Special administration instructions"
            />
          </div>
          <div class="mb-3">
            <label>Client-Specific Instructions</label>
            <el-input
              v-model="medicationFormData.special_instructions"
              type="textarea"
              :rows="2"
              placeholder="Instructions specific to this client"
            />
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="closeMedicationModal">Cancel</el-button>
          <el-button type="primary" @click="handleSaveMedication">
            {{ editingMedication ? 'Update' : 'Add' }}
          </el-button>
        </span>
      </AIVDialog>

      <!-- Medication Administration History Modal -->
      <AIVDialog
        :visible.sync="showMedicationHistoryModal"
        :title="selectedMedicationForHistory ? selectedMedicationForHistory.name + ' - Administration History' : 'Administration History'"
        width="800px"
        @close="closeMedicationHistoryModal"
      >
        <AIVTable
          :columns="medHistoryColumns"
          :data="medicationHistory"
          :pagination="medHistoryPagination"
          :item-name="'records'"
          :loading="loadingMedicationHistory"
          @pageChange="fetchMedicationHistory"
          @pageSizeChange="handleMedHistoryPageSizeChange"
          @sort-change="handleMedHistorySortChange"
        >
          <template #cell-administered_at="{ row }">
            {{ formatDateTime(row.administered_at) }}
          </template>
          <template #cell-status="{ row }">
            <el-tag :type="row.was_administered ? 'success' : 'danger'" size="small">
              {{ row.was_administered ? 'Given' : 'Skipped' }}
            </el-tag>
          </template>
        </AIVTable>
        <span slot="footer" class="dialog-footer">
          <el-button @click="closeMedicationHistoryModal">Close</el-button>
        </span>
      </AIVDialog>

      <!-- MAR Download Modal -->
      <AIVDialog
        :visible.sync="showMarModal"
        title="Download MAR Report"
        width="400px"
      >
        <div class="mar-modal-body">
          <p style="margin-bottom: 16px; color: #e0e0e0;">Select the month and year for the Medication Administration Record.</p>
          <div class="d-flex align-items-center" style="gap: 12px">
            <el-select v-model="marMonth" placeholder="Month" style="width: 160px">
              <el-option v-for="m in monthOptions" :key="m.value" :label="m.label" :value="m.value" />
            </el-select>
            <el-input-number v-model="marYear" :min="2020" :max="2050" :step="1" controls-position="right" style="width: 120px" />
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showMarModal = false">Cancel</el-button>
          <el-button type="primary" :loading="marDownloading" @click="downloadMar">
            <i class="fa fa-download mr-2"></i>Download PDF
          </el-button>
        </span>
      </AIVDialog>

      <!-- Intake Tab -->
      <div v-if="activeTab === 'intake'" class="client-tab-pane">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>Intake Forms</h5>
          <div v-if="!isCaregiver">
            <el-button type="success" size="small" @click="showUploadIntakeDialog = true">
              <i class="fa fa-upload mr-1"></i> Upload Previous Intake
            </el-button>
            <el-button type="primary" size="small" @click="draftIntakeForm ? openIntakeModal(draftIntakeForm.id) : startIntake()">
              <i :class="draftIntakeForm ? 'fa fa-play mr-1' : 'fa fa-plus mr-1'"></i>
              {{ draftIntakeForm ? 'Continue Intake' : 'Start New Intake' }}
            </el-button>
          </div>
        </div>

        <!-- Upload Previous Intake Dialog -->
        <AIVDialog
          title="Upload Previous Intake"
          :visible.sync="showUploadIntakeDialog"
          width="500px"
        >
          <div class="upload-intake-body">
            <p class="text-muted">
              Upload a previously completed intake PDF. The system will parse it and auto-populate the intake record and generate a CST draft.
            </p>
            <div class="upload-intake-field">
              <label>Select Intake PDF</label>
              <input
                type="file"
                accept=".pdf"
                @change="intakePdfFile = $event.target.files[0]"
              />
            </div>
          </div>
          <template #footer>
            <el-button @click="showUploadIntakeDialog = false; intakePdfFile = null;" size="small">Cancel</el-button>
            <el-button type="primary" size="small" :loading="uploadingIntakePdf" :disabled="!intakePdfFile" @click="uploadIntakePdf">
              <i class="fa fa-upload mr-1" v-if="!uploadingIntakePdf"></i> Upload & Parse
            </el-button>
          </template>
        </AIVDialog>

        <div v-if="loadingIntake" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading intake forms...
        </div>
        <div v-else-if="intakeForms.length === 0" class="text-center p-4">
          <p class="text-muted">No intake forms found for this client.</p>
        </div>
        <div v-else>
          <div v-for="form in intakeForms" :key="form.id" class="intake-form-card mb-3" style="background:rgba(37,55,65,0.95); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:1rem;">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong style="color:#fff;">Intake #{{ form.id }}</strong>
                <el-tag size="mini" :type="form.status === 'completed' ? 'success' : form.status === 'draft' ? 'warning' : 'info'" class="ml-2">{{ form.status }}</el-tag>
              </div>
              <div>
                <el-button size="mini" @click="openIntakeModal(form.id)">
                  {{ form.status === 'draft' ? 'Continue' : 'View' }}
                </el-button>
                <el-button v-if="isAdmin" size="mini" type="danger" @click="deleteIntakeForm(form)">
                  <i class="fa fa-trash"></i>
                </el-button>
              </div>
            </div>
            <div style="color:#b3a6a6; font-size:0.85rem; margin-top:0.5rem;">
              Created: {{ formatDate(form.createdAt) }}
              <span v-if="form.completed_at"> | Completed: {{ formatDate(form.completed_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Support Strategies Tab -->
      <div v-if="activeTab === 'supportStrategies'" class="client-tab-pane">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5>Support Strategies</h5>
          <el-button
            v-if="isAdmin || isLead"
            type="primary"
            size="small"
            @click="openNewSupportStrategy"
          >
            <i class="fa fa-plus mr-1"></i> New Support Strategy
          </el-button>
        </div>

        <!-- PCSP Plan Year Info -->
        <div v-if="client.pcsp_start_date" class="pcsp-plan-year-info mb-3">
          <i class="fa fa-calendar mr-1"></i>
          <strong>Current PCSP Plan Year:</strong>
          {{ formatDate(client.pcsp_start_date) }} &ndash; {{ formatDate(client.pcsp_end_date) }}
          <span v-if="supportStrategyDueDate" class="ml-3">
            <i class="fa fa-clock mr-1"></i>
            <strong>Support Strategy Due:</strong>
            <span :class="supportStrategyDueDateClass">{{ formatDate(supportStrategyDueDate) }}</span>
          </span>
        </div>
        <div v-else class="pcsp-plan-year-info mb-3 text-warning">
          <i class="fa fa-exclamation-triangle mr-1"></i>
          No PCSP dates set. Go to <a href="#" @click.prevent="activeTab = 'details'; detailsSubTab = 'service'" style="color: #e6a23c; text-decoration: underline;">Service Information</a> to set PCSP Start/End dates.
        </div>

        <div v-if="loadingSupportStrategies" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading support strategies...
        </div>

        <div v-else-if="supportStrategies.length === 0" class="text-center p-4" style="color: #b0b0b0;">
          <p>No support strategies have been created for this client yet.</p>
        </div>

        <div v-else class="support-strategies-list">
          <div
            v-for="strategy in supportStrategies"
            :key="strategy.id"
            class="strategy-card"
            :class="{ 'strategy-final': strategy.status === 'final', 'strategy-draft': strategy.status === 'draft' }"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-1">
                <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                  <el-tag
                    :type="strategy.status === 'final' ? 'success' : 'warning'"
                    size="small"
                  >
                    {{ strategy.status === 'final' ? 'Finalized' : 'Draft' }}
                  </el-tag>
                  <span v-if="strategy.pcsp_start_date" class="text-muted" style="font-size: 13px;">
                    Plan Year: {{ formatDate(strategy.pcsp_start_date) }} &ndash; {{ formatDate(strategy.pcsp_end_date) }}
                  </span>
                  <span v-if="strategy.due_date" class="ml-2" style="font-size: 13px;" :class="getStrategyDueDateClass(strategy)">
                    <i class="fa fa-clock mr-1"></i>Due: {{ formatDate(strategy.due_date) }}
                  </span>
                  <span v-if="strategy.status === 'draft' && isStrategyOverdue(strategy)" class="text-danger" style="font-size: 13px; font-weight: 600;">
                    <i class="fa fa-exclamation-circle mr-1"></i>OVERDUE
                  </span>
                </div>
                <div v-if="strategy.plan_cycle_date" style="font-size: 13px; color: #c0c0c0;" class="mb-1">
                  Plan Cycle Date: {{ formatDate(strategy.plan_cycle_date) }}
                </div>
                <div style="font-size: 13px; color: #909399;">
                  Created by {{ strategy.createdBy ? `${strategy.createdBy.first_name} ${strategy.createdBy.last_name}` : 'Unknown' }}
                  on {{ formatDate(strategy.createdAt) }}
                </div>
              </div>
              <div v-if="isAdmin || isLead" class="ml-3" style="display: flex; gap: 6px;">
                <el-button
                  v-if="strategy.status === 'final'"
                  size="mini"
                  @click="viewStrategyPdf(strategy)"
                  :loading="strategy._viewingPdf"
                  title="View PDF"
                >
                  <i class="fa fa-file-pdf mr-1"></i> View PDF
                </el-button>
                <el-button
                  v-if="strategy.status === 'final'"
                  size="mini"
                  @click="openStrategyEmailModal(strategy)"
                  title="Email to Support Coordinator"
                >
                  <i class="fa fa-envelope mr-1"></i> Email to Support Coordinator
                </el-button>
                <el-button
                  v-if="strategy.status === 'draft'"
                  size="mini"
                  type="text"
                  @click="editSupportStrategy(strategy)"
                  title="Edit"
                >
                  <i class="fa fa-edit"></i>
                </el-button>
                <el-button
                  size="mini"
                  type="text"
                  class="text-danger"
                  @click="confirmDeleteSupportStrategy(strategy)"
                  title="Delete"
                >
                  <i class="fa fa-trash"></i>
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Email Support Strategy Modal -->
        <AIVDialog
          v-model="showStrategyEmailModal"
          title="Email Support Strategy to Support Coordinator"
          width="600px"
          height="80vh"
        >
          <el-form label-position="top" class="full-width-form">
            <AIVRow
              :record.sync="strategyEmailForm"
              :fields="[
                { label: 'To', key: 'to', placeholder: 'Recipient email' },
              ]"
            />
            <AIVRow
              :record.sync="strategyEmailForm"
              :fields="[
                { label: 'Subject', key: 'subject' },
              ]"
            />
            <div class="row mb-3 w-100 mx-0">
              <div class="col-12 px-2">
                <label class="d-block">Body</label>
                <el-input
                  v-model="strategyEmailForm.body"
                  type="textarea"
                  :rows="8"
                ></el-input>
              </div>
            </div>
            <div class="row mb-3 w-100 mx-0">
              <div class="col-12 px-2">
                <div style="color: #999; font-size: 12px;">
                  <i class="fa fa-paperclip" style="margin-right: 4px"></i>
                  Attached: Support Strategy PDF
                </div>
              </div>
            </div>
          </el-form>
          <template slot="footer">
            <div class="text-center">
              <el-button round @click="showStrategyEmailModal = false">Cancel</el-button>
              <el-button type="primary" round @click="sendStrategyEmail" :loading="sendingStrategyEmail">
                <i class="fa fa-paper-plane" style="margin-right: 6px"></i> Send Email
              </el-button>
            </div>
          </template>
        </AIVDialog>

        <SupportStrategyModal
          v-if="clientId"
          v-model="showSupportStrategyModalTab"
          :clientId="clientId"
          :strategyId="editingSupportStrategyIdTab"
          @document-generated="onSupportStrategyGeneratedFromTab"
          @saved="onSupportStrategySavedFromTab"
        />
      </div>

      <!-- CST Tab -->
      <div v-if="activeTab === 'cst'" class="client-tab-pane">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>Client Summary of Training</h5>
          <div v-if="!isCaregiver">
            <el-button type="success" size="small" @click="showUploadCstDialog = true">
              <i class="fa fa-upload mr-1"></i> Upload CST
            </el-button>
            <el-button type="primary" size="small" @click="createCst">
              <i class="fa fa-plus mr-1"></i> Create CST
            </el-button>
          </div>
        </div>

        <!-- Upload Existing CST Dialog -->
        <AIVDialog
          title="Upload Existing CST"
          :visible.sync="showUploadCstDialog"
          width="500px"
        >
          <div class="upload-intake-body">
            <p class="text-muted">
              Upload an existing CST document (PDF). The system will parse it, attach it to this client,
              and create a draft CST populated from the document. Review the draft, then publish it —
              publishing supersedes any active CST and asks assigned caregivers to acknowledge it.
            </p>
            <div class="upload-intake-field">
              <label>Select CST PDF</label>
              <input
                type="file"
                accept=".pdf"
                @change="cstPdfFile = $event.target.files[0]"
              />
            </div>
          </div>
          <template #footer>
            <el-button @click="showUploadCstDialog = false; cstPdfFile = null;" size="small">Cancel</el-button>
            <el-button type="primary" size="small" :loading="uploadingCstPdf" :disabled="!cstPdfFile" @click="uploadCstPdf">
              <i class="fa fa-upload mr-1" v-if="!uploadingCstPdf"></i> Upload &amp; Parse
            </el-button>
          </template>
        </AIVDialog>
        <div v-if="loadingCst" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading CST...
        </div>
        <div v-else-if="cstList.length === 0" class="text-center p-4">
          <p class="text-muted">No CSTs found for this client.</p>
        </div>
        <div v-else>
          <div v-for="cst in cstList" :key="cst.id" class="cst-card mb-3" style="background:rgba(37,55,65,0.95); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:1rem;">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong style="color:#fff;">CST v{{ cst.version }}</strong>
                <el-tag size="mini" :type="cst.status === 'active' ? 'success' : cst.status === 'draft' ? 'warning' : 'info'" class="ml-2">{{ cst.status }}</el-tag>
              </div>
              <div>
                <el-button size="mini" @click="editingCstId = cst.id; showCstEditor = true">
                  {{ cst.status === 'draft' ? 'Edit' : 'View' }}
                </el-button>
                <el-button v-if="isAdmin" size="mini" type="danger" @click="deleteCst(cst)">
                  <i class="fa fa-trash"></i>
                </el-button>
              </div>
            </div>
            <div style="color:#b3a6a6; font-size:0.85rem; margin-top:0.5rem;">
              Created: {{ formatDate(cst.createdAt) }}
              <span v-if="cst.published_at"> | Published: {{ formatDate(cst.published_at) }}</span>
              <span v-if="cst.updatedAt"> | Updated: {{ formatDate(cst.updatedAt) }}</span>
            </div>
          </div>
        </div>
        <CstAcknowledgmentTracker v-if="activeCstId" :cstId="activeCstId" ref="ackTracker" class="mt-4" />
      </div>

      <!-- Compliance Tab -->
      <div v-if="activeTab === 'compliance'" class="client-tab-pane">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>Compliance</h5>
          <div>
            <el-button v-if="isAdmin" size="small" @click="showComplianceConfigModal = true">
              <i class="fa fa-cog mr-1"></i> Configure Forms
            </el-button>
            <el-button type="primary" size="small" @click="showSendComplianceForms = true">
              <i class="fa fa-paper-plane mr-1"></i> Send Forms
            </el-button>
            <el-button v-if="isAdmin" size="small" @click="showClientDocUpload = true">
              <i class="fa fa-upload mr-1"></i> Upload Document
            </el-button>
          </div>
        </div>
        <div v-if="loadingClientCompliance" class="text-center p-3">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>
        <ComplianceStatusCard
          v-else-if="clientId"
          ref="complianceStatusCard"
          :clientId="clientId"
          :existingDocuments="clientComplianceDocs"
          :complianceStatus="clientComplianceStatus"
          :canEdit="isAdmin"
          @view-pdf="onViewCompliancePdf"
          @view-doc="viewClientCompDoc"
          @edit-doc="editClientCompDoc"
          @delete-doc="deleteClientCompDoc"
          @toggle-waiver="toggleClientWaiver"
          @refresh="loadClientComplianceDocs"
        />

        <SendComplianceFormsModal
          :visible.sync="showSendComplianceForms"
          :clientId="clientId"
          :clientName="clientName"
        />

        <ClientComplianceConfigModal
          :visible.sync="showComplianceConfigModal"
          :clientId="clientId"
          :clientName="clientName"
          @saved="onComplianceConfigSaved"
        />

        <!-- Upload Client Document Modal -->
        <AIVDialog
          v-if="showClientDocUpload"
          title="Upload Client Compliance Document"
          :visible.sync="showClientDocUpload"
          width="550px"
        >
          <div class="p-3">
            <div class="mb-3">
              <label class="form-label">Document Type</label>
              <AIVSelect
                v-model="clientDocUpload.type"
                :options="clientComplianceTypeOptions"
                placeholder="Select type"
              />
            </div>
            <div class="mb-3">
              <label class="form-label">File</label>
              <div>
                <input type="file" ref="clientCompFileInput" accept="image/*,.pdf,.doc,.docx" style="display: none;" @change="clientCompFileName = $event.target.files[0] ? $event.target.files[0].name : ''" />
                <el-button size="small" icon="el-icon-folder-opened" @click="$refs.clientCompFileInput.click()">
                  {{ clientCompFileName || 'Choose File' }}
                </el-button>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Expiration Date</label>
              <el-date-picker
                v-model="clientDocUpload.expirationDate"
                type="date"
                placeholder="MM/DD/YYYY"
                value-format="yyyy-MM-dd"
                format="MM/dd/yyyy"
                :editable="true"
                style="width: 100%;"
              />
            </div>
            <div class="mb-3">
              <label class="form-label">Also satisfies (multi-select)</label>
              <el-checkbox-group v-model="clientDocUpload.complianceItemTypes" class="compliance-checkbox-group">
                <el-checkbox v-for="t in clientComplianceTypes" :key="t" :label="t" class="d-block" style="margin-left: 0 !important;">
                  {{ clientComplianceDisplayLabel(t) }}
                </el-checkbox>
              </el-checkbox-group>
              <span style="font-size: 11px; color: #999;">Select additional compliance items this file covers</span>
            </div>
          </div>
          <template #footer>
            <el-button @click="showClientDocUpload = false">Cancel</el-button>
            <el-button type="primary" icon="el-icon-upload2" @click="uploadClientCompDoc" :loading="uploadingClientDoc">Upload</el-button>
          </template>
        </AIVDialog>

        <!-- Documents Section (merged from Documents tab) -->
        <div class="documents-section mt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5>Documents</h5>
          </div>

          <!-- Upload/Add Link Section -->
          <div v-if="!isCaregiver" class="documents-upload-section mb-4">
            <h6>Add Document or Link</h6>
            <div class="row">
              <div class="col-md-4">
                <label>Category</label>
                <AIVSelect
                  v-model="newDocument.category"
                  :options="documentCategoryOptions"
                  placeholder="Select category"
                />
              </div>
              <div class="col-md-4">
                <label>Display Name</label>
                <el-input
                  v-model="newDocument.displayName"
                  placeholder="Enter display name"
                ></el-input>
              </div>
              <div class="col-md-4">
                <label>Type</label>
                <AIVSelect
                  v-model="newDocument.type"
                  :options="documentTypeOptions"
                  placeholder="Select type"
                />
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-md-5">
                <label v-if="newDocument.type === 'file'">Select File</label>
                <label v-else>URL</label>
                <el-input
                  v-if="newDocument.type === 'link'"
                  v-model="newDocument.url"
                  placeholder="Enter URL"
                ></el-input>
                <div v-else>
                  <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileSelect"
                    style="display: none;"
                  />
                  <el-button size="small" icon="el-icon-folder-opened" @click="$refs.fileInput.click()">
                    {{ newDocument.file ? newDocument.file.name : 'Choose File' }}
                  </el-button>
                </div>
              </div>
              <div class="col-md-3">
                <label>Expiration Date <span class="text-muted small">(optional)</span></label>
                <el-date-picker
                  v-model="newDocument.expirationDate"
                  type="date"
                  placeholder="Select date"
                  format="MM/dd/yyyy"
                  value-format="yyyy-MM-dd"
                  style="width: 100%"
                ></el-date-picker>
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <el-button
                  type="primary"
                  @click="uploadDocument"
                  :loading="uploading"
                  :disabled="!canUpload"
                >
                  <i class="el-icon-upload2" style="margin-right: 4px;" v-if="newDocument.type === 'file'"></i>
                  {{ newDocument.type === "file" ? "Upload" : "Add Link" }}
                </el-button>
              </div>
            </div>
          </div>

          <!-- Documents Display -->
          <div v-if="loadingDocuments" class="text-center p-4">
            <i class="fa fa-spinner fa-spin"></i> Loading documents...
          </div>
          <div v-else>
            <div v-for="category in documentCategories" :key="category.key" class="document-category-section mb-4">
              <div class="category-header-with-action">
                <h5 class="category-header">
                  {{ category.label }}
                </h5>

                <!-- Training Submission Button for Caregivers (CST only) -->
                <div v-if="category.key === 'CST' && !isAdmin" class="training-action-section">
                  <div v-if="currentUserTrainingStatus" class="training-status-badge">
                    <span class="badge" :class="{
                      'badge-warning': currentUserTrainingStatus === 'pending',
                      'badge-success': currentUserTrainingStatus === 'approved',
                      'badge-danger': currentUserTrainingStatus === 'rejected',
                    }">
                      Training {{ currentUserTrainingStatus.charAt(0).toUpperCase() + currentUserTrainingStatus.slice(1) }}
                    </span>
                  </div>
                  <el-button
                    v-if="!currentUserTrainingStatus || currentUserTrainingStatus === 'rejected'"
                    type="primary"
                    size="small"
                    @click="openTrainingSubmissionModal"
                    :disabled="!documents.CST || documents.CST.length === 0"
                    class="submit-training-button"
                  >
                    <i class="fa fa-check-circle"></i>
                    {{ currentUserTrainingStatus === "rejected" ? "Resubmit Training Completion" : "Submit Training Completion" }}
                  </el-button>
                </div>
              </div>

              <div v-if="documents[category.key] && documents[category.key].length > 0" class="documents-list">
                <div v-for="doc in documents[category.key]" :key="doc.id" class="document-item">
                  <div class="document-info">
                    <i :class="doc.is_link ? 'fa fa-link' : 'fa fa-file'" class="mr-2"></i>
                    <a href="#" @click.prevent="viewDocument(doc)" class="document-name">{{ doc.display_name }}</a>
                    <span class="document-meta ml-3">
                      Uploaded by {{ doc.uploader ? `${doc.uploader.first_name} ${doc.uploader.last_name}` : "Unknown" }}
                      on {{ formatDate(doc.createdAt) }}
                    </span>
                    <span v-if="doc.expiration_date" class="document-expiration ml-3" :class="getExpirationClass(doc.expiration_date)">
                      <i class="fa fa-calendar-times-o mr-1"></i>
                      <template v-if="!editingExpirationDocId || editingExpirationDocId !== doc.id">
                        {{ getExpirationLabel(doc.expiration_date) }}
                        <a v-if="isAdmin" href="#" @click.prevent="startEditExpiration(doc)" class="ml-1" title="Edit expiration date"><i class="fa fa-pencil" style="font-size: 11px"></i></a>
                      </template>
                      <span v-else style="display: inline-flex; align-items: center; gap: 4px;">
                        <el-date-picker v-model="editingExpirationDate" type="date" size="mini" format="MM/dd/yyyy" value-format="yyyy-MM-dd" style="width: 140px" @change="saveExpiration(doc)"></el-date-picker>
                        <a href="#" @click.prevent="cancelEditExpiration" class="text-muted"><i class="fa fa-times"></i></a>
                      </span>
                    </span>
                    <span v-else-if="isAdmin" class="document-expiration ml-3 text-muted">
                      <span v-if="editingExpirationDocId === doc.id" style="display: inline-flex; align-items: center; gap: 4px;">
                        <el-date-picker v-model="editingExpirationDate" type="date" size="mini" format="MM/dd/yyyy" value-format="yyyy-MM-dd" style="width: 140px" @change="saveExpiration(doc)"></el-date-picker>
                        <a href="#" @click.prevent="cancelEditExpiration" class="text-muted"><i class="fa fa-times"></i></a>
                      </span>
                      <a v-else href="#" @click.prevent="startEditExpiration(doc)" title="Set expiration date"><i class="fa fa-calendar-plus-o mr-1"></i>Set expiration</a>
                    </span>
                  </div>
                  <el-button v-if="isAdmin" type="danger" size="small" @click="deleteDocument(doc.id)" class="delete-button">
                    <i class="fa fa-trash"></i>
                  </el-button>
                </div>
              </div>
              <div v-else class="no-documents">
                <p>No documents in this category</p>
                <p v-if="category.key === 'CST' && !isAdmin" class="text-muted small">
                  Documents must be uploaded before you can submit training completion
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Quarterly Reports Section -->
        <div v-if="client && (client.payer_type === 'DSPD' || client.payer_type === 'Private')" class="document-category-section mt-4">
          <h5 class="category-header">Quarterly Reports</h5>
          <div v-if="loadingQuarterlies" class="text-center py-3">
            <i class="fa fa-spinner fa-spin"></i> Loading...
          </div>
          <div v-else-if="quarterlyReports.length === 0" class="no-documents">
            <p>No quarterly reports found for this client.</p>
          </div>
          <div v-else>
            <table class="table table-sm table-dark table-hover compliance-table">
              <thead>
                <tr>
                  <th>Quarter</th>
                  <th>Service Codes</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Emailed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="qr in quarterlyReports" :key="qr.id">
                  <td>{{ formatQuarter(qr.start_date, qr.end_date) }}</td>
                  <td>{{ parseQrServiceCodes(qr.service_codes) }}</td>
                  <td>
                    <span :class="['badge', qr.status === 'final' ? 'badge-success' : 'badge-warning']">
                      {{ qr.status === 'final' ? 'Final' : 'Draft' }}
                    </span>
                  </td>
                  <td>{{ formatDate(qr.createdAt) }}</td>
                  <td>
                    <template v-if="qr.emailHistory && qr.emailHistory.length > 0">
                      <span style="color: #67c23a;">
                        <i class="fa fa-check-circle mr-1"></i>
                        {{ formatDateTime(qr.emailHistory[0].sent_at) }}
                      </span>
                      <span v-if="qr.emailHistory.length > 1" style="color: #999; font-size: 11px; margin-left: 4px;">
                        ({{ qr.emailHistory.length }} sends)
                      </span>
                    </template>
                    <span v-else style="color: #999;">—</span>
                  </td>
                  <td>
                    <el-button size="mini" type="info" @click="downloadQuarterlyPdf(qr)" :loading="qr._downloading">
                      <i class="fa fa-file-pdf"></i> PDF
                    </el-button>
                    <el-button
                      v-if="qr.emailHistory && qr.emailHistory.length > 0"
                      size="mini"
                      @click="$set(expandedEmailHistory, qr.id, !expandedEmailHistory[qr.id])"
                    >
                      <i class="fa fa-history"></i> History
                    </el-button>
                  </td>
                </tr>
                <!-- Email history expansion row -->
                <template v-for="qr in quarterlyReports">
                  <tr v-if="expandedEmailHistory[qr.id] && qr.emailHistory && qr.emailHistory.length > 0" :key="'hist-' + qr.id">
                    <td colspan="6" style="padding: 0;">
                      <div style="background: rgba(0,0,0,0.2); padding: 10px 20px;">
                        <strong style="font-size: 12px; color: #b0b0b0;">Email History</strong>
                        <table class="table table-sm table-borderless mb-0 mt-1" style="font-size: 12px;">
                          <thead>
                            <tr style="color: #999;">
                              <th>Date</th>
                              <th>Sent To</th>
                              <th>Subject</th>
                              <th>Sent By</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="eh in qr.emailHistory" :key="eh.id">
                              <td>{{ formatDateTime(eh.sent_at) }}</td>
                              <td>{{ eh.sent_to }}</td>
                              <td>{{ eh.subject }}</td>
                              <td>{{ eh.sender ? `${eh.sender.first_name} ${eh.sender.last_name}` : '—' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Support Strategies Compliance Section -->
        <div v-if="client && client.payer_type === 'DSPD'" class="document-category-section mt-4">
          <h5 class="category-header">Support Strategies</h5>
          <div v-if="loadingComplianceStrategies" class="text-center py-3">
            <i class="fa fa-spinner fa-spin"></i> Loading...
          </div>
          <div v-else-if="complianceStrategies.length === 0" class="no-documents">
            <p>No support strategies found for this client.</p>
          </div>
          <div v-else>
            <table class="table table-sm table-dark table-hover compliance-table">
              <thead>
                <tr>
                  <th>PCSP Plan Year</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Submission</th>
                  <th>Emailed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ss in complianceStrategies" :key="ss.id">
                  <td>
                    <template v-if="ss.pcsp_start_date">
                      {{ formatDate(ss.pcsp_start_date) }} &ndash; {{ formatDate(ss.pcsp_end_date) }}
                    </template>
                    <template v-else>
                      {{ formatDate(ss.createdAt) }}
                    </template>
                  </td>
                  <td>
                    <span :class="['badge', ss.status === 'final' ? 'badge-success' : 'badge-warning']">
                      {{ ss.status === 'final' ? 'Final' : 'Draft' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="ss.due_date" :class="getComplianceStrategyDueDateClass(ss)">
                      {{ formatDate(ss.due_date) }}
                    </span>
                    <span v-else style="color: #999;">—</span>
                  </td>
                  <td>
                    <span v-if="ss.status === 'final' && ss.emailHistory && ss.emailHistory.length > 0" class="text-success">
                      <i class="fa fa-check-circle mr-1"></i> Sent
                    </span>
                    <span v-else-if="ss.status === 'final'" class="text-warning">
                      <i class="fa fa-exclamation-triangle mr-1"></i> Unsent
                    </span>
                    <span v-else class="text-muted">
                      <i class="fa fa-minus-circle mr-1"></i> Missing
                    </span>
                  </td>
                  <td>
                    <template v-if="ss.emailHistory && ss.emailHistory.length > 0">
                      <span style="color: #67c23a;">
                        <i class="fa fa-check-circle mr-1"></i>
                        {{ formatDateTime(ss.emailHistory[0].sent_at) }}
                      </span>
                      <span v-if="ss.emailHistory.length > 1" style="color: #999; font-size: 11px; margin-left: 4px;">
                        ({{ ss.emailHistory.length }} sends)
                      </span>
                    </template>
                    <span v-else style="color: #999;">—</span>
                  </td>
                  <td>
                    <el-button size="mini" type="info" @click="downloadStrategyPdf(ss)" :loading="ss._downloading">
                      <i class="fa fa-file-pdf"></i> PDF
                    </el-button>
                    <el-button
                      v-if="ss.emailHistory && ss.emailHistory.length > 0"
                      size="mini"
                      @click="$set(expandedStrategyEmailHistory, ss.id, !expandedStrategyEmailHistory[ss.id])"
                    >
                      <i class="fa fa-history"></i> History
                    </el-button>
                  </td>
                </tr>
                <!-- Email history expansion row -->
                <template v-for="ss in complianceStrategies">
                  <tr v-if="expandedStrategyEmailHistory[ss.id] && ss.emailHistory && ss.emailHistory.length > 0" :key="'ss-hist-' + ss.id">
                    <td colspan="6" style="padding: 0;">
                      <div style="background: rgba(0,0,0,0.2); padding: 10px 20px;">
                        <strong style="font-size: 12px; color: #b0b0b0;">Email History</strong>
                        <table class="table table-sm table-borderless mb-0 mt-1" style="font-size: 12px;">
                          <thead>
                            <tr style="color: #999;">
                              <th>Date</th>
                              <th>Sent To</th>
                              <th>Subject</th>
                              <th>Sent By</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="eh in ss.emailHistory" :key="eh.id">
                              <td>{{ formatDateTime(eh.sent_at) }}</td>
                              <td>{{ eh.sent_to }}</td>
                              <td>{{ eh.subject }}</td>
                              <td>{{ eh.sender ? `${eh.sender.first_name} ${eh.sender.last_name}` : '—' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Edit Client Document Modal -->
        <AIVDialog
          v-if="showEditClientDocModal"
          title="Edit Client Compliance Document"
          :visible.sync="showEditClientDocModal"
          width="500px"
        >
          <div class="p-3">
            <div class="mb-3">
              <label class="form-label">Document Type</label>
              <el-input :value="editingClientDoc.type" disabled />
            </div>
            <div class="mb-3">
              <label class="form-label">Expiration Date</label>
              <el-date-picker
                v-model="editingClientDoc.expirationDate"
                type="date"
                placeholder="MM/DD/YYYY"
                value-format="yyyy-MM-dd"
                format="MM/dd/yyyy"
                :editable="true"
                style="width: 100%;"
              />
            </div>
            <div class="mb-3">
              <label class="form-label">Status</label>
              <el-select v-model="editingClientDoc.status" style="width: 100%;">
                <el-option label="Active" value="Active" />
                <el-option label="Expired" value="Expired" />
                <el-option label="Waived" value="Waived" />
              </el-select>
            </div>
          </div>
          <template #footer>
            <el-button @click="showEditClientDocModal = false">Cancel</el-button>
            <el-button type="primary" @click="saveEditClientDoc" :loading="savingClientDoc">Save</el-button>
          </template>
        </AIVDialog>
      </div>

      <!-- Units Tab -->
      <div v-if="activeTab === 'units'" class="client-tab-pane">
        <h5 class="mb-3">Unit Tracking</h5>
        <div class="row">
          <div class="col-md-6 mb-3">
            <UnitTrackingCard v-if="clientId" :clientId="clientId" />
          </div>
          <div class="col-md-6 mb-3">
            <MonthlyUnitChart v-if="clientId" :clientId="clientId" />
          </div>
        </div>
      </div>

      <!-- Access Tab (Admin only) -->
      <div v-if="activeTab === 'access' && isAdmin" class="client-tab-pane">
        <div class="user-access-section">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">User Access</h5>
            <button class="btn btn-sm btn-primary" @click="showGrantUserAccessModal = true">
              <i class="fa fa-plus"></i> Grant Access
            </button>
          </div>

          <p class="text-muted small mb-3">
            Users who can view this client's data. Access is automatically granted through shift/schedule assignments, but can also be managed manually. Admins always have access to all clients and are not shown here.
          </p>

          <div v-if="loadingUserAccess" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading...
          </div>

          <div v-else-if="userAccessRecords.length === 0" class="text-center p-3">
            <p class="text-muted">No access records found.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Granted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in userAccessRecords" :key="record.id">
                  <td>
                    <span v-if="record.user">{{ record.user.first_name }} {{ record.user.last_name }}</span>
                    <span v-else class="text-muted">Unknown</span>
                  </td>
                  <td>{{ record.user ? record.user.user_type : '-' }}</td>
                  <td>{{ record.user ? record.user.email : '-' }}</td>
                  <td>
                    <span class="badge" :class="{
                      'badge-secondary': record.source === 'shift',
                      'badge-info': record.source === 'schedule',
                      'badge-primary': record.source === 'manual',
                      'badge-warning': record.source === 'lead',
                    }">{{ record.source }}</span>
                  </td>
                  <td>{{ formatAccessDate(record.createdAt) }}</td>
                  <td>
                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="revokeUserAccess(record)"
                      title="Revoke access"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Grant User Access Modal -->
        <AIVDialog
          v-if="showGrantUserAccessModal"
          title="Grant User Access"
          :visible.sync="showGrantUserAccessModal"
          width="500px"
        >
          <div class="p-3">
            <div v-if="loadingAvailableUsers" class="text-center p-3">
              <i class="fa fa-spinner fa-spin"></i> Loading users...
            </div>
            <div v-else>
              <label class="mb-2">Select users to grant access to this client</label>
              <AIVSelect
                v-model="selectedUsersToGrant"
                :options="availableUsersOptions"
                placeholder="Search and select users..."
                :multiSelect="true"
                :searchable="true"
                :clearable="true"
              />
              <p v-if="availableUsersForAccess.length === 0" class="text-muted mt-2">
                All active users already have access to this client.
              </p>
            </div>
          </div>
          <template #footer>
            <button class="btn btn-default" @click="showGrantUserAccessModal = false" :disabled="grantingAccess">Cancel</button>
            <button
              class="btn btn-primary"
              :disabled="selectedUsersToGrant.length === 0 || grantingAccess"
              @click="grantUserAccessBulk"
            >
              <i v-if="grantingAccess" class="fa fa-spinner fa-spin mr-1"></i>
              {{ grantingAccess ? 'Granting...' : `Grant Access (${selectedUsersToGrant.length})` }}
            </button>
          </template>
        </AIVDialog>
      </div>
    </div>

    <!-- Edit Authorization Modal -->
    <AIVDialog
      v-model="$modal.state.create_or_edit_authorization"
      :title="authorizationModalTitle"
      height="95vh"
      :no-body-padding="true"
      :no-body-overflow="true"
    >
      <!-- Processing uploaded PDF -->
      <div v-if="processing" class="text-center py-5" style="padding: 20px">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Processing...</span>
        </div>
        <p class="mt-2">Processing PDF document...</p>
      </div>

      <!-- Loading existing PDF -->
      <div
        v-else-if="pdfLoading"
        class="text-center py-5"
        style="padding: 20px"
      >
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2">Loading PDF document...</p>
      </div>

      <!-- PDF loaded or no PDF -->
      <div v-else class="pdf-verification-section w-100">
        <!-- Form first, PDF below it — one scroll context (the dialog body),
             so the form is immediately usable and the PDF never squeezes it -->
        <div class="row w-100 m-0">
          <div class="col-12">
            <div class="verification-form">
              <div
                v-if="!authorizationBeingEdited && !pdfUrl"
                class="row mb-3 w-100 mx-0"
              >
                <div class="col-12 col-md-2 px-2 mb-2 mb-md-0">
                  <label class="d-block">Auth Type</label>
                  <AIVSelect
                    v-model="authorization.auth_type"
                    :options="authTypeOptions"
                  />
                </div>
                <div
                  v-if="!isUnitBasedAuth"
                  class="col-12 col-md-10 px-2 d-flex align-items-end"
                  style="padding-bottom: 3px"
                >
                  <el-button
                    size="small"
                    @click="triggerFileSelection"
                    type="primary"
                  >
                    <i class="fa fa-file-pdf mr-1"></i>
                    {{
                      authorization.pdf_filename ? "Replace PDF" : "Upload PDF"
                    }}
                  </el-button>
                  <small
                    v-if="authorization.original_filename"
                    class="text-muted ml-3"
                  >
                    Original PDF: {{ authorization.original_filename }}
                  </small>
                </div>
              </div>

              <el-form
                ref="pdfForm"
                :model="authorization"
                label-position="top"
                label-width="120px"
              >
                <AIVRow :record.sync="authorization" :fields="clientIdFields" />
                <AIVRow
                  v-if="!isUnitBasedAuth"
                  :record.sync="authorization"
                  :fields="counselorAuthFields"
                />
                <AIVRow
                  :record.sync="authorization"
                  :fields="[
                    { label: 'Fiscal Year', key: 'fiscal_year' },
                    {
                      label: 'Begin Date',
                      key: 'auth_begin_date',
                      type: 'date',
                    },
                    { label: 'End Date', key: 'auth_end_date', type: 'date' },
                  ]"
                />
                <AIVRow
                  v-if="!isUnitBasedAuth"
                  :record.sync="authorization"
                  :fields="[
                    {
                      label: 'Description',
                      key: 'auth_description',
                      type: 'textarea',
                      rows: 3,
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="authorization"
                  :fields="serviceCodeHoursRateFields"
                />
                <AIVRow
                  v-if="isUnitBasedAuth"
                  :record.sync="authorization"
                  :fields="dspdUnitsFields"
                />
                <AIVRow
                  v-if="!isUnitBasedAuth"
                  :record.sync="authorization"
                  :fields="[
                    {
                      label: 'Low Hours Threshold',
                      key: 'low_hours_threshold',
                      type: 'number',
                      placeholder:
                        'Hours remaining threshold for alerts (default: 10)',
                      hint: 'Email alert will be sent when hours remaining reach this threshold',
                    },
                  ]"
                />
              </el-form>

              <!-- Action buttons under the form, left-aligned with the form
                   fields (px-2 matches AIVRow), so they sit directly beneath
                   the last field instead of below the tall PDF viewer. -->
              <div class="row w-100 mx-0 mt-2">
                <div class="col-12 px-2 text-left">
                  <el-button
                    v-if="!processing && !pdfLoading && authorizationBeingEdited && !isUnitBasedAuth && !pdfUrl"
                    type="info"
                    round
                    @click="uploadAuthorizationPdf"
                  >
                    <i class="fa fa-file-pdf mr-1"></i>
                    Upload Authorization
                  </el-button>
                  <el-button
                    v-if="!processing"
                    type="primary"
                    round
                    @click="saveAuthorization"
                  >
                    {{ authorizationBeingEdited ? "Update" : "Create" }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="pdfUrl" class="col-12 mt-3">
            <div class="pdf-viewer-container">
              <iframe :src="pdfUrl" class="pdf-viewer"> </iframe>
            </div>
          </div>
        </div>
      </div>
    </AIVDialog>

    <!-- Approved Shifts Modal -->
    <ApprovedShiftsModal
      :visible.sync="showApprovedShiftsModal"
      :authorization-data="selectedAuthorizationForShifts"
    />

    <!-- Attach Future Auth Modal -->
    <AttachFutureAuthModal
      :visible="showFutureAuthModal"
      :client-id="futureAuthClientId"
      :service-code-id="futureAuthServiceCodeId"
      :authorization-id="newAuthorizationId"
      :payer-type="futureAuthPayerType"
      @update:visible="showFutureAuthModal = $event"
      @attached="handleFutureAuthAttached"
    />

    <!-- Shift Modal -->
    <ShiftModal
      :visible.sync="showShiftModal"
      :shift-being-edited="shiftBeingEdited"
      :available-clients="availableClients"
      :available-caregivers="availableCaregivers"
      :allow-delete="true"
      @cancel="closeShiftModal"
      @save="saveShift"
      @delete="handleEditModalDelete"
    />

    <!-- Shift Action Modal -->
    <ShiftActionModal
      :visible.sync="showShiftActionModal"
      :shift="selectedShiftForActions"
      :viewOnly="isViewOnlyMode"
      hideField="client"
      @clock-in="handleShiftClockIn"
      @clock-out="handleShiftClockOut"
      @admin-clock-out="handleAdminClockOut"
      @edit-shift="handleShiftEdit"
      @edit-shift-notes="handleEditShiftNotes"
      @edit-clock-in-time="handleEditClockInTime"
      @edit-clock-out-time="handleEditClockOutTime"
      @approve-shift="handleApproveShift"
      @undo-approval="handleUndoApproval"
      @approve-missed-shift="handleApproveMissedShift"
      @cancel-shift="handleCancelShift"
      @delete-shift="handleShiftDeletion"
      @open-tasks="handleOpenGroupTasks"
    />

    <!-- Delete Recurring Shift Dialog -->
    <DeleteRecurringShiftDialog
      :visible.sync="deleteRecurringDialog.visible"
      @this-only="deleteThisShiftOnly"
      @this-and-future="deleteThisAndFutureShifts"
      @all-shifts="deleteAllShifts"
    />

    <GroupShiftTasksModal
      :visible.sync="showGroupTasksModal"
      :shift="groupTasksShift"
      :user-id="userId"
      @refresh="loadClientShifts"
    />

    <ClockInModal
      :visible.sync="showClockInModal"
      :shift="currentShift"
      :user-id="userId"
      @clock-in-success="handleClockInSuccess"
    />

    <ClockOutModal
      :visible.sync="showClockOutModal"
      :shift="currentShift"
      :user-id="userId"
      @clock-out-success="handleClockOutSuccess"
    />

    <AdminClockOutModal
      :visible.sync="showAdminClockOutModal"
      :shift="shiftToAdminClockOut"
      @clocked-out="loadClientShifts"
    />

    <!-- Edit Clock In Time Modal -->
    <EditClockInTimeModal
      :visible.sync="showEditClockInTimeModal"
      :shift="shiftToEditClockInTime"
      @clock-in-updated="loadClientShifts"
    />

    <!-- Edit Clock Out Time Modal -->
    <EditClockOutTimeModal
      :visible.sync="showEditClockOutTimeModal"
      :shift="shiftToEditClockOutTime"
      @clock-times-updated="loadClientShifts"
    />

    <!-- Edit Shift Notes Modal -->
    <EditShiftNotesModal
      :visible.sync="showEditShiftNotesModal"
      :shift="shiftToEditNotes"
      @updated="loadClientShifts"
    />

    <!-- Approve Shift Modal -->
    <ApproveShiftModal
      :visible.sync="showApproveShiftModal"
      :shift="shiftToApprove"
      @shift-approved="loadClientShifts"
    />

    <!-- Approve Missed Shift Modal -->
    <ApproveMissedShift
      :visible.sync="showApproveMissedShiftModal"
      :shift="shiftToApproveMissed"
      @shift-approved="loadClientShifts"
    />

    <!-- Training Submission Modal -->
    <TrainingSubmissionModal
      v-model="showTrainingSubmissionModal"
      :documents="documents.CST || []"
      @submit="submitTrainingCompletion"
    />

    <!-- Document Viewer Modal -->
    <AIVDialog
      v-model="$modal.state.view_client_document_pdf"
      :title="selectedPdfName"
      width="90%"
      :no-body-padding="true"
      :no-body-overflow="true"
    >
      <div class="pdf-viewer-container">
        <div v-if="loadingPdf" class="text-center">
          <i class="fa fa-spinner fa-spin"></i> Loading document...
        </div>
        <iframe
          v-else-if="pdfUrl"
          :src="pdfUrl"
          class="pdf-viewer-frame"
          type="application/pdf"
        />
        <div v-else class="no-pdf">
          <p>Unable to load document.</p>
        </div>
      </div>
    </AIVDialog>

    <CstEditor
      :visible.sync="showCstEditor"
      :cstId="editingCstId"
      @saved="onCstSaved"
      @close="editingCstId = null"
    />

    <IntakeFormModal
      :visible.sync="showIntakeModal"
      :formId="activeIntakeFormId"
      :client="client"
      @completed="onIntakeCompleted"
    />
  </div>
</template>

<script>
import { authorizationsService } from "src/serviceHandlers/authorizationsService";
import { serviceCodesService } from "src/serviceHandlers/serviceCodesService";
import pdfUploadAPIservice from "src/serviceHandlers/pdfUploadService";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { documentsService } from "@/serviceHandlers/documentsService";
import { caregiverTrainingService } from "@/serviceHandlers/caregiverTrainingService";
import { officesService } from "@/serviceHandlers/officesService";
import { adminUsersService } from "@/serviceHandlers/adminUsersService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { counselorsService } from "@/serviceHandlers/counselorsService";
import { userClientAccessService } from "@/serviceHandlers/userClientAccessService";
import router from "src/routes/routes";
import ApprovedShiftsModal from "src/components/ApprovedShiftsModal.vue";
import AttachFutureAuthModal from "src/components/AttachFutureAuthModal.vue";
import MonthView from "@/pages/Calendar/MonthView.vue";
import ShiftModal from "@/components/ShiftModal.vue";
import ShiftActionModal from "@/components/ShiftActionModal.vue";
import DeleteRecurringShiftDialog from "@/components/DeleteRecurringShiftDialog.vue";
import EditClockInTimeModal from "@/components/EditClockInTimeModal.vue";
import EditClockOutTimeModal from "@/components/EditClockOutTimeModal.vue";
import EditShiftNotesModal from "@/components/EditShiftNotesModal.vue";
import ApproveShiftModal from "@/components/ApproveShiftModal.vue";
import ApproveMissedShift from "@/components/ApproveMissedShift.vue";
import GroupShiftTasksModal from "@/components/GroupShiftTasksModal.vue";
import ClockInModal from "@/components/ClockInModal.vue";
import ClockOutModal from "@/components/ClockOutModal.vue";
import AdminClockOutModal from "@/components/AdminClockOutModal.vue";
import TrainingSubmissionModal from "@/components/TrainingSubmissionModal.vue";
import CstAcknowledgmentTracker from "@/components/CstAcknowledgmentTracker.vue";
import CstEditor from "@/pages/CstEditor.vue";

import SupportStrategyModal from "@/components/SupportStrategyModal.vue";
import { supportStrategiesService } from "@/serviceHandlers/supportStrategiesService";
import ComplianceStatusCard from "@/components/ComplianceStatusCard.vue";
import SendComplianceFormsModal from "@/components/SendComplianceFormsModal.vue";
import ClientComplianceConfigModal from "@/components/ClientComplianceConfigModal.vue";
import { clientComplianceService } from "@/serviceHandlers/clientComplianceService";
import { quarterlyReportsService } from "@/serviceHandlers/quarterlyReportsService";
import UnitTrackingCard from "@/components/UnitTrackingCard.vue";
import MonthlyUnitChart from "@/components/MonthlyUnitChart.vue";
import { intakeFormsService } from "@/serviceHandlers/intakeFormsService";
import IntakeFormModal from "@/components/IntakeFormModal.vue";
import { cstService } from "@/serviceHandlers/cstService";
import { PDFDocument } from "pdf-lib";
import { useLoginStore } from "@/stores/loginStore";
import { isDSPDGroupShift, getCaregiverShiftStatus } from "@/utils/shiftStatus";
import AIVAddressAutocomplete from "@/components/AIVAddressAutocomplete.vue";
import AIVMapPinPicker from "@/components/AIVMapPinPicker.vue";
import conflictMessageMixin from "@/mixins/conflictMessageMixin";

const pdfService = new pdfUploadAPIservice(router);

export default {
  name: "ClientDetails",
  mixins: [conflictMessageMixin],
  components: {
    ApprovedShiftsModal,
    AttachFutureAuthModal,
    MonthView,
    ShiftModal,
    ShiftActionModal,
    DeleteRecurringShiftDialog,
    EditClockInTimeModal,
    EditClockOutTimeModal,
    EditShiftNotesModal,
    ApproveShiftModal,
    ApproveMissedShift,
    GroupShiftTasksModal,
    ClockInModal,
    ClockOutModal,
    AdminClockOutModal,
    TrainingSubmissionModal,
    CstAcknowledgmentTracker,
    CstEditor,
    SupportStrategyModal,
    ComplianceStatusCard,
    SendComplianceFormsModal,
    ClientComplianceConfigModal,
    UnitTrackingCard,
    MonthlyUnitChart,
    IntakeFormModal,
    AIVAddressAutocomplete,
    AIVMapPinPicker,
  },
  data() {
    const loginStore = useLoginStore();
    return {
      clientId: null,
      clientName: "",
      // Map pin picker state
      showMapPinPicker: false,
      mapPinPickerGeocode: "",
      mapPinPickerAddress: {},
      mapPinPickerAddressIndex: null,
      activeTab: "overview",
      detailsSubTab: "payer",
      searchQuery: "",
      activeStatusFilter: "active",
      activeStatusOptions: [
        { id: "active", name: "Active" },
        { id: "billing", name: "Billing" },
        { id: "inactive", name: "Inactive" },
      ],
      sortProp: "",
      sortOrder: "",
      authorizations: [],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      serviceCodes: [],
      authorizationBeingEdited: null,
      newAuthorization: {
        auth_number: "",
        client_vr_id: "",
        dspd_pid: "",
        client_name: "",
        auth_begin_date: "",
        auth_end_date: "",
        auth_description: "",
        counselor_name: "",
        service_code_id: null,
        fiscal_year: "",
        hours: null,
        units: null,
        remaining_units: null,
        rate: null,
        low_hours_threshold: 10,
        low_units_threshold: null,
        pdf_filename: null,
        original_filename: null,
        auth_type: "VR",
      },
      pdfFile: null,
      pdfUrl: null,
      pdfProcessed: false,
      pdfLoading: false,
      processing: false,
      originalData: {},
      showApprovedShiftsModal: false,
      selectedAuthorizationForShifts: null,
      showFutureAuthModal: false,
      showTrainingSubmissionModal: false,
      // CBS tabs data
      intakeForms: [],
      loadingIntake: false,
      showIntakeModal: false,
      activeIntakeFormId: null,
      showUploadIntakeDialog: false,
      uploadingIntakePdf: false,
      intakePdfFile: null,
      cstList: [],
      loadingCst: false,
      activeCstId: null,
      showCstEditor: false,
      editingCstId: null,
      showUploadCstDialog: false,
      uploadingCstPdf: false,
      cstPdfFile: null,
      showSendComplianceForms: false,
      showComplianceConfigModal: false,
      // Client compliance documents
      loadingClientCompliance: false,
      clientComplianceStatus: [],
      clientComplianceDocs: [],
      clientComplianceTypes: ['Human Rights', 'Media Release', 'Suicide/Self-Harm', 'Eligibility Screening', 'Liability', 'Fee Agreement', 'Consent', 'Rules', 'Grievance'],
      showClientDocUpload: false,
      clientDocUpload: { type: '', expirationDate: '', complianceItemTypes: [] },
      uploadingClientDoc: false,
      clientCompFileName: '',
      showEditClientDocModal: false,
      editingClientDoc: { id: null, type: '', expirationDate: '', status: '' },
      savingClientDoc: false,
      // Quarterly reports in compliance tab
      quarterlyReports: [],
      loadingQuarterlies: false,
      expandedEmailHistory: {},
      // Support Strategies in Compliance tab
      complianceStrategies: [],
      loadingComplianceStrategies: false,
      expandedStrategyEmailHistory: {},
      routeOptions: [
        { id: "Oral", name: "Oral" },
        { id: "Topical", name: "Topical" },
        { id: "Injection", name: "Injection" },
        { id: "Inhaled", name: "Inhaled" },
        { id: "Sublingual", name: "Sublingual" },
        { id: "Rectal", name: "Rectal" },
        { id: "Transdermal", name: "Transdermal" },
      ],
      newAuthorizationId: null,
      futureAuthClientId: null,
      futureAuthServiceCodeId: null,
      futureAuthPayerType: 'VR',
      // Calendar data
      currentMonth: new Date(),
      daysInMonth: [],
      dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      clientShifts: [],
      loadingShifts: false,
      // Notes data
      notesSearch: "",
      newNoteText: "",
      showAddNoteModal: false,
      allNotes: [],
      filteredAllNotes: [],
      loadingAllNotes: false,
      // Goals data
      goals: [],
      loadingGoals: false,
      goalFormData: {
        question_text: "",
        question_type: "free_response",
      },
      editingGoal: null,
      // Medications data
      medications: [],
      loadingMedications: false,
      medWeekdayOptions: [
        { value: 0, label: "Sun" },
        { value: 1, label: "Mon" },
        { value: 2, label: "Tue" },
        { value: 3, label: "Wed" },
        { value: 4, label: "Thu" },
        { value: 5, label: "Fri" },
        { value: 6, label: "Sat" },
      ],
      medicationColumns: [
        { key: "name", label: "Medication" },
        { key: "dosage", label: "Dosage" },
        { key: "schedule", label: "Schedule", sortable: false },
        { key: "route", label: "Route" },
        { key: "amount_remaining", label: "Remaining", sortable: false },
        { key: "lastGiven", label: "Last Given", sortable: false },
        { key: "history", label: "History", sortable: false },
        { key: "actions", label: "Actions", sortable: false,
          condition: () => this.isAdmin || this.isLead },
      ],
      medicationPagination: {
        total: 0, perPage: 20, currentPage: 1, totalPages: 0,
      },
      medicationSortProp: "",
      medicationSortOrder: "",
      showMedicationModal: false,
      showMarModal: false,
      marMonth: new Date().getMonth() + 1,
      marYear: new Date().getFullYear(),
      marDownloading: false,
      monthOptions: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],
      medicationFormData: {
        name: "",
        dosage: "",
        route: "",
        instructions: "",
        amount_remaining: null,
        special_instructions: "",
        prescribed_date: null,
        daySchedule: {},
      },
      editingMedication: null,
      showMedicationHistoryModal: false,
      medicationHistory: [],
      loadingMedicationHistory: false,
      selectedMedicationForHistory: null,
      medHistoryPagination: { total: 0, perPage: 20, currentPage: 1, totalPages: 0 },
      medHistorySortProp: "",
      medHistorySortOrder: "",
      medHistoryColumns: [
        { key: "administered_at", label: "Date" },
        { key: "administeredBy", label: "Caregiver" },
        { key: "status", label: "Status", sortable: false },
        { key: "skip_reason", label: "Skip Reason" },
        { key: "dosage_given", label: "Dosage Given" },
        { key: "notes", label: "Notes" },
      ],
      // Client details data
      client: null,
      loadingClient: false,
      // Training data
      trainingRecords: [],
      loadingTraining: false,
      currentUserTrainingStatus: null, // 'pending', 'approved', 'rejected', or null
      // Shift modals
      showShiftModal: false,
      shiftBeingEdited: null,
      showShiftActionModal: false,
      deleteRecurringDialog: { visible: false, shift: null },
      selectedShift: null,
      selectedShiftForActions: null,
      isViewOnlyMode: false,
      showEditClockInTimeModal: false,
      shiftToEditClockInTime: null,
      showEditClockOutTimeModal: false,
      shiftToEditClockOutTime: null,
      showEditShiftNotesModal: false,
      shiftToEditNotes: null,
      showApproveShiftModal: false,
      shiftToApprove: null,
      showApproveMissedShiftModal: false,
      shiftToApproveMissed: null,
      showGroupTasksModal: false,
      groupTasksShift: null,
      showClockInModal: false,
      showClockOutModal: false,
      showAdminClockOutModal: false,
      shiftToAdminClockOut: null,
      currentShift: null,
      // Support Strategy Modal
      showSupportStrategyModal: false,
      editingSupportStrategyId: null,
      // Support Strategies tab
      supportStrategies: [],
      loadingSupportStrategies: false,
      showSupportStrategyModalTab: false,
      editingSupportStrategyIdTab: null,
      showStrategyEmailModal: false,
      sendingStrategyEmail: false,
      emailingStrategyId: null,
      strategyEmailForm: {
        to: "",
        subject: "",
        body: "",
      },
      // Documents data
      documents: {
        CST: [],
        Training: [],
        BSP: [],
        SupportStrategy: [],
        Compliance: [],
        Miscellaneous: [],
        QuarterlyReport: [],
      },
      loadingDocuments: false,
      uploading: false,
      newDocument: {
        category: "",
        displayName: "",
        type: "file",
        url: "",
        file: null,
        expirationDate: null,
      },
      editingExpirationDocId: null,
      editingExpirationDate: null,
      documentCategoryOptions: [
        { id: "CST", name: "Client Specific Training (CST)" },
        { id: "Training", name: "Training" },
        { id: "BSP", name: "Behavior Support Plan (BSP)" },
        { id: "SupportStrategy", name: "Support Strategy" },
        { id: "QuarterlyReport", name: "Quarterly Report" },
        { id: "Miscellaneous", name: "Miscellaneous" },
      ],
      documentTypeOptions: [
        { id: "file", name: "Upload File" },
        { id: "link", name: "Add Link" },
      ],
      loadingPdf: false,
      selectedPdfName: "",
      apiBaseUrl:
        process.env.VUE_APP_API_BASE_URL || "http://192.168.2.90:1071",
      officeOptions: [],
      adminOptions: [],
      caregiverOptions: [],
      counselorOptions: [],
      supportCoordinatorOptions: [],
      payerTypeOptions: [
        { id: "VR", name: "VR" },
        { id: "DSPD", name: "DSPD" },
        { id: "Private", name: "Private Pay" },
      ],
      authTypeOptions: [
        { id: "VR", name: "VR" },
        { id: "DSPD", name: "DSPD" },
        { id: "PP", name: "Private Pay" },
      ],
      // User access tab data
      userAccessRecords: [],
      loadingUserAccess: false,
      showGrantUserAccessModal: false,
      availableUsersForAccess: [],
      loadingAvailableUsers: false,
      selectedUsersToGrant: [],
      grantingAccess: false,
    };
  },
  computed: {
    clientPhotoUrl() {
      if (!this.client?.photo) return null;
      // If already a full URL, use as-is
      if (this.client.photo.startsWith('http')) return this.client.photo;
      // Otherwise, build URL from API base
      const base = process.env.VUE_APP_API_URL || '';
      return `${base}/${this.client.photo}`;
    },
    draftIntakeForm() {
      return this.intakeForms.find(f => f.status === 'draft') || null;
    },
    activeMedDays() {
      return this.medWeekdayOptions.filter(
        (day) => this.medicationFormData.daySchedule[day.value] && this.medicationFormData.daySchedule[day.value].length > 0
      );
    },
    isAdmin() {
      const loginStore = useLoginStore();
      return loginStore.isAdmin;
    },
    clientComplianceTypeOptions() {
      return this.clientComplianceTypes.map((t) => ({ id: t, name: this.clientComplianceDisplayLabel(t) }));
    },
    documentCategories() {
      return [
        { key: 'CST', label: 'Client Specific Training (CST)' },
        { key: 'Training', label: 'Training' },
        { key: 'BSP', label: 'Behavior Support Plan (BSP)' },
        { key: 'SupportStrategy', label: 'Support Strategy' },
        { key: 'QuarterlyReport', label: 'Quarterly Report' },
        { key: 'Miscellaneous', label: 'Miscellaneous' },
      ];
    },
    isLead() {
      const loginStore = useLoginStore();
      return loginStore.isLead;
    },
    availableUsersOptions() {
      return this.availableUsersForAccess.map((u) => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name} (${u.user_type})`,
      }));
    },
    isEmploymentSpecialist() {
      const loginStore = useLoginStore();
      return loginStore.isEmploymentSpecialist;
    },
    isCaregiver() {
      const loginStore = useLoginStore();
      return loginStore.isCaregiver;
    },
    authorizationColumns() {
      const columns = [
        { key: "auth_number", label: "Auth Number" },
      ];
      if (this.client?.payer_type !== "DSPD") {
        columns.push({ key: "counselor_display_name", label: "Counselor" });
      }
      columns.push(
        { key: "auth_begin_date_formatted", label: "Begin Date" },
        { key: "days_since_start", label: "Days Since Start" },
        { key: "auth_end_date_formatted", label: "End Date" },
        { key: "hours_display", label: "Total" },
        { key: "hours_remaining_display", label: "Remaining" },
        { key: "rate_display", label: "Rate" },
        { key: "service_code_abbr", label: "Service" },
        { key: "actions", label: "Actions", sortable: false },
      );
      return columns;
    },
    canAddShift() {
      // Admins, Leads, and Employment Specialists can add shifts
      return this.isAdmin || this.isLead || this.isEmploymentSpecialist;
    },
    hasDSPDAuth() {
      return this.authorizations.some((auth) => {
        const num = auth.auth_number || "";
        return num.startsWith("D") || num.startsWith("P");
      });
    },
    userRole() {
      const loginStore = useLoginStore();
      return loginStore.user?.user_type || "Caregiver";
    },
    userId() {
      const loginStore = useLoginStore();
      return loginStore.user?.id;
    },
    canEditClient() {
      return this.isAdmin || this.isLead;
    },
    supportStrategyDueDate() {
      if (!this.client) return null;
      const baseDate = this.client.pcsp_start_date || this.client.initiated_services;
      if (!baseDate) return null;
      const d = new Date(baseDate + "T00:00:00");
      d.setDate(d.getDate() + 30);
      return d.toISOString().split("T")[0];
    },
    supportStrategyDueDateClass() {
      if (!this.supportStrategyDueDate) return "";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(this.supportStrategyDueDate + "T00:00:00");
      const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) return "text-danger font-weight-bold";
      if (daysUntil <= 7) return "text-warning font-weight-bold";
      return "text-success";
    },
    emergencyAndGuardianContacts() {
      if (!this.client || !this.client.contacts) return [];
      return this.client.contacts.filter(
        (c) => c.contact_type === 'emergency' || c.contact_type === 'guardian' || c.contact_type === 'responsible_party'
      );
    },
    authorization: {
      get() {
        return this.authorizationBeingEdited || this.newAuthorization;
      },
      set(val) {
        if (this.authorizationBeingEdited) {
          this.authorizationBeingEdited = val;
        } else {
          this.newAuthorization = val;
        }
      },
    },
    currentMonthYear() {
      const month = this.currentMonth.toLocaleString("default", {
        month: "long",
      });
      const year = this.currentMonth.getFullYear();
      return `${month} ${year}`;
    },
    availableClients() {
      // For client details page, only return this client
      if (this.client) {
        return [
          {
            id: this.client.id,
            first_name: this.client.first_name,
            last_name: this.client.last_name,
            name: `${this.client.first_name} ${this.client.last_name}`,
            payer_type: this.client.payer_type,
          },
        ];
      }
      return [];
    },
    availableCaregivers() {
      // This will need to be populated from the API if needed
      // For now, return empty array and the modal will load it
      return [];
    },
    canUpload() {
      if (!this.newDocument.category || !this.newDocument.displayName) {
        return false;
      }
      if (this.newDocument.type === "file") {
        return this.newDocument.file !== null;
      } else {
        return this.newDocument.url !== "";
      }
    },
    responsibleParties() {
      if (!this.client || !this.client.contacts) return [];
      return this.client.contacts.filter(
        (contact) => contact.contact_type === "responsible_party",
      );
    },
    emergencyContactsList() {
      if (!this.client || !this.client.contacts) return [];
      return this.client.contacts.filter(
        (contact) => contact.contact_type === "emergency",
      );
    },
    guardianContactsList() {
      if (!this.client || !this.client.contacts) return [];
      return this.client.contacts.filter(
        (contact) => contact.contact_type === "guardian",
      );
    },
    isDSPDAuth() {
      return this.authorization.auth_type === "DSPD";
    },
    isPPAuth() {
      return this.authorization.auth_type === "PP";
    },
    isUnitBasedAuth() {
      return this.isDSPDAuth || this.isPPAuth;
    },
    authorizationModalTitle() {
      if (this.authorizationBeingEdited) {
        const authType = this.isPPAuth ? "Private Pay" : (this.isDSPDAuth ? "DSPD" : "VR");
        return `Edit ${authType} Authorization`;
      }
      return "Create Authorization";
    },
    clientIdFields() {
      const authNumberField = {
        label: "Authorization Number",
        key: "auth_number",
      };

      // Only for DSPD/PP: disable and add tooltip
      if (this.isUnitBasedAuth) {
        authNumberField.disabled = true;
        authNumberField.tooltip = this.authorizationBeingEdited
          ? "Authorization numbers cannot be modified once created to maintain data integrity and audit trails."
          : "Authorization numbers are automatically generated upon creation.";
      }

      return [
        { label: "Client Name", key: "client_name" },
        {
          label: this.isPPAuth ? "PP Client ID" : (this.isDSPDAuth ? "DSPD PID" : "Client VR ID"),
          key: this.isUnitBasedAuth ? "dspd_pid" : "client_vr_id",
        },
        authNumberField,
      ];
    },
    counselorAuthFields() {
      const fields = [];
      if (!this.isUnitBasedAuth) {
        fields.push({ label: "Counselor Name", key: "counselor_name" });
      }
      return fields;
    },
    serviceCodeHoursRateFields() {
      const fields = [
        {
          label: "Service Code",
          key: "service_code_id",
          type: "select",
          options: this.serviceCodes,
          placeholder: "Select service code",
        },
      ];

      if (this.isUnitBasedAuth) {
        fields.push({ label: "Rate ($)", key: "rate", type: "currency" });
      } else {
        // For VR: Service Code, Hours, Rate
        fields.push({ label: "Hours", key: "hours", type: "number" });
        fields.push({ label: "Rate ($)", key: "rate", type: "currency" });
      }

      return fields;
    },
    dspdUnitsFields() {
      return [
        { label: "Units", key: "units", type: "number" },
        { label: "Remaining Units", key: "remaining_units", type: "number" },
        {
          label: "Low Units Threshold",
          key: "low_units_threshold",
          type: "number",
          placeholder: "Units remaining threshold for alerts",
        },
      ];
    },
  },
  watch: {
    "client.pcsp_start_date": function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        const start = new Date(newVal + "T00:00:00");
        const end = new Date(start);
        end.setFullYear(end.getFullYear() + 1);
        end.setDate(end.getDate() - 1);
        const yyyy = end.getFullYear();
        const mm = String(end.getMonth() + 1).padStart(2, "0");
        const dd = String(end.getDate()).padStart(2, "0");
        this.client.pcsp_end_date = `${yyyy}-${mm}-${dd}`;
      }
    },
    showGrantUserAccessModal(val) {
      if (val) {
        this.loadingAvailableUsers = true;
        this.selectedUsersToGrant = [];
        userClientAccessService.availableUsers(this.clientId)
          .then((users) => { this.availableUsersForAccess = users; })
          .catch((err) => { this.$notifyError("Error loading users: " + err); })
          .finally(() => { this.loadingAvailableUsers = false; });
      }
    },
    "authorization.auth_type": function (newVal, oldVal) {
      // Only react to changes when creating AND modal is open (not when resetting or editing)
      if (
        newVal !== oldVal &&
        oldVal !== undefined &&
        !this.authorizationBeingEdited &&
        this.$modal?.state?.create_or_edit_authorization
      ) {
        // Reload service codes when auth type changes during creation
        this.getServiceCodes(newVal);
        // Clear service code selection when switching auth types
        this.$set(this.authorization, "service_code_id", null);
      } else {
      }
    },
    "authorization.service_code_id": function (newVal) {
      if (newVal) {
        const selectedServiceCode = this.serviceCodes.find(
          (sc) => sc.id === newVal,
        );
        if (selectedServiceCode) {
          // Auto-populate rate when service code is selected
          if (!this.authorizationBeingEdited && selectedServiceCode.rate) {
            this.$set(
              this.authorization,
              "rate",
              parseFloat(selectedServiceCode.rate).toFixed(2),
            );
          }
          // Set low hours threshold based on service code (only for new authorizations and non-unit-based)
          if (!this.authorizationBeingEdited && !this.isUnitBasedAuth) {
            const code = selectedServiceCode.code;
            let threshold = 10; // default

            if (
              code === "WSA1" ||
              code === "WSA2" ||
              code === "SE" ||
              code === "CE" ||
              code === "SJBT" ||
              code === "TWE"
            ) {
              threshold = 0;
            } else if (code === "LS") {
              threshold = 6;
            } else if (code === "SB") {
              threshold = 2;
            }

            this.$set(this.authorization, "low_hours_threshold", threshold);
          }
        }
      }
    },
    async activeTab(newTab) {
      if (newTab === 'intake' && this.intakeForms.length === 0) this.loadIntakeForms();
      else if (newTab === 'cst' && this.cstList.length === 0) this.loadCstList();
      else if (newTab === "notes" && this.allNotes.length === 0) {
        await this.loadClientNotes();
      } else if (newTab === "goals") {
        await this.loadClientGoals();
      } else if (newTab === "medications") {
        if (this.medications.length === 0) {
          await this.loadClientMedications();
        }
      } else if (newTab === "supportStrategies") {
        await this.loadSupportStrategies();
      }
    },
    "client.payer_type"(newVal, oldVal) {
      if (!this.client) return;

      // Ensure addresses array is initialized
      if (!this.client.addresses || this.client.addresses.length === 0) {
        this.client.addresses = [
          this.client.address || {
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
          },
        ];
      }
    },
  },
  async mounted() {
    this.clientId = Number(this.$route.params.id);
    this.clientName = this.$route.query.name || "Client Details";

    // Set active tab from query parameter if provided
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }

    this.updateDaysInMonth();

    // Load client first
    await this.fetchClient();

    // Then load service codes for this client and other data in parallel
    await Promise.all([
      this.fetchAuthorizations(),
      this.getServiceCodes(),
      this.loadClientShifts(),
      this.loadTrainingRecords(),
      this.getOffices(),
      this.getAdmins(),
      this.getCaregivers(),
      this.getCounselors(),
      this.getSupportCoordinatorOptions(),
    ]);
  },
  methods: {
    goBack() {
      this.$router.push({ name: "Clients" });
    },

    triggerPhotoUpload() {
      this.$refs.photoInput.click();
    },

    async handlePhotoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const result = await clientsService.uploadPhoto(this.clientId, file);
        if (result.photo) {
          this.client.photo = result.photo;
          this.$notifySuccess("Photo updated successfully");
        }
      } catch (err) {
        this.$notifyError("Failed to upload photo");
      }
      // Reset file input so the same file can be re-selected
      event.target.value = "";
    },

    getResponsibleParty(priority) {
      if (!this.client || !this.client.contacts) return null;
      return this.client.contacts.find(
        (contact) =>
          contact.contact_type === "responsible_party" &&
          contact.priority === priority,
      );
    },

    getPhysician() {
      if (!this.client || !this.client.contacts) return null;
      return this.client.contacts.find(
        (contact) => contact.contact_type === "physician",
      );
    },

    async loadIntakeForms() {
      if (!this.clientId) return;
      this.loadingIntake = true;
      try {
        const data = await intakeFormsService.getByClient(this.clientId);
        this.intakeForms = data || [];
      } catch {
        this.intakeForms = [];
      } finally {
        this.loadingIntake = false;
      }
    },
    startIntake() {
      intakeFormsService.create({ id_clients: this.clientId, status: 'draft' }).then((form) => {
        this.activeIntakeFormId = form.id;
        this.showIntakeModal = true;
      }).catch((err) => {
        this.$notifyError('Failed to create intake form: ' + err);
      });
    },
    openIntakeModal(formId) {
      this.activeIntakeFormId = formId;
      this.showIntakeModal = true;
    },
    onIntakeCompleted() {
      this.showIntakeModal = false;
      this.loadIntakeForms();
    },
    async deleteIntakeForm(form) {
      try {
        await this.$confirm(
          `Are you sure you want to delete Intake #${form.id}? This cannot be undone.`,
          'Delete Intake Form',
          { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' }
        );
        await intakeFormsService.delete(form.id);
        this.$notifySuccess('Intake form deleted');
        await this.loadIntakeForms();
      } catch (err) {
        if (err !== 'cancel') {
          this.$notifyError('Error deleting intake form: ' + (err.response?.data?.message || err.message));
        }
      }
    },
    async uploadIntakePdf() {
      if (!this.intakePdfFile) return;
      this.uploadingIntakePdf = true;
      try {
        const formData = new FormData();
        formData.append("file", this.intakePdfFile);
        formData.append("clientId", this.clientId);
        const result = await intakeFormsService.uploadPdf(formData);
        let msg = result.message || "Intake uploaded and parsed successfully.";
        if (result.parseWarnings && result.parseWarnings.length > 0) {
          msg += ` ${result.parseWarnings.length} field(s) may need review.`;
        }
        this.$notifySuccess(msg);
        this.showUploadIntakeDialog = false;
        this.intakePdfFile = null;
        this.loadIntakeForms();
      } catch (err) {
        this.$notifyError(err.response?.data?.message || err.message || "Failed to upload intake PDF");
      } finally {
        this.uploadingIntakePdf = false;
      }
    },
    async deleteCst(cst) {
      try {
        await this.$confirm(
          `Are you sure you want to delete CST v${cst.version}? This cannot be undone.`,
          'Delete CST',
          { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' }
        );
        await cstService.delete(cst.id);
        this.$notifySuccess('CST deleted');
        await this.loadCstList();
      } catch (err) {
        if (err !== 'cancel') {
          this.$notifyError('Error deleting CST: ' + (err.response?.data?.message || err.message));
        }
      }
    },
    async onCstSaved() {
      await this.loadCstList();
      if (this.$refs.ackTracker) {
        this.$refs.ackTracker.fetchAcknowledgments();
      }
    },
    async loadCstList() {
      if (!this.clientId) return;
      this.loadingCst = true;
      try {
        const data = await cstService.getAllByClient(this.clientId);
        this.cstList = data || [];
        const active = this.cstList.find((c) => c.status === 'active');
        this.activeCstId = active ? active.id : null;
      } catch {
        this.cstList = [];
      } finally {
        this.loadingCst = false;
      }
    },
    createCst() {
      cstService.create({ id_clients: this.clientId, status: 'draft', version: 1 }).then((cst) => {
        this.editingCstId = cst.id;
        this.showCstEditor = true;
      }).catch((err) => {
        this.$notifyError('Failed to create CST: ' + err);
      });
    },
    async uploadCstPdf() {
      if (!this.cstPdfFile) return;
      this.uploadingCstPdf = true;
      try {
        const formData = new FormData();
        formData.append("file", this.cstPdfFile);
        formData.append("clientId", this.clientId);
        const result = await cstService.uploadPdf(formData);
        let msg = result.message || "CST uploaded and parsed successfully.";
        if (result.parseWarnings && result.parseWarnings.length > 0) {
          msg += ` ${result.parseWarnings.length} item(s) may need review.`;
        }
        this.$notifySuccess(msg);
        this.showUploadCstDialog = false;
        this.cstPdfFile = null;
        await this.loadCstList();
        // Open the populated draft so the admin can review/correct before publishing
        if (result.cstId) {
          this.editingCstId = result.cstId;
          this.showCstEditor = true;
        }
      } catch (err) {
        this.$notifyError(err.response?.data?.message || err.message || "Failed to upload CST PDF");
      } finally {
        this.uploadingCstPdf = false;
      }
    },

    async fetchClient() {
      if (!this.clientId) return;

      this.loadingClient = true;
      try {
        const clientData = await clientsService.getById(this.clientId);
        this.client = {
          ...clientData,
          address: clientData.address || {
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
          },
          addresses:
            clientData.addresses && clientData.addresses.length > 0
              ? clientData.addresses
              : [
                  clientData.address || {
                    address1: "",
                    address2: "",
                    city: "",
                    state: "",
                    zip: "",
                  },
                ],
        };
      } catch (error) {
        this.$notifyError("Could not load client details: " + error);
      } finally {
        this.loadingClient = false;
      }
    },

    addAddress() {
      if (!this.client.addresses) {
        this.client.addresses = [];
      }
      this.client.addresses.push({
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
      });
    },

    removeAddress(index) {
      this.client.addresses.splice(index, 1);
    },

    applyPlaceToAddress(index, place) {
      const addr = this.client.addresses[index];
      this.$set(addr, "address1", place.address1);
      this.$set(addr, "city", place.city);
      this.$set(addr, "state", place.state);
      this.$set(addr, "zip", place.zip);
      this.$set(addr, "geocode", place.geocode);
    },

    openMapPinPicker(index) {
      const addr = this.client.addresses[index];
      this.mapPinPickerAddressIndex = index;
      this.mapPinPickerGeocode = addr.geocode || "";
      this.mapPinPickerAddress = { city: addr.city, state: addr.state, zip: addr.zip };
      this.showMapPinPicker = true;
    },

    async handleMapPinSelected(location) {
      const index = this.mapPinPickerAddressIndex;
      if (index === null || !this.client.addresses[index]) return;
      this.$set(this.client.addresses[index], "geocode", location.geocode);
      // Auto-save the geocode
      try {
        this.$spinner.show();
        const { schedule, ...payload } = { ...this.client };
        await clientsService.update(this.client.id, payload);
        await this.fetchClient();
        this.$notifySuccess("Location set successfully.");
      } catch (error) {
        this.$notifyError("Error saving location: " + (error.message || error));
      } finally {
        this.$spinner.hide();
      }
    },

    addResponsibleParty() {
      if (!this.client.contacts) {
        this.client.contacts = [];
      }
      const maxPriority = this.responsibleParties.reduce((max, party) => {
        return Math.max(max, party.priority || 0);
      }, 0);
      this.client.contacts.push({
        id_clients: this.client.id,
        contact_type: "responsible_party",
        priority: maxPriority + 1,
        name: "",
        relationship: "",
        email: "",
        phone: "",
        phone_type: "",
        phone_alt: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        hipaa_disclosure_auth: null,
        medical_decisions: null,
      });
    },

    removeResponsibleParty(index) {
      this.removeContact("responsible_party", index);
    },

    addContact(contactType) {
      if (!this.client.contacts) {
        this.client.contacts = [];
      }
      this.client.contacts.push({
        id_clients: this.client.id,
        contact_type: contactType,
        name: "",
        relationship: "",
        email: "",
        phone: "",
        phone_type: "",
        phone_alt: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        hipaa_disclosure_auth: null,
        medical_decisions: null,
      });
    },

    removeContact(contactType, index) {
      const contacts = this.client.contacts.filter(
        (c) => c.contact_type === contactType,
      );
      const toRemove = contacts[index];
      const contactIndex = this.client.contacts.indexOf(toRemove);
      if (contactIndex > -1) {
        this.client.contacts.splice(contactIndex, 1);
      }
    },

    updateContact(contactType, index, updatedData) {
      if (!this.client || !this.client.contacts) return;
      const contacts = this.client.contacts.filter(
        (c) => c.contact_type === contactType,
      );
      if (index >= 0 && index < contacts.length) {
        const contact = contacts[index];
        const contactIndex = this.client.contacts.indexOf(contact);
        if (contactIndex !== -1) {
          this.$set(this.client.contacts, contactIndex, {
            ...this.client.contacts[contactIndex],
            ...updatedData,
          });
        }
      }
    },

    addPhysician() {
      if (!this.client.contacts) {
        this.client.contacts = [];
      }
      this.client.contacts.push({
        id_clients: this.client.id,
        contact_type: "physician",
        name: "",
        phone: "",
        email: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
      });
    },

    updatePhysician(updatedData) {
      if (!this.client || !this.client.contacts) return;
      const physicianIndex = this.client.contacts.findIndex(
        (contact) => contact.contact_type === "physician",
      );
      if (physicianIndex !== -1) {
        // Update the physician object in the contacts array
        this.$set(this.client.contacts, physicianIndex, {
          ...this.client.contacts[physicianIndex],
          ...updatedData,
        });
      }
    },

    updateResponsibleParty(index, updatedData) {
      if (!this.client || !this.client.contacts) return;
      const parties = this.client.contacts.filter(
        (contact) => contact.contact_type === "responsible_party",
      );
      if (index >= 0 && index < parties.length) {
        const party = parties[index];
        const contactIndex = this.client.contacts.indexOf(party);
        if (contactIndex !== -1) {
          // Update the party object in the contacts array
          this.$set(this.client.contacts, contactIndex, {
            ...this.client.contacts[contactIndex],
            ...updatedData,
          });
        }
      }
    },

    async saveClientDetails() {
      const clientObj = { ...this.client };

      if (!clientObj.first_name) {
        this.$notifyError("No client first name!");
        return;
      }
      if (!clientObj.last_name) {
        this.$notifyError("No client last name!");
        return;
      }

      this.$spinner.show();

      try {
        const { schedule, ...payloadWithoutSchedule } = clientObj;
        await clientsService.update(clientObj.id, payloadWithoutSchedule);
        this.$notifySuccess("Successfully updated client details.");

        // Refresh client data
        await this.fetchClient();

        // Check if any addresses are missing geocodes after save
        if (this.client.addresses && this.client.addresses.length > 0) {
          const missingGeocode = this.client.addresses.filter(
            (addr) => addr.address1 && (!addr.geocode || !addr.geocode.trim())
          );
          if (missingGeocode.length > 0) {
            this.$notify(
              `${missingGeocode.length} address(es) could not be geocoded. Fix the address or use "Set on Map" to place a pin manually.`,
              "warning"
            );
          }
        }

        // Update the client name in the header if it changed
        this.clientName = `${clientObj.first_name} ${clientObj.last_name}`;
      } catch (error) {
        this.$notifyError("Error updating client: " + (error.response?.data?.message || error.message || error));
      } finally {
        this.$spinner.hide();
      }
    },

    formatDateToMDY(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
    },

    formatHours(hours) {
      if (!hours && hours !== 0) return "";
      const numHours = parseFloat(hours);
      if (Number.isInteger(numHours)) {
        return numHours.toString();
      }
      return numHours.toString();
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return "";
      const numAmount = parseFloat(amount);
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    },

    calculateDaysSinceStart(startDate) {
      if (!startDate) return "";
      const start = new Date(startDate);
      const today = new Date();
      if (isNaN(start.getTime())) return "";

      const timeDiff = today.getTime() - start.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

      return daysDiff >= 0 ? daysDiff : 0;
    },

    async fetchAuthorizations(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);
        params.append("clientId", this.clientId); // Filter by client ID
        params.append("activeStatus", this.activeStatusFilter); // Filter by status

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await authorizationsService.listPaginated(params);
        this.authorizations = response.rows.map((auth) => {
          // Determine which fields to use based on auth number prefix
          const authNumber = auth.auth_number || "";
          let total = 0;
          let used = 0;
          let label = "";

          if (authNumber.startsWith("D")) {
            total = parseFloat(auth.units) || 0;
            used = parseFloat(auth.units_used) || 0;
            label = " units";
          } else {
            // Default to hours for VR auths (V prefix) and future auths
            total = parseFloat(auth.hours) || 0;
            used = parseFloat(auth.hours_used) || 0;
            label = " hours";
          }

          const remaining = total - used;

          return {
            ...auth,
            counselor_display_name: auth.counselor
              ? auth.counselor.name
              : "N/A",
            auth_begin_date_formatted: this.formatDateToMDY(
              auth.auth_begin_date,
            ),
            days_since_start: this.calculateDaysSinceStart(
              auth.auth_begin_date,
            ),
            auth_end_date_formatted: this.formatDateToMDY(auth.auth_end_date),
            // Display values live on their own keys (matching Authorizations.vue)
            // so the raw rate/hours survive on the row — the edit modal is fed
            // from this object, and overwriting `rate` with a display string
            // made saved DSPD rates look like they never persisted.
            hours_display: this.formatHours(total) + label,
            hours_remaining_display: this.formatHours(remaining) + label,
            rate_display: this.formatCurrency(auth.rate),
            service_code_abbr: auth.service_code ? auth.service_code.code : "",
          };
        });
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load authorizations: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },

    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },

    onStatusFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },

    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchAuthorizations(1);
    },

    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },

    getServiceCodes(authType = null) {
      // Determine filter type: use provided authType, or fall back to client's payer_type
      let filterType = this.client?.payer_type || "VR";

      if (authType) {
        // If authType is explicitly provided, use it
        filterType = authType;
      } else if (
        this.authorizationBeingEdited ||
        this.newAuthorization.auth_type !== "VR" ||
        this.$modal?.state?.create_or_edit_authorization
      ) {
        // Otherwise, check if we're in modal context
        filterType = this.authorization.auth_type || "VR";
      }

      // Load service codes based on filter type
      let serviceCodePromise;
      if (filterType === "VR") {
        serviceCodePromise = serviceCodesService.listVR();
      } else if (filterType === "DSPD") {
        serviceCodePromise = serviceCodesService.listDSPD();
      } else if (filterType === "PP") {
        serviceCodePromise = serviceCodesService.listPP();
      } else {
        // 'all' - load all service codes
        serviceCodePromise = serviceCodesService.list();
      }

      return serviceCodePromise
        .then((serviceCodes) => {
          this.serviceCodes = serviceCodes.map((sc) => ({
            id: sc.id,
            name: sc.code,
            code: sc.code,
            fullName: sc.name,
            fullLabel: `${sc.code} - ${sc.name}`,
            rate: sc.rate,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load service codes: " + error);
        });
    },

    getOffices() {
      return officesService
        .list()
        .then((offices) => {
          this.officeOptions = offices.map((office) => ({
            id: office.id,
            name: office.name,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load offices: " + error);
        });
    },

    getAdmins() {
      return adminUsersService
        .list()
        .then((admins) => {
          this.adminOptions = admins.map((admin) => ({
            id: admin.id,
            name: `${admin.first_name} ${admin.last_name}`,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load admins: " + error);
        });
    },

    getCaregivers() {
      return caregiversService
        .list()
        .then((caregivers) => {
          this.caregiverOptions = caregivers.map((caregiver) => ({
            id: caregiver.id,
            name: `${caregiver.first_name} ${caregiver.last_name}`,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load caregivers: " + error);
        });
    },

    getCounselors() {
      return counselorsService
        .list()
        .then((counselors) => {
          this.counselorOptions = counselors.map((c) => ({
            id: c.id,
            name: c.name,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load counselors: " + error);
        });
    },

    async getSupportCoordinatorOptions() {
      try {
        const counselors = await counselorsService.list();
        this.supportCoordinatorOptions = (counselors || [])
          .filter(
            (c) =>
              (c.title || "").trim().toLowerCase() === "support coordinator",
          )
          .map((c) => ({
            id: c.id,
            name: c.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        this.$notifyError("Could not load support coordinators: " + error);
      }
    },

    formatContactType(type) {
      const labels = {
        emergency: 'Emergency',
        guardian: 'Guardian',
        responsible_party: 'Responsible Party',
        healthcare: 'Healthcare',
        physician: 'Physician',
        therapist: 'Therapist',
        behaviorist: 'Behaviorist',
        legal_representative: 'Legal Rep',
        other_support: 'Other Support',
        other: 'Other',
      };
      return labels[type] || type;
    },

    async editAuthorization(authorization) {
      this.resetModal();

      // Use the client's payer_type as the auth_type (map 'Private' to 'PP')
      const clientPayer = authorization.client?.payer_type || "VR";
      const authType = clientPayer === "Private" ? "PP" : clientPayer;

      // Load service codes for this auth type FIRST, before setting the authorization
      await this.getServiceCodes(authType);

      this.authorizationBeingEdited = {
        ...authorization,
        client_name: authorization.client
          ? `${authorization.client.first_name} ${authorization.client.last_name}`
          : "",
        client_vr_id: authorization.client
          ? authorization.client.vr_client_id
          : "",
        dspd_pid: authorization.client ? authorization.client.dspd_pid : "",
        counselor_name: authorization.counselor
          ? authorization.counselor.name
          : "",
        auth_begin_date: this.formatDateForInput(authorization.auth_begin_date),
        auth_end_date: this.formatDateForInput(authorization.auth_end_date),
        service_code_id: authorization.id_service_codes,
        hours: authorization.hours,
        units: authorization.units,
        remaining_units: authorization.units != null && authorization.units_used != null
          ? parseFloat(authorization.units) - parseFloat(authorization.units_used)
          : null,
        low_hours_threshold: authorization.low_hours_threshold || 10,
        low_units_threshold: authorization.low_units_threshold,
        pdf_filename: authorization.pdf_filename,
        original_filename: authorization.original_filename,
        auth_type: authType,
      };

      if (authorization.pdf_filename) {
        this.pdfProcessed = true;
        this.loadSecurePDF(authorization.pdf_filename);
      }

      this.$modal.open("create_or_edit_authorization");
    },

    deactivateAuthorization(authorization) {
      return authorizationsService
        .deactivate(authorization.id)
        .then(() => {
          this.$notifySuccess("Successfully deactivated authorization!");
          this.fetchAuthorizations(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError("Error deactivating authorization: " + error);
        });
    },

    activateAuthorization(authorization) {
      return authorizationsService
        .activate(authorization.id)
        .then((response) => {
          const message =
            response.message || "Successfully reactivated authorization!";
          this.$notifySuccess(message);
          this.fetchAuthorizations(this.pagination.currentPage);
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message || error.message || error;
          this.$notifyError(
            "Error reactivating authorization: " + errorMessage,
          );
        });
    },

    deleteAuthorization(authorization) {
      return authorizationsService
        .hardDelete(authorization.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted authorization!");
          this.fetchAuthorizations(this.pagination.currentPage);
        })
        .catch((error) => {
          // Check if error has a response with a message
          const errorMessage =
            error.response?.data?.message || error.message || error;
          this.$notifyError("Error deleting authorization: " + errorMessage);
        });
    },

    viewApprovedShifts(authorization) {
      this.selectedAuthorizationForShifts = {
        ...authorization,
        client_display_name: authorization.client
          ? `${authorization.client.first_name} ${authorization.client.last_name}`
          : authorization.client_display_name || "Unknown Client",
      };
      this.showApprovedShiftsModal = true;
    },

    viewAuthorizationDetails(authorization) {
      this.$router.push({
        name: "ClientAuthorizationDetails",
        params: {
          clientId: this.clientId,
          id: authorization.id,
        },
        query: {
          authNumber: authorization.auth_number,
          clientName: this.clientName,
        },
      });
    },

    resetModal() {
      this.authorizationBeingEdited = null;
      this.resetPdf();
      this.newAuthorization = {
        auth_number: "",
        client_vr_id: "",
        dspd_pid: "",
        client_name: "",
        auth_begin_date: "",
        auth_end_date: "",
        auth_description: "",
        counselor_name: "",
        service_code_id: null,
        fiscal_year: "",
        hours: null,
        units: null,
        remaining_units: null,
        rate: null,
        low_hours_threshold: 10,
        low_units_threshold: null,
        pdf_filename: null,
        original_filename: null,
        auth_type: "VR",
      };
    },

    formatDateForInput(dateString) {
      if (!dateString) return "";

      if (dateString.includes("/")) {
        const [month, day, year] = dateString.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }

      if (dateString.includes("T") || dateString.includes(" ")) {
        return dateString.split(/[T ]/)[0];
      }

      return dateString;
    },

    resetPdf() {
      this.pdfFile = null;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.pdfProcessed = false;
      this.pdfLoading = false;
      this.processing = false;
      this.originalData = {};
    },

    uploadAuthorizationPdf() {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";

      fileInput.onchange = async (event) => {
        const file = event.target.files[0];
        if (file && file.type === "application/pdf") {
          this.$modal.open("create_or_edit_authorization");
          this.processing = true;
          try {
            const processedFile = await this.checkAndProcessMultiPagePdf(file);
            const data = await pdfService.uploadPdf(processedFile);
            this.$set(this.authorization, "pdf_filename", data.filename);
            this.$set(
              this.authorization,
              "original_filename",
              data.originalFilename,
            );
            this.pdfUrl = URL.createObjectURL(processedFile);
            this.pdfProcessed = true;
            this.$notifySuccess(
              "PDF uploaded. Click Update to save.",
            );
          } catch (error) {
            this.$notifyError("Error uploading PDF. Please try again.");
          } finally {
            this.processing = false;
          }
        } else {
          this.$modal.open("create_or_edit_authorization");
        }
      };

      fileInput.oncancel = () => {
        this.$modal.open("create_or_edit_authorization");
      };

      let dialogHandled = false;

      const handleDialogDismissal = () => {
        if (!dialogHandled) {
          dialogHandled = true;
          setTimeout(() => {
            if (
              !this.$modal.state.create_or_edit_authorization &&
              !fileInput.value
            ) {
              this.$modal.open("create_or_edit_authorization");
            }
          }, 100);
        }
      };

      window.addEventListener("focus", handleDialogDismissal, { once: true });

      setTimeout(() => {
        window.removeEventListener("focus", handleDialogDismissal);
      }, 5000);

      fileInput.click();
    },

    triggerFileSelection() {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";

      fileInput.onchange = (event) => {
        const file = event.target.files[0];
        if (file && file.type === "application/pdf") {
          this.$modal.open("create_or_edit_authorization");
          this.processPdfFile(file);
        } else {
          this.$modal.open("create_or_edit_authorization");
        }
      };

      fileInput.oncancel = () => {
        this.$modal.open("create_or_edit_authorization");
      };

      let dialogHandled = false;

      const handleDialogDismissal = () => {
        if (!dialogHandled) {
          dialogHandled = true;
          setTimeout(() => {
            if (
              !this.$modal.state.create_or_edit_authorization &&
              !fileInput.value
            ) {
              this.$modal.open("create_or_edit_authorization");
            }
          }, 100);
        }
      };

      window.addEventListener("focus", handleDialogDismissal, { once: true });

      setTimeout(() => {
        window.removeEventListener("focus", handleDialogDismissal);
      }, 5000);

      fileInput.click();
    },

    async processPdfFile(file) {
      this.pdfFile = file;
      this.processing = true;

      try {
        // Check if PDF has multiple pages and extract first page if needed
        const processedFile = await this.checkAndProcessMultiPagePdf(file);

        this.pdfUrl = URL.createObjectURL(processedFile);

        const data = await pdfService.uploadPdf(processedFile);

        this.originalData = { ...data };

        let serviceCodeId = data.serviceCodeId;
        if (!serviceCodeId && data.serviceCode) {
          const matchingServiceCode = this.serviceCodes.find(
            (sc) => sc.code === data.serviceCode,
          );
          serviceCodeId = matchingServiceCode ? matchingServiceCode.id : null;
        }

        // Determine default low hours threshold based on service code
        let defaultLowHoursThreshold = 10;
        const selectedServiceCode = this.serviceCodes.find(
          (sc) => sc.id === serviceCodeId,
        );
        if (selectedServiceCode) {
          const code = selectedServiceCode.code;
          if (
            code === "WSA1" ||
            code === "WSA2" ||
            code === "SE" ||
            code === "CE" ||
            code === "SJBT" ||
            code === "TWE"
          ) {
            defaultLowHoursThreshold = 0;
          } else if (code === "LS") {
            defaultLowHoursThreshold = 6;
          } else if (code === "SB") {
            defaultLowHoursThreshold = 2;
          }
        }

        this.newAuthorization = {
          auth_number: data.authorizationNumber || "",
          client_vr_id: data.clientID || "",
          client_name: data.clientName || "",
          auth_begin_date: this.formatDateForInput(data.authorizationBegin),
          auth_end_date: this.formatDateForInput(data.authorizationEnd),
          auth_description: data.description || "",
          counselor_name: data.counselor || "",
          service_code_id: serviceCodeId,
          fiscal_year: data.fiscalYear || "",
          hours: data.calculatedHours || null,
          rate: data.hourlyRate ? parseFloat(data.hourlyRate).toFixed(2) : null,
          low_hours_threshold: defaultLowHoursThreshold,
          pdf_filename: data.filename || null,
          original_filename: data.originalFilename || null,
          auth_type: this.newAuthorization.auth_type || "VR",
        };

        this.pdfProcessed = true;
      } catch (error) {
        this.$notifyError("Error processing PDF. Please try again.");
      } finally {
        this.processing = false;
      }
    },

    async checkAndProcessMultiPagePdf(file) {
      try {
        // Read the PDF file as array buffer
        const arrayBuffer = await file.arrayBuffer();

        // Load the PDF document
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        // Get page count
        const pageCount = pdfDoc.getPageCount();

        // If more than one page, show notification and extract first page
        if (pageCount > 1) {
          this.$notifyInfo(
            `This PDF has ${pageCount} pages. Only the first page will be processed.`,
          );

          // Create a new PDF with only the first page
          const newPdfDoc = await PDFDocument.create();
          const [firstPage] = await newPdfDoc.copyPages(pdfDoc, [0]);
          newPdfDoc.addPage(firstPage);

          // Serialize the new PDF
          const pdfBytes = await newPdfDoc.save();

          // Create a new File object with the first page only
          return new File([pdfBytes], file.name, { type: "application/pdf" });
        }

        // If single page, return original file
        return file;
      } catch (error) {
        // If there's an error reading the PDF, return the original file
        return file;
      }
    },

    async loadSecurePDF(filename) {
      this.pdfLoading = true;

      try {
        const token = localStorage.getItem("jwtToken");
        if (!token) {
          this.$notifyError("Authentication required to view PDF");
          return;
        }

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/pdf/secure/${filename}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
            },
          },
        );

        if (!response.ok) {
          if (response.status === 401) {
            this.$notifyError("Authentication required to view PDF");
            this.$router.push({ name: "Login" });
          } else if (response.status === 403) {
            this.$notifyError("You don't have permission to view this PDF");
          } else if (response.status === 404) {
            this.$notifyError("PDF file not found");
          } else {
            this.$notifyError("Failed to load PDF file");
          }
          return;
        }

        const blob = await response.blob();
        this.pdfUrl = URL.createObjectURL(blob);
      } catch (error) {
        this.$notifyError("Failed to load PDF file");
      } finally {
        this.pdfLoading = false;
      }
    },

    async checkDuplicateAuthNumber(authNumber, excludeId = null) {
      try {
        if (!authNumber || !authNumber.trim()) {
          return false;
        }

        const existingAuth = this.authorizations.find((auth) => {
          const isSameAuthNumber =
            auth.auth_number &&
            auth.auth_number.toLowerCase().trim() ===
              authNumber.toLowerCase().trim();
          const isDifferentAuth = excludeId ? auth.id !== excludeId : true;
          return isSameAuthNumber && isDifferentAuth;
        });

        return !!existingAuth;
      } catch (error) {
        return false;
      }
    },

    parseClientName(fullName) {
      if (!fullName || typeof fullName !== "string")
        return { first_name: "", last_name: "" };

      const trimmedName = fullName.trim();

      if (trimmedName.includes(",")) {
        const parts = trimmedName.split(",").map((part) => part.trim());
        if (parts.length >= 2) {
          return {
            first_name: parts[1] || "",
            last_name: parts[0] || "",
          };
        }
      }

      const nameParts = trimmedName
        .split(" ")
        .filter((part) => part.length > 0);
      if (nameParts.length >= 2) {
        return {
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(" "),
        };
      }

      return {
        first_name: "",
        last_name: trimmedName,
      };
    },

    async saveAuthorization() {
      const authObj = this.authorization;

      const requiredFields = [
        { field: "client_name", name: "Client Name" },
        { field: "auth_begin_date", name: "Begin Date" },
        { field: "auth_end_date", name: "End Date" },
        { field: "fiscal_year", name: "Fiscal Year" },
        { field: "service_code_id", name: "Service Code" },
      ];

      // Add conditional required fields based on auth type
      if (!this.isUnitBasedAuth) {
        requiredFields.push({ field: "auth_description", name: "Description" });
        requiredFields.push({
          field: "counselor_name",
          name: "Counselor Name",
        });
        requiredFields.push({ field: "rate", name: "Rate" });
        requiredFields.push({ field: "client_vr_id", name: "Client VR ID" });
        requiredFields.push({ field: "hours", name: "Hours" });
      } else {
        requiredFields.push({ field: "dspd_pid", name: "DSPD PID" });
        requiredFields.push({ field: "units", name: "Units" });
        requiredFields.push({ field: "rate", name: "Rate" });
      }

      // Auth number is always required, except for new DSPD/PP authorizations (auto-generated)
      if (this.authorizationBeingEdited || !this.isUnitBasedAuth) {
        requiredFields.push({
          field: "auth_number",
          name: "Authorization Number",
        });
      }

      for (const { field, name } of requiredFields) {
        if (
          !authObj[field] ||
          (typeof authObj[field] === "string" && authObj[field].trim() === "")
        ) {
          this.$notifyError(`${name} is required!`);
          return;
        }
      }

      // Check for duplicate authorization number (skip for new DSPD/PP authorizations as it's auto-generated)
      if (this.authorizationBeingEdited || !this.isUnitBasedAuth) {
        const isDuplicate = await this.checkDuplicateAuthNumber(
          authObj.auth_number,
          this.authorizationBeingEdited?.id,
        );

        if (isDuplicate) {
          this.$notifyError(
            `Authorization number "${authObj.auth_number}" already exists in the system. Please use a different number.`,
          );
          return;
        }
      }

      // Validate hours or units based on auth type
      if (this.isUnitBasedAuth) {
        if (authObj.units <= 0) {
          this.$notifyError("Units must be greater than 0!");
          return;
        }
      } else {
        if (authObj.hours <= 0) {
          this.$notifyError("Hours must be greater than 0!");
          return;
        }
      }

      // Remove currency formatting from rate field before validation
      const cleanRate =
        typeof authObj.rate === "string"
          ? parseFloat(authObj.rate.replace(/[$,]/g, ""))
          : parseFloat(authObj.rate);

      if (isNaN(cleanRate) || cleanRate <= 0) {
        this.$notifyError("Rate must be greater than 0!");
        return;
      }

      if (
        new Date(authObj.auth_begin_date) >= new Date(authObj.auth_end_date)
      ) {
        this.$notifyError("End date must be after begin date!");
        return;
      }

      this.$spinner.show();

      const parsedName = this.parseClientName(authObj.client_name);
      const authData = {
        ...authObj,
        client_name: `${parsedName.first_name} ${parsedName.last_name}`.trim(),
        client_first_name: parsedName.first_name,
        client_last_name: parsedName.last_name,
      };

      // Include fields based on auth type
      if (this.isUnitBasedAuth) {
        // DSPD/PP: Include units, rate, and units_used (derived from remaining_units); exclude hours
        authData.dspd_pid = authObj.dspd_pid;
        authData.units = authObj.units;
        authData.units_used = parseFloat(authObj.units || 0) - parseFloat(authObj.remaining_units || 0);
        authData.rate = cleanRate;
        authData.low_units_threshold = authObj.low_units_threshold;
        authData.hours = 0; // Set hours to 0 for unit-based auths
      } else {
        // VR: Include rate and hours, exclude units
        authData.client_vr_id = authObj.client_vr_id;
        authData.rate = cleanRate;
        authData.hours = authObj.hours;
        authData.units = null;
      }

      let apiSaveCall;

      if (this.authorizationBeingEdited) {
        apiSaveCall = authorizationsService
          .update(authData.id, authData)
          .then(() => {
            this.$notifySuccess("Successfully updated authorization.");
          });
      } else {
        apiSaveCall = authorizationsService
          .create(authData)
          .then((response) => {
            this.$notifySuccess("Successfully created authorization.");
            const createdAuth = response;
            if (
              createdAuth &&
              createdAuth.id_clients &&
              createdAuth.id_service_codes
            ) {
              this.newAuthorizationId = createdAuth.id;
              this.futureAuthClientId = createdAuth.id_clients;
              this.futureAuthServiceCodeId = createdAuth.id_service_codes;
              this.futureAuthPayerType = this.isPPAuth ? 'PP' : (this.isDSPDAuth ? 'DSPD' : 'VR');
            }
            return response;
          });
      }

      apiSaveCall
        .then(() => {
          return this.fetchAuthorizations();
        })
        .then(() => {
          // Only close modal and reset on success
          this.$modal.close("create_or_edit_authorization");
          const wasNewAuth = !this.authorizationBeingEdited;
          const hasAuthId = this.newAuthorizationId;
          this.resetModal();
          if (wasNewAuth && hasAuthId) {
            setTimeout(() => {
              this.checkForFutureAuthorizations();
            }, 300);
          }
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            const errorData = error.response.data;
            this.$notifyError(
              errorData.message ||
                "Authorization number already exists in the system.",
            );
          } else if (error.response?.status === 400) {
            const errorData = error.response.data;
            this.$notifyError(errorData.message || "Invalid data provided.");
          } else {
            this.$notifyError(
              "Error saving authorization: " +
                (error.response?.data?.message || error.message || error),
            );
          }
        })
        .finally(() => {
          this.$spinner.hide();
        });
    },

    async checkForFutureAuthorizations() {
      if (!this.futureAuthClientId || !this.newAuthorizationId) {
        return;
      }

      try {
        const response = await authorizationsService.getFutureAuthorizations(
          this.futureAuthClientId,
          this.futureAuthServiceCodeId,
        );

        if (response && response.length > 0) {
          this.showFutureAuthModal = true;
        }
      } catch (error) {
        // Don't show error to user as this is a non-critical feature
      }
    },

    handleFutureAuthAttached(result) {
      this.fetchAuthorizations();
      this.newAuthorizationId = null;
      this.futureAuthClientId = null;
      this.futureAuthServiceCodeId = null;
      this.futureAuthPayerType = 'VR';
    },

    // Calendar methods
    async loadClientShifts() {
      if (!this.clientId) return;

      this.loadingShifts = true;
      try {
        const shifts = await this.getClientShifts(this.clientId);
        this.clientShifts = shifts;
      } catch (error) {
        this.$notifyError("Error loading client schedule: " + error);
      } finally {
        this.loadingShifts = false;
      }
    },

    async getClientShifts(clientId) {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

      const params = new URLSearchParams();
      params.append("startDate", startDate.toISOString());
      params.append("endDate", endDate.toISOString());
      params.append("clientId", clientId);
      params.append("userType", this.userRole);
      params.append("userId", this.userId);
      params.append("perPage", "1000");

      const response = await shiftsService.getByRole(params);

      return response.shifts.map((shift) => {
        let status = "scheduled";

        // For group shifts, check if the current user is an assigned caregiver
        // Use shift_type === 'Group' (not isDSPDGroupShift) so this works even without service_codes
        if (shift.shift_type === 'Group' && shift.caregivers && shift.caregivers.length > 0) {
          const isAssigned = shift.caregivers.some(c => Number(c.id) === Number(this.userId));
          if (isAssigned) {
            const clockTimes = shift.caregiverClockTimes || [];
            const myClockTime = clockTimes.find(ct => Number(ct.id_users) === Number(this.userId));
            const overallStatus = (shift.shift_status || shift.status || '').toLowerCase();
            const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
            const today = new Date();
            const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
            const dayPast = shiftDate && shiftDate < todayStr;

            if (!myClockTime || !myClockTime.clock_in) {
              if (overallStatus === 'approved') status = 'approved';
              else if (overallStatus === 'missed') status = 'missed';
              else if (dayPast) status = 'missed';
              else status = 'scheduled';
            } else if (myClockTime.clock_in && !myClockTime.clock_out) {
              status = dayPast ? 'error' : 'clocked-in';
            } else if (myClockTime.clock_out) {
              status = overallStatus === 'approved' ? 'approved' : 'clocked-out';
            }
          } else if (isDSPDGroupShift(shift)) {
            const statuses = shift.caregivers.map(c => getCaregiverShiftStatus(shift, c.id));
            if (statuses.some(s => s === 'error')) status = 'error';
            else if (statuses.every(s => s === 'approved')) status = 'approved';
            else if (statuses.every(s => s === 'missed')) status = 'missed';
            else if (statuses.every(s => s === 'missed' || s === 'error')) status = 'error';
            else if (statuses.some(s => s === 'clocked-in')) status = 'clocked-in';
            else if (statuses.some(s => s === 'clocked-out')) status = 'clocked-out';
          }
        } else {
          const shiftStatus = shift.shift_status || shift.status;

          if (shiftStatus && shiftStatus.toLowerCase() === "missed") {
            status = "missed";
          } else if (shiftStatus && shiftStatus.toLowerCase() === "approved") {
            status = "approved";
          } else if ((shiftStatus && shiftStatus.toLowerCase() === "clocked out") || shift.clock_out || shift.clockOut) {
            status = "clocked-out";
          } else if ((shiftStatus && shiftStatus.toLowerCase() === "clocked in") || shift.clock_in || shift.clockIn) {
            status = "clocked-in";
          } else if (shiftStatus && shiftStatus.toLowerCase() === "canceled") {
            status = "canceled";
          }

          // No status set — check if the shift date has passed
          if (status === "scheduled" && !shiftStatus) {
            const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
            if (shiftDate) {
              const today = new Date();
              const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
              if (shiftDate < todayStr) {
                if (!shift.clockIn && !shift.clock_in) {
                  status = 'missed';
                } else {
                  status = 'error';
                }
              }
            }
          }
        }

        const caregiverName =
          shift.caregivers && shift.caregivers.length > 0
            ? shift.caregivers[0].name ||
              `${shift.caregivers[0].first_name || ""} ${shift.caregivers[0].last_name || ""}`.trim()
            : "Unassigned";

        const clientName =
          shift.clients && shift.clients.length > 0
            ? shift.clients[0].name ||
              `${shift.clients[0].first_name || ""} ${shift.clients[0].last_name || ""}`.trim()
            : "Unassigned";

        const rawStartTime =
          shift.scheduled_start || shift.startTime || "09:00";
        const rawEndTime = shift.scheduled_end || shift.endTime || "17:00";

        const { localStartTime, localEndTime } = this.convertUTCToLocal(
          shift.date,
          rawStartTime,
          rawEndTime,
        );

        return {
          id: shift.id,
          date: shift.date,
          startTime: localStartTime,
          endTime: localEndTime,
          status: status,
          clientName: clientName,
          caregiver: caregiverName,
          caregivers: shift.caregivers || [],
          clients: shift.clients || [],
          payer: shift.payer || "DSPD",
          shiftType: shift.shift_type || "1-on-1",
          groupName: shift.group_name || null,
          originalData: shift, // Store original shift data for modal access
        };
      });
    },

    formatTime(timeString) {
      if (!timeString) return "";

      const timeParts = timeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${hours}:${minutes}${ampm}`;
    },

    updateDaysInMonth() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const arr = [];
      const first = new Date(year, month, 1);
      const firstWeekday = first.getDay();

      for (let i = 0; i < firstWeekday; i++) {
        arr.push(new Date(year, month, i - firstWeekday + 1));
      }

      const d = new Date(year, month, 1);
      while (d.getMonth() === month) {
        arr.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }

      const total = arr.length;
      const trailing = (7 - (total % 7)) % 7;
      for (let i = 1; i <= trailing; i++) {
        arr.push(new Date(year, month + 1, i));
      }
      this.daysInMonth = arr;
    },

    goToPreviousMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() - 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      this.loadClientShifts();
    },

    goToNextMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() + 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      this.loadClientShifts();
    },

    shiftsForDate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      return this.clientShifts
        .filter((shift) => shift.date === dateStr)
        .map((shift) => {
          let statusClass = "status-scheduled";

          if (shift.status === "missed") {
            statusClass = "status-missed";
          } else if (shift.status === "approved") {
            statusClass = "status-approved";
          } else if (shift.status === "clocked-out") {
            statusClass = "status-clocked-out";
          } else if (shift.status === "clocked-in") {
            statusClass = "status-clocked-in";
          } else if (shift.status === "error") {
            statusClass = "status-error";
          } else if (shift.status === "canceled") {
            statusClass = "status-canceled";
          } else if (shift.status === "unassigned") {
            statusClass = "status-unassigned";
          } else {
            // Check if shift is unassigned (no caregivers and status is scheduled or empty)
            const hasCaregivers = shift.caregivers && shift.caregivers.length > 0;
            const hasCaregiver =
              shift.caregiver && shift.caregiver !== "Unassigned";
            if (!hasCaregivers && !hasCaregiver) {
              statusClass = "status-unassigned";
            }
          }

          // Convert actual clock in/out times from UTC to local
          const convertUTCDateTimeToLocal = (utcDateTimeStr) => {
            if (!utcDateTimeStr) return null;
            const utcDate = new Date(utcDateTimeStr);
            if (isNaN(utcDate.getTime())) return null;
            return utcDate.toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
            });
          };

          const clockInTime =
            shift.originalData?.clock_in || shift.originalData?.clockIn;
          const clockOutTime =
            shift.originalData?.clock_out || shift.originalData?.clockOut;

          const actualStartTime = clockInTime
            ? convertUTCDateTimeToLocal(clockInTime)
            : null;
          const actualEndTime = clockOutTime
            ? convertUTCDateTimeToLocal(clockOutTime)
            : null;

          // Determine which times to show as primary display
          const hasClockIn =
            shift.status === "clocked-in" ||
            shift.status === "clocked-out" ||
            shift.status === "approved";
          const hasClockOut =
            shift.status === "clocked-out" || shift.status === "approved";

          // On client calendar, show caregiver name as primary (client is already known)
          let primaryDisplay = shift.caregiver || "Unassigned";
          if (shift.caregivers && shift.caregivers.length > 1) {
            primaryDisplay = shift.groupName || "Multiple";
          }

          // For group shifts with multiple clients, use groupName
          let groupName = null;
          if (shift.clients && shift.clients.length > 1) {
            groupName = shift.groupName || "Multiple";
          }

          return {
            ...shift,
            // For display on calendar (primary times)
            formattedStartTime:
              hasClockIn && actualStartTime
                ? this.formatTime(actualStartTime)
                : this.formatTime(shift.startTime),
            formattedEndTime:
              hasClockOut && actualEndTime
                ? this.formatTime(actualEndTime)
                : this.formatTime(shift.endTime),
            // Always include scheduled times for tooltip
            formattedScheduledStartTime: this.formatTime(shift.startTime),
            formattedScheduledEndTime: this.formatTime(shift.endTime),
            scheduledStartTime: shift.startTime,
            scheduledEndTime: shift.endTime,
            actualStartTime: actualStartTime,
            actualEndTime: actualEndTime,
            client: groupName ? null : primaryDisplay,
            caregiver: null,
            groupName: groupName || null,
            statusClass: statusClass,
            payer: shift.payer || "DSPD",
            shiftType: shift.shiftType || "1-on-1",
            hasClockIn: hasClockIn,
            hasClockOut: hasClockOut,
          };
        });
    },

    isToday(dateObj) {
      const now = new Date();
      return (
        dateObj.getFullYear() === now.getFullYear() &&
        dateObj.getMonth() === now.getMonth() &&
        dateObj.getDate() === now.getDate()
      );
    },

    viewShiftDetails(shift) {
      this.selectedShift = shift;
      this.selectedShiftForActions = shift;

      // Check if shift is approved - if so, show Shift Details modal instead of Edit
      const isShiftApproved = shift.status === "approved";

      // Check if shift is missed - if so, show action modal to allow approval
      const isShiftMissed = shift.status === "missed";

      // Check if user can clock in, clock out, or edit
      const canClockIn = this.canUserClockIntoShift(shift);
      const canClockOut = this.canUserClockOutOfShift(shift);
      const canEdit = this.canUserEditShift(shift);

      // If shift is approved, show the action modal (allows Undo Approval for Admins)
      if (isShiftApproved) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      // If shift is missed, show action modal (admin/lead will see approve button)
      if (isShiftMissed) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      // If user can't clock in but can edit, go straight to edit
      // EXCEPT for clocked-in, clocked-out, or error shifts where admins should see action modal for time editing
      // EXCEPT for assigned admins who need to see the action modal for clock-in/out buttons
      const isShiftClockedIn =
        shift.status === "clocked-in" || shift.status === "clocked in";
      const isShiftClockedOut =
        shift.status === "clocked-out" || shift.status === "clocked out";
      const isShiftError = shift.status === "error";
      const isAssignedCaregiver = (shift.originalData?.caregivers || shift.caregivers || []).some(
        (c) => Number(c.id) === Number(this.userId)
      );
      if (
        !canClockIn &&
        !canClockOut &&
        canEdit &&
        !isShiftClockedIn &&
        !isShiftClockedOut &&
        !isShiftError &&
        !isAssignedCaregiver
      ) {
        this.editShift(shift);
        return;
      }

      // If user can't clock in, can't clock out, and can't edit, show view-only modal
      if (!canClockIn && !canClockOut && !canEdit) {
        this.isViewOnlyMode = true;
        this.showShiftActionModal = true;
        return;
      }

      // Reset view-only mode for normal cases (user can perform some action)
      this.isViewOnlyMode = false;

      // Show the action modal which will determine available actions
      this.showShiftActionModal = true;
    },

    canUserEditShift(shift) {
      const role = this.userRole;
      if (role === "Admin" || role === "Lead" || role === "Employment Specialist") {
        return true;
      }

      // Caregivers assigned to clocked-out or missed shifts can edit them
      if (role === "Caregiver" && shift) {
        const status = shift.status;
        const isAssigned = (shift.originalData?.caregivers || shift.caregivers || []).some(
          (c) => Number(c.id) === Number(this.userId)
        );
        if (isAssigned && (status === "clocked-out" || status === "clocked out" || status === "missed")) {
          return true;
        }
      }

      return false;
    },

    canUserClockIntoShift(shift) {
      if (!shift.id) return false;

      const status = shift.status;
      const isLinkedToUser = shift.originalData?.caregivers?.some(
        (c) =>
          Number(c.id) === Number(this.userId) ||
          String(c.id) === String(this.userId),
      );

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const shiftDateStr = shift.date.split("T")[0];
      const isToday = todayStr === shiftDateStr;

      const now = new Date();
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

      const startTime =
        shift.originalData?.scheduled_start || shift.startTime || "";
      const endTime = shift.originalData?.scheduled_end || shift.endTime || "";

      if (startTime && endTime) {
        const convertUTCTimeToLocalMinutes = (timeStr, dateStr) => {
          // Handle full ISO datetime strings
          if (timeStr && timeStr.includes('T')) {
            const utcDate = new Date(timeStr);
            if (!isNaN(utcDate.getTime())) {
              return utcDate.getHours() * 60 + utcDate.getMinutes();
            }
          }
          const dt = new Date(`${dateStr}T${timeStr}Z`);
          return dt.getHours() * 60 + dt.getMinutes();
        };

        const startMinutes = convertUTCTimeToLocalMinutes(
          startTime,
          shiftDateStr,
        );
        const endMinutes = convertUTCTimeToLocalMinutes(endTime, shiftDateStr);
        // CMP exception: allow clock-in any time during the calendar day
        const isCMPShift = (shift.originalData?.service_codes || shift.service_codes || [])
          .some(sc => sc.code === 'CMP');

        const allowedStartMinutes = startMinutes - 30; // 30 minutes before

        const isInTimeWindow = isCMPShift
          ? true
          : (currentTimeMinutes >= allowedStartMinutes &&
             currentTimeMinutes < endMinutes);

        // For group shifts, check the individual caregiver's status
        // (the overall shift status may not be "scheduled" if another caregiver already clocked in)
        const shiftData = shift.originalData || shift;
        let isScheduledStatus;
        if (isDSPDGroupShift(shiftData)) {
          const caregiverStatus = getCaregiverShiftStatus(shiftData, this.userId);
          isScheduledStatus = caregiverStatus === 'scheduled';
        } else {
          isScheduledStatus = status === "scheduled";
        }

        return (
          isScheduledStatus && isLinkedToUser && isToday && isInTimeWindow
        );
      }

      return false;
    },

    canUserClockOutOfShift(shift) {
      if (!shift.id) return false;

      // Check both status and shift_status fields
      const status = shift.status || shift.originalData?.shift_status;
      const isLinkedToUser = shift.originalData?.caregivers?.some(
        (c) =>
          Number(c.id) === Number(this.userId) ||
          String(c.id) === String(this.userId),
      );

      // Handle various status formats for clocked in
      const normalizedStatus = status
        ? status.toLowerCase().replace(/\s+/g, "-")
        : "";
      const isClockedIn =
        normalizedStatus === "clocked-in" || status === "Clocked In";

      // Only allow clock out if shift is clocked in and user is the assigned caregiver
      return isClockedIn && isLinkedToUser;
    },

    async editShift(shift) {
      // Convert the calendar shift format to the format expected by ShiftModal
      // Use the original UTC times from the database, not the converted local times

      // Extract clientId from clients array to ensure it's loaded first
      let clientId = null;
      if (shift.originalData.clients && shift.originalData.clients.length > 0) {
        clientId =
          Number(shift.originalData.clients[0].id) ||
          shift.originalData.clients[0].id;
      }

      const shiftToEdit = {
        ...shift.originalData,
        id: shift.id,
        date: shift.date,
        scheduled_start:
          shift.originalData.scheduled_start || shift.originalData.startTime,
        scheduled_end:
          shift.originalData.scheduled_end || shift.originalData.endTime,
        shift_type: shift.shiftType,
        payer: shift.payer,
        shift_notes: shift.originalData?.shift_notes ?? shift.notes,
        shift_status:
          shift.originalData.shift_status ||
          shift.originalData.status ||
          shift.status,
        clientId: clientId, // Set clientId directly like createShift does
        clients: shift.originalData.clients || [],
        caregivers: shift.originalData.caregivers || [],
        isPotential: shift.originalData.isPotential || false,
      };

      // Handle clocked-out/error shifts - route to appropriate edit modal
      const status = (shift.status || shift.originalData?.shift_status)?.toLowerCase();
      const isClockedOut = status === "clocked-out" || status === "clocked out" || status === "error";
      if (isClockedOut) {
        const mappedShift = {
          ...shiftToEdit,
          startTime: shiftToEdit.scheduled_start,
          endTime: shiftToEdit.scheduled_end,
          originalData: {
            ...shift.originalData,
            scheduled_start: shiftToEdit.scheduled_start,
            scheduled_end: shiftToEdit.scheduled_end,
          },
        };

        // Check if the current user is the assigned caregiver
        const caregivers = shiftToEdit.caregivers || [];
        const isAssignedCaregiver = caregivers.some(
          (c) => Number(c.id) === Number(this.userId)
        );

        // Admins/Leads who are NOT assigned use EditClockOutTimeModal
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          this.handleEditClockOutTime(shift);
          return;
        }

        // Everyone else (caregivers, ES, assigned admins) edits notes
        this.shiftToEditNotes = mappedShift;
        this.showEditShiftNotesModal = true;
        return;
      }

      this.shiftBeingEdited = shiftToEdit;
      this.showShiftModal = true;
    },

    createShift() {
      // Pre-populate the shift with the current client
      this.shiftBeingEdited = {
        clientId: this.clientId ? Number(this.clientId) : null,
        payer: this.client?.payer_type || "VR",
      };
      this.showShiftModal = true;
    },

    closeShiftModal() {
      this.showShiftModal = false;
      this.shiftBeingEdited = null;
    },

    async saveShift({ shift, shifts, isEditing, isGroupShift, isDSPDGroupShift, exceptionShiftIds }) {
      try {
        // Convert local times to UTC datetimes for the new schema
        const convertToUTCDateTime = (timeStr, dateStr) => {
          if (!timeStr || !dateStr) return null;
          // If timeStr is already a full datetime, use it directly
          if (timeStr.includes("T")) {
            return timeStr;
          }
          // Otherwise, combine date and time and convert to UTC
          const localDateTime = new Date(`${dateStr}T${timeStr}`);
          return localDateTime.toISOString();
        };

        // Helper to convert times for a single shift object
        const convertShiftTimes = (s) => {
          const dateOnly = s.recurring
            ? s.recurring_start_date
            : s.date
              ? s.date.split("T")[0]
              : null;
          return {
            ...s,
            scheduled_start: convertToUTCDateTime(s.scheduled_start, dateOnly),
            scheduled_end: convertToUTCDateTime(s.scheduled_end, dateOnly),
          };
        };

        // Handle DSPD group shifts (single shift with multiple clients and task assignments)
        if (isDSPDGroupShift && shift && !isEditing) {
          const processedShift = convertShiftTimes(shift);
          await shiftsService.create(processedShift);

          const clientCount = shift.clientIds?.length || 1;
          this.$notifySuccess(`DSPD group shift created successfully for ${clientCount} client(s)!`);
          this.closeShiftModal();
          await this.loadClientShifts();
          return;
        }

        // Handle group shifts (multiple shifts to create)
        if (isGroupShift && shifts && Array.isArray(shifts)) {
          const createdShifts = [];
          for (const shiftData of shifts) {
            const processedShift = convertShiftTimes(shiftData);
            const savedShift = await shiftsService.create(processedShift);
            createdShifts.push(savedShift);
          }

          this.$notifySuccess(`${createdShifts.length} group shifts created successfully!`);
          this.closeShiftModal();
          await this.loadClientShifts();
          return;
        }

        // Preserve existing status if it's "missed", otherwise default to "Scheduled"
        let shiftStatus = "Scheduled";
        if (isEditing && this.shiftBeingEdited) {
          const currentStatus =
            this.shiftBeingEdited.shift_status || this.shiftBeingEdited.status;
          if (currentStatus && currentStatus.toLowerCase() === "missed") {
            shiftStatus = "Missed";
          } else if (currentStatus && currentStatus !== "Scheduled") {
            // Preserve other statuses like "Approved", "Clocked In", "Clocked Out", etc.
            shiftStatus = currentStatus;
          }
        }

        // For recurring shifts, use the start date; for single shifts, use the date
        const dateOnly = shift.recurring
          ? shift.recurring_start_date
          : shift.date
            ? shift.date.split("T")[0]
            : null;

        // Start with all shift data to preserve fields like editEntireSeriesFromDate
        const shiftData = {
          ...shift,
          // Override specific fields
          scheduled_start: convertToUTCDateTime(
            shift.scheduled_start,
            dateOnly,
          ),
          scheduled_end: convertToUTCDateTime(shift.scheduled_end, dateOnly),
          shift_status: shiftStatus,
          clientIds:
            shift.clientIds || (shift.clientId ? [shift.clientId] : []),
          caregiverIds:
            shift.caregiverIds ||
            (shift.caregiverId ? [shift.caregiverId] : []),
          serviceCodeIds:
            shift.serviceCodeIds || shift.selectedServiceCodes || [],
        };

        // Include client address ID if provided
        if (shift.clientAddressId) {
          shiftData.clientAddressId = shift.clientAddressId;
        }

        // Include clientGeocode if provided
        if (shift.clientGeocode) {
          shiftData.clientGeocode = shift.clientGeocode;
        }

        // Include clientGeocodeAddressId if provided (which address the geocode is for)
        if (shift.clientGeocodeAddressId) {
          shiftData.clientGeocodeAddressId = shift.clientGeocodeAddressId;
        }

        // Add recurring shift data if applicable
        if (shift.recurring) {
          shiftData.recurring = true;
          shiftData.recurrencePattern = shift.recurrencePattern;
          shiftData.selectedWeekdays = shift.selectedWeekdays || [];
          shiftData.customDates = shift.customDates || [];
          shiftData.recurring_start_date = shift.recurring_start_date;
          shiftData.recurring_end_date = shift.recurring_end_date || null;
        }

        // Include VR alternate location data if present
        if (shift.vrAlternateLocation) {
          shiftData.vrAlternateLocation = shift.vrAlternateLocation;
          shiftData.useAlternateLocation = shift.useAlternateLocation;
        }

        let savedShift;
        if (isEditing && !shift.isPotential) {
          savedShift = await shiftsService.update(shift.id, shiftData);
          this.$notifySuccess("Shift updated successfully");
        } else if (isEditing && shift.isPotential) {
          savedShift = await shiftsService.create(shiftData);
          // Delete the potential shift after creating the real one
          await shiftsService.deletePotential(shift.id);
          this.$notifySuccess("Potential shift converted to scheduled shift!");
        } else {
          savedShift = await shiftsService.create(shiftData);
          this.$notifySuccess("Shift created successfully");
        }

        // If the scheduler chose to keep individually-changed occurrences, move
        // them onto the new series the edit created (a series edit splits the
        // old series and starts a new one; exceptions stay tied to the old one
        // until re-parented).
        if (exceptionShiftIds && exceptionShiftIds.length > 0 && savedShift) {
          try {
            const newRecurringShiftId =
              savedShift.newRecurringShiftId || savedShift.id;
            if (newRecurringShiftId) {
              await shiftsService.handleExceptionShifts({
                action: "keep",
                exceptionShiftIds,
                newRecurringShiftId,
              });
              this.$notifySuccess("Changed occurrences kept on the new series");
            }
          } catch (exceptionError) {
            this.$notifyError(
              "Shift saved, but the changed occurrences could not be moved to the new series",
            );
          }
        }

        this.closeShiftModal();
        await this.loadClientShifts();
      } catch (error) {
        // Client overlap is overridable: offer "Schedule anyway" and retry
        // with the acknowledgement flag (managers get notified server-side).
        // Caregiver availability is overridable too, with a confirm that
        // states the payroll/report consequences before the retry.
        if (await this.confirmCaregiverOverride(error)) {
          if (shift) shift.acknowledgeCaregiverConflict = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeCaregiverConflict = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
          });
        }
        if (await this.confirmClientOverlap(error)) {
          if (shift) shift.acknowledgeClientOverlap = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeClientOverlap = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
          });
        }
        // Rich, itemized overlap message when conflict detail is present.
        this.handleShiftSaveError(error);
      }
    },

    handleOpenGroupTasks(shift) {
      this.groupTasksShift = shift.originalData || shift;
      this.showGroupTasksModal = true;
    },

    async handleShiftClockIn(shift) {
      if (!(await this.$clockNudge("in"))) return;
      this.currentShift = shift.originalData || shift;
      this.showClockInModal = true;
    },

    async handleShiftClockOut(shift) {
      if (!(await this.$clockNudge("out"))) return;
      this.currentShift = shift.originalData || shift;
      this.showClockOutModal = true;
    },

    handleAdminClockOut(shift) {
      this.shiftToAdminClockOut = shift;
      this.showAdminClockOutModal = true;
    },

    async handleClockInSuccess() {
      await this.loadClientShifts();
      this.$root.$emit("shift-saved");
    },

    async handleClockOutSuccess() {
      await this.loadClientShifts();
      this.$root.$emit("shift-saved");
    },

    handleShiftEdit(shift) {
      this.editShift(shift);
    },

    handleEditShiftNotes(shift) {
      const originalData = shift.originalData || shift;
      const mappedShift = {
        ...originalData,
        id: shift.id,
        date: shift.date,
        startTime: originalData.scheduled_start,
        endTime: originalData.scheduled_end,
        originalData: {
          ...originalData,
          scheduled_start: originalData.scheduled_start,
          scheduled_end: originalData.scheduled_end,
        },
      };
      this.shiftToEditNotes = mappedShift;
      this.showEditShiftNotesModal = true;
    },

    handleEditClockInTime(shift) {
      // Use originalData if available, which contains the full shift details
      this.shiftToEditClockInTime = shift.originalData || shift;
      this.showEditClockInTimeModal = true;
    },

    handleEditClockOutTime(shift) {
      // Create a properly structured shift object that includes both original data and processed times
      const originalData = shift.originalData || shift;
      const mappedShift = {
        ...originalData,
        // Include actual times from Calendar processing if available
        actualStartTime: shift.actualStartTime,
        actualEndTime: shift.actualEndTime,
        // Ensure clock times are available in multiple formats for compatibility
        clockIn: originalData.clock_in || originalData.clockIn,
        clock_in: originalData.clock_in || originalData.clockIn,
        clockOut: originalData.clock_out || originalData.clockOut,
        clock_out: originalData.clock_out || originalData.clockOut,
        // Preserve scheduled times
        startTime: shift.scheduledStartTime || originalData.scheduled_start,
        endTime: shift.scheduledEndTime || originalData.scheduled_end,
        scheduled_start: originalData.scheduled_start,
        scheduled_end: originalData.scheduled_end,
        // Store original data for reference
        originalData: originalData,
      };

      this.shiftToEditClockOutTime = mappedShift;
      this.showEditClockOutTimeModal = true;
    },

    handleApproveShift(shift) {
      // Map the shift data for the approve shift modal
      this.shiftToApprove = {
        ...shift,
        startTime: shift.startTime || shift.originalData?.scheduled_start,
        endTime: shift.endTime || shift.originalData?.scheduled_end,
        // Preserve service code data from originalData
        service_codes: shift.originalData?.service_codes || [],
        // Preserve all JC-related activity data
        jc_primary_activities: shift.originalData?.jc_primary_activities,
        jc_secondary_activities: shift.originalData?.jc_secondary_activities,
        jc_shift_materials: shift.originalData?.jc_shift_materials,
        // Preserve shift notes (all notes are now in shift_notes)
        shift_notes: shift.originalData?.shift_notes,
        // Preserve group shift data
        caregiverClockTimes: shift.originalData?.caregiverClockTimes || [],
        clientDetails: shift.originalData?.clientDetails || [],
        originalData: {
          ...shift.originalData,
          clockIn: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clock_in: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clockOut:
            shift.originalData?.clockOut || shift.originalData?.clock_out,
          clock_out:
            shift.originalData?.clockOut || shift.originalData?.clock_out,
        },
      };
      this.showApproveShiftModal = true;
    },

    async handleUndoApproval(shift) {
      try {
        // Confirm the action
        const confirmed = await this.$confirm(
          'Are you sure you want to undo this shift approval? This will restore hours back to the authorization and revert the shift to "Clocked Out" status.',
          "Confirm Undo Approval",
          {
            confirmButtonText: "Yes, Undo Approval",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );

        if (!confirmed) {
          return;
        }

        // Call the undo approval API
        await shiftsService.undoShiftApproval({
          shiftId: shift.originalData?.id || shift.id,
        });

        this.$notifySuccess(
          "Shift approval undone successfully! Authorization hours have been restored.",
        );

        // Close the modal and reload shifts
        this.showShiftActionModal = false;
        await this.loadClientShifts();
      } catch (error) {
        if (error !== "cancel") {
          this.$notifyError(
            error.response?.data?.error || "Failed to undo shift approval",
          );
        }
      }
    },

    handleApproveMissedShift(shift) {
      // Map the shift data for the approve missed shift modal
      const mappedShift = {
        ...shift,
        scheduledStartTime:
          shift.originalData?.scheduled_start || shift.startTime,
        scheduledEndTime: shift.originalData?.scheduled_end || shift.endTime,
        startTime: shift.originalData?.scheduled_start || shift.startTime,
        endTime: shift.originalData?.scheduled_end || shift.endTime,
        originalData: {
          ...shift.originalData,
          scheduled_start:
            shift.originalData?.scheduled_start || shift.startTime,
          scheduled_end: shift.originalData?.scheduled_end || shift.endTime,
        },
      };

      this.shiftToApproveMissed = mappedShift;
      this.showApproveMissedShiftModal = true;
    },

    async handleCancelShift(shift) {
      const shiftId = shift.id || shift.originalData?.id;
      if (!shiftId) return;
      let reason;
      try {
        const result = await this.$prompt(
          "Please provide a reason for canceling this shift:",
          "Cancel Shift",
          {
            confirmButtonText: "Cancel Shift",
            cancelButtonText: "Go Back",
            inputType: "textarea",
            inputPlaceholder: "e.g., client canceled, caregiver sick, weather...",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "A cancellation reason is required";
              return true;
            },
          },
        );
        reason = result.value;
      } catch {
        return;
      }
      try {
        await shiftsService.update(shiftId, {
          shift_status: "Canceled",
          adminChangeReason: reason.trim(),
        });
        this.$notifySuccess("Shift canceled successfully");
        await this.loadClientShifts();
      } catch (error) {
        this.$notifyError(error.response?.data?.message || "Error canceling shift");
      }
    },

    handleEditModalDelete(shift) {
      this.closeShiftModal();
      this.handleShiftDeletion(shift);
    },
    // Delete a shift, mirroring the Calendar page: recurring shifts get the
    // 3-option dialog, others a simple confirm.
    async handleShiftDeletion(shift) {
      const original = shift.originalData || shift;
      const isRecurring =
        original.recurring ||
        original.parent_recurring_shift_id ||
        shift.recurring ||
        shift.parent_recurring_shift_id;

      try {
        if (isRecurring) {
          this.deleteRecurringDialog = { visible: true, shift };
        } else {
          const confirmDelete = await this.$confirmAction(
            null,
            "Are you sure you want to delete this shift?",
          );
          if (!confirmDelete) return;
          await shiftsService.delete(shift.id);
          this.$notifySuccess("Shift deleted successfully!");
          this.showShiftActionModal = false;
          await this.loadClientShifts();
        }
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisShiftOnly() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteWithDate(shift.id, shiftDate);
        this.$notifySuccess("Shift occurrence deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadClientShifts();
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisAndFutureShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteFromDate(parentId, shiftDate);
        this.$notifySuccess("This and all future occurrences deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadClientShifts();
      } catch (error) {
        this.$notifyError("Error deleting shifts: " + error);
      }
    },
    async deleteAllShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        await shiftsService.deleteEntireSeries(parentId);
        this.$notifySuccess("Entire recurring series deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadClientShifts();
      } catch (error) {
        this.$notifyError("Error deleting series: " + error);
      }
    },

    convertUTCToLocal(date, startTime, endTime) {
      let startDateTime, endDateTime;

      if (startTime && startTime.includes("T")) {
        startDateTime = new Date(startTime);
        endDateTime = new Date(endTime);
      } else {
        startDateTime = new Date(`${date}T${startTime}Z`);
        endDateTime = new Date(`${date}T${endTime}Z`);
      }

      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        return { localStartTime: startTime, localEndTime: endTime };
      }

      const localStartHours = startDateTime.getHours();
      const localStartMinutes = startDateTime.getMinutes();
      const localEndHours = endDateTime.getHours();
      const localEndMinutes = endDateTime.getMinutes();

      const localStartTime = `${String(localStartHours).padStart(2, "0")}:${String(localStartMinutes).padStart(2, "0")}`;
      const localEndTime = `${String(localEndHours).padStart(2, "0")}:${String(localEndMinutes).padStart(2, "0")}`;

      return { localStartTime, localEndTime };
    },

    // Notes methods
    async loadClientNotes() {
      if (!this.clientId) return;

      this.loadingAllNotes = true;
      try {
        const response = await clientsService.getNotes(this.clientId);

        // Format notes for display
        const formattedNotes = response.notes.map((note) => {
          if (note.type === "general") {
            return {
              id: note.id,
              type: "general",
              date: note.date,
              notes: note.note,
              createdBy: note.createdBy,
              createdAt: note.createdAt,
            };
          }
          // For shift and schedule notes, keep original format
          return {
            id: note.id,
            type: note.type,
            date: note.date,
            notes: note.note,
            createdAt: note.createdAt,
            serviceCode: note.serviceCode,
            caregiver: note.caregiver,
            actual_start: note.actual_start,
            actual_end: note.actual_end,
            questionAnswers: note.questionAnswers || [],
            goalAnswers: note.goalAnswers || [],
            activities: note.activities,
            mileage: note.mileage,
            shiftNotes: note.shiftNotes,
            jd_activity: note.jd_activity,
            jd_outcome: note.jd_outcome,
            jd_next_steps: note.jd_next_steps,
          };
        });

        this.allNotes = formattedNotes;
        this.filteredAllNotes = formattedNotes;
      } catch (error) {
        this.$notifyError("Error loading client notes: " + error);
      } finally {
        this.loadingAllNotes = false;
      }
    },

    async createGeneralNote(noteText) {
      if (!this.clientId || !noteText || !noteText.trim()) return;

      try {
        const newNote = await clientsService.createNote(
          this.clientId,
          noteText,
        );

        // Format the new note for display
        const formattedNote = {
          id: newNote.id,
          type: "general",
          date: newNote.date,
          notes: newNote.note,
          createdBy: newNote.createdBy,
          createdAt: newNote.createdAt,
        };

        // Add to the beginning of the list
        this.allNotes.unshift(formattedNote);
        this.filteredAllNotes = [...this.allNotes];

        this.$notifySuccess("Note created successfully");
      } catch (error) {
        this.$notifyError("Error creating note: " + error);
      }
    },

    openAddNoteModal() {
      this.showAddNoteModal = true;
      this.newNoteText = "";
    },

    closeAddNoteModal() {
      this.showAddNoteModal = false;
      this.newNoteText = "";
    },

    async handleCreateNote() {
      await this.createGeneralNote(this.newNoteText);
      this.closeAddNoteModal();
    },

    filterAllNotes() {
      if (!this.notesSearch.trim()) {
        this.filteredAllNotes = this.allNotes;
        return;
      }

      const searchTerm = this.notesSearch.toLowerCase();
      this.filteredAllNotes = this.allNotes.filter((note) => {
        return (
          note.notes.toLowerCase().includes(searchTerm) ||
          (note.caregiver &&
            note.caregiver.toLowerCase().includes(searchTerm)) ||
          (note.createdBy &&
            note.createdBy.toLowerCase().includes(searchTerm)) ||
          this.formatDate(note.date).toLowerCase().includes(searchTerm)
        );
      });
    },

    clearNotesSearch() {
      this.notesSearch = "";
      this.filteredAllNotes = this.allNotes;
    },

    // Goals methods
    async loadClientGoals() {
      if (!this.clientId) return;

      this.loadingGoals = true;
      try {
        const response = await clientsService.getGoals(this.clientId);
        this.goals = response.goals || [];
      } catch (error) {
        this.$notifyError("Error loading client goals: " + error);
      } finally {
        this.loadingGoals = false;
      }
    },

    openAddGoalModal() {
      this.editingGoal = null;
      this.goalFormData.question_text = "";
      this.goalFormData.question_type = "free_response";
      this.$modal.open("add_edit_goal");
    },

    editGoal(goal) {
      this.editingGoal = goal;
      this.goalFormData.question_text = goal.question_text;
      this.goalFormData.question_type = goal.question_type;
      this.$modal.open("add_edit_goal");
    },

    async handleSaveGoal() {
      try {
        if (this.editingGoal) {
          // Update existing goal question
          const updatedGoal = await clientsService.updateGoal(
            this.clientId,
            this.editingGoal.id,
            this.goalFormData.question_text,
            this.goalFormData.question_type,
          );

          // Update the goal in the list
          const index = this.goals.findIndex(
            (g) => g.id === this.editingGoal.id,
          );
          if (index !== -1) {
            this.$set(this.goals, index, updatedGoal);
          }

          this.$notifySuccess("Goal question updated successfully");
        } else {
          // Add new goal question
          const newGoal = await clientsService.addGoal(
            this.clientId,
            this.goalFormData.question_text,
            this.goalFormData.question_type,
          );

          this.goals.push(newGoal);
          this.$notifySuccess("Goal question added successfully");
        }

        this.closeGoalModal();
      } catch (error) {
        this.$notifyError("Error saving goal question: " + error);
      }
    },

    confirmDeleteGoal(goal) {
      this.$confirm(
        `Are you sure you want to delete this goal question: "${goal.question_text}"?`,
        "Confirm Delete",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        },
      )
        .then(() => {
          this.deleteGoal(goal);
        })
        .catch(() => {
          // User cancelled
        });
    },

    async deleteGoal(goal) {
      try {
        await clientsService.deleteGoal(this.clientId, goal.id);

        // Remove from list
        const index = this.goals.findIndex((g) => g.id === goal.id);
        if (index !== -1) {
          this.goals.splice(index, 1);
        }

        this.$notifySuccess("Goal question deleted successfully");
      } catch (error) {
        this.$notifyError("Error deleting goal question: " + error);
      }
    },

    closeGoalModal() {
      this.$modal.close("add_edit_goal");
      this.editingGoal = null;
      this.goalFormData.question_text = "";
      this.goalFormData.question_type = "free_response";
    },

    // Support Strategies tab methods
    async loadSupportStrategies() {
      if (!this.clientId) return;
      this.loadingSupportStrategies = true;
      try {
        this.supportStrategies = await supportStrategiesService.getSupportStrategiesForClient(this.clientId);
      } catch (error) {
        this.$notifyError("Error loading support strategies: " + error);
      } finally {
        this.loadingSupportStrategies = false;
      }
    },
    openNewSupportStrategy() {
      this.editingSupportStrategyIdTab = null;
      this.showSupportStrategyModalTab = true;
    },
    editSupportStrategy(strategy) {
      this.editingSupportStrategyIdTab = strategy.id;
      this.showSupportStrategyModalTab = true;
    },
    confirmDeleteSupportStrategy(strategy) {
      this.$confirm(
        "Are you sure you want to delete this support strategy?",
        "Confirm Delete",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        },
      )
        .then(() => this.deleteSupportStrategy(strategy))
        .catch(() => {});
    },
    async deleteSupportStrategy(strategy) {
      try {
        await supportStrategiesService.deleteSupportStrategy(strategy.id);
        this.supportStrategies = this.supportStrategies.filter((s) => s.id !== strategy.id);
        this.$notifySuccess("Support strategy deleted");
      } catch (error) {
        this.$notifyError("Error deleting support strategy: " + error);
      }
    },
    isStrategyOverdue(strategy) {
      if (!strategy.due_date || strategy.status === "final") return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(strategy.due_date + "T00:00:00") < today;
    },
    getStrategyDueDateClass(strategy) {
      if (!strategy.due_date) return "";
      if (strategy.status === "final") return "text-success";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(strategy.due_date + "T00:00:00");
      const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) return "text-danger";
      if (daysUntil <= 7) return "text-warning";
      return "text-muted";
    },
    onSupportStrategySavedFromTab() {
      this.loadSupportStrategies();
    },
    onSupportStrategyGeneratedFromTab(document) {
      this.loadSupportStrategies();
      this.documents.SupportStrategy.unshift(document);
      if (this.$refs.supportStrategyTracker) {
        this.$refs.supportStrategyTracker.refresh();
      }
      this.loadClientGoals();
    },
    openStrategyEmailModal(strategy) {
      const sc = this.client?.support_coordinator;
      const clientName = `${this.client?.first_name || ""} ${this.client?.last_name || ""}`.trim();
      const planYear = strategy.pcsp_start_date && strategy.pcsp_end_date
        ? ` (${this.formatDate(strategy.pcsp_start_date)} - ${this.formatDate(strategy.pcsp_end_date)})`
        : "";

      this.emailingStrategyId = strategy.id;
      this.strategyEmailForm.to = sc ? sc.email || "" : "";
      this.strategyEmailForm.subject = `Support Strategy for ${clientName}${planYear}`;
      this.strategyEmailForm.body =
        `Hello${sc ? ` ${sc.first_name}` : ""},\n\n` +
        `Please find attached the finalized support strategy for ${clientName}${planYear}.\n\n` +
        `Please let us know if you have any questions or need additional information.`;
      this.showStrategyEmailModal = true;
    },
    async sendStrategyEmail() {
      if (!this.strategyEmailForm.to) {
        this.$notifyError("Recipient email is required");
        return;
      }
      this.sendingStrategyEmail = true;
      try {
        await supportStrategiesService.emailSupportCoordinator(this.emailingStrategyId, {
          to: this.strategyEmailForm.to,
          subject: this.strategyEmailForm.subject,
          body: this.strategyEmailForm.body,
        });
        this.$notifySuccess("Email sent successfully");
        this.showStrategyEmailModal = false;
        // Refresh both the Support Strategies tab and Compliance tab lists
        this.loadSupportStrategies();
        this.loadComplianceStrategies();
      } catch (err) {
        this.$notifyError("Failed to send email: " + (err.message || "Unknown error"));
      } finally {
        this.sendingStrategyEmail = false;
      }
    },

    // Medication methods
    async downloadMar() {
      this.marDownloading = true;
      try {
        const blob = await clientsService.downloadMarPdf(this.clientId, this.marMonth, this.marYear);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `MAR_${this.client?.last_name}_${this.client?.first_name}_${this.marMonth}_${this.marYear}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.showMarModal = false;
        this.$notifySuccess("MAR report downloaded");
      } catch (err) {
        this.$notifyError("Failed to download MAR report");
      } finally {
        this.marDownloading = false;
      }
    },

    async loadClientMedications(pageNumber = 1) {
      if (!this.clientId) return;
      this.loadingMedications = true;
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.medicationPagination.perPage);
        if (this.medicationSortProp) {
          params.append("sortField", this.medicationSortProp);
          params.append("sortOrder", this.medicationSortOrder);
        }
        const response = await clientsService.getMedicationsPaginated(this.clientId, params);
        this.medications = response.rows;
        this.medicationPagination = response.pagination;
      } catch (error) {
        this.$notifyError("Error loading client medications: " + error);
      } finally {
        this.loadingMedications = false;
      }
    },

    handleMedicationSortChange({ prop, order }) {
      this.medicationSortProp = prop;
      this.medicationSortOrder = order;
      this.loadClientMedications(1);
    },

    handleMedicationPageSizeChange(newSize) {
      this.medicationPagination.perPage = newSize;
      this.medicationPagination.currentPage = 1;
      this.loadClientMedications(1);
    },

    openAddMedicationModal() {
      this.editingMedication = null;
      this.medicationFormData = {
        name: "",
        dosage: "",
        route: "",
        instructions: "",
        amount_remaining: null,
        special_instructions: "",
        prescribed_date: null,
        daySchedule: {},
      };
      this.showMedicationModal = true;
    },

    editMedication(med) {
      this.editingMedication = med;
      // Convert backend schedules array into daySchedule object
      const daySchedule = {};
      if (med.schedules && med.schedules.length > 0) {
        for (const s of med.schedules) {
          const day = s.day_of_week;
          if (!daySchedule[day]) daySchedule[day] = [];
          daySchedule[day].push(s.time ? s.time.substring(0, 5) : "");
        }
      }
      this.medicationFormData = {
        name: med.name || "",
        dosage: med.dosage || "",
        route: med.route || "",
        instructions: med.instructions || "",
        amount_remaining: med.amount_remaining != null ? parseFloat(med.amount_remaining) : null,
        special_instructions: med.special_instructions || "",
        prescribed_date: med.prescribed_date || null,
        daySchedule,
      };
      this.showMedicationModal = true;
    },

    toggleMedDay(dayValue) {
      if (this.medicationFormData.daySchedule[dayValue]) {
        // Remove the day
        this.$delete(this.medicationFormData.daySchedule, dayValue);
      } else {
        // Add the day with one empty time slot
        this.$set(this.medicationFormData.daySchedule, dayValue, [""]);
      }
    },

    addMedDayTime(dayValue) {
      if (!this.medicationFormData.daySchedule[dayValue]) {
        this.$set(this.medicationFormData.daySchedule, dayValue, []);
      }
      this.medicationFormData.daySchedule[dayValue].push("");
    },

    removeMedDayTime(dayValue, idx) {
      this.medicationFormData.daySchedule[dayValue].splice(idx, 1);
      if (this.medicationFormData.daySchedule[dayValue].length === 0) {
        this.$delete(this.medicationFormData.daySchedule, dayValue);
      }
    },

    async handleSaveMedication() {
      if (!this.medicationFormData.name || !this.medicationFormData.name.trim()) {
        this.$notifyError("Medication name is required");
        return;
      }

      // Convert daySchedule into backend schedules format
      const payload = { ...this.medicationFormData };
      const schedules = [];
      for (const [dayStr, times] of Object.entries(payload.daySchedule || {})) {
        const day = parseInt(dayStr, 10);
        for (const time of times) {
          if (time && time.trim()) {
            schedules.push({ day_of_week: day, time: time.trim() });
          }
        }
      }
      payload.schedules = schedules.length > 0 ? schedules : null;
      delete payload.daySchedule;

      try {
        if (this.editingMedication) {
          await clientsService.updateMedication(
            this.clientId,
            this.editingMedication.id,
            payload,
          );
          this.$notifySuccess("Medication updated successfully");
        } else {
          await clientsService.addMedication(
            this.clientId,
            payload,
          );
          this.$notifySuccess("Medication added successfully");
        }
        this.closeMedicationModal();
        await this.loadClientMedications(this.medicationPagination.currentPage);
      } catch (error) {
        this.$notifyError("Error saving medication: " + error);
      }
    },

    confirmDeleteMedication(med) {
      this.$confirm(
        `Are you sure you want to delete "${med.name}"?`,
        "Confirm Delete",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        },
      )
        .then(() => {
          this.deleteMedication(med);
        })
        .catch(() => {
          // User cancelled
        });
    },

    async deleteMedication(med) {
      try {
        await clientsService.deleteMedication(this.clientId, med.id);
        this.$notifySuccess("Medication deleted successfully");
        await this.loadClientMedications(this.medicationPagination.currentPage);
      } catch (error) {
        this.$notifyError("Error deleting medication: " + error);
      }
    },

    closeMedicationModal() {
      this.showMedicationModal = false;
      this.editingMedication = null;
      this.medicationFormData = {
        name: "",
        dosage: "",
        frequency: "",
        route: "",
        instructions: "",
        amount_remaining: null,
        special_instructions: "",
        prescribed_date: null,
        schedule_days: [],
        administration_times: [],
      };
    },

    async openMedicationHistory(med) {
      this.selectedMedicationForHistory = med;
      this.showMedicationHistoryModal = true;
      this.medHistoryPagination.currentPage = 1;
      this.medHistorySortProp = "";
      this.medHistorySortOrder = "";
      await this.fetchMedicationHistory();
    },

    async fetchMedicationHistory(pageNumber = 1) {
      if (!this.selectedMedicationForHistory) return;
      this.loadingMedicationHistory = true;
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.medHistoryPagination.perPage);
        if (this.medHistorySortProp) {
          params.append("sortField", this.medHistorySortProp);
          params.append("sortOrder", this.medHistorySortOrder);
        }
        const response = await clientsService.getMedicationHistory(
          this.clientId,
          this.selectedMedicationForHistory.id,
          params,
        );
        this.medicationHistory = response.rows || [];
        this.medHistoryPagination = response.pagination;
      } catch (error) {
        this.$notifyError("Error loading medication history: " + error);
      } finally {
        this.loadingMedicationHistory = false;
      }
    },

    handleMedHistorySortChange({ prop, order }) {
      this.medHistorySortProp = prop;
      this.medHistorySortOrder = order;
      this.fetchMedicationHistory(1);
    },

    handleMedHistoryPageSizeChange(newSize) {
      this.medHistoryPagination.perPage = newSize;
      this.medHistoryPagination.currentPage = 1;
      this.fetchMedicationHistory(1);
    },

    closeMedicationHistoryModal() {
      this.showMedicationHistoryModal = false;
      this.medicationHistory = [];
      this.selectedMedicationForHistory = null;
      this.medHistoryPagination = { total: 0, perPage: 20, currentPage: 1, totalPages: 0 };
    },

    formatDateTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatDate(dateString) {
      if (!dateString) return "No Date";
      // Extract YYYY-MM-DD from any date format (string, ISO datetime, or Date object)
      const str = typeof dateString === "object" ? dateString.toISOString() : String(dateString);
      const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return `Invalid Date: ${dateString}`;
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
      // Use Date only for weekday calculation, constructed as local time
      const dow = new Date(year, month, day).getDay();
      return `${days[dow]}, ${months[month]} ${day}, ${year}`;
    },

    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return "";

      const formatTime = (timeString) => {
        // Handle ISO datetime strings (e.g. "2026-02-23T16:45:00.000Z")
        if (typeof timeString === "string" && timeString.includes("T")) {
          const d = new Date(timeString);
          if (!isNaN(d)) {
            let hours = d.getHours();
            const minutes = d.getMinutes().toString().padStart(2, "0");
            const ampm = hours >= 12 ? "pm" : "am";
            hours = hours % 12 || 12;
            return `${hours}:${minutes}${ampm}`;
          }
        }
        // Fallback for plain "HH:MM" strings
        const timeParts = String(timeString).split(":");
        let hours = parseInt(timeParts[0]);
        const minutes = timeParts[1] || "00";
        const ampm = hours >= 12 ? "pm" : "am";
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${hours}:${minutes}${ampm}`;
      };

      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    },

    formatMileage(val) {
      if (val == null) return [];
      if (Array.isArray(val)) return val.map((s) => String(s).trim()).filter(Boolean);
      if (typeof val === "string") {
        const trimmed = val.trim();
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.map((s) => String(s).trim()).filter(Boolean);
        } catch (e) { /* not JSON */ }
        // Fallback: strip brackets/quotes and split on commas
        return trimmed
          .replace(/^\[\s*/, "")
          .replace(/\s*\]$/, "")
          .split(/"\s*,\s*"/)
          .map((s) => s.replace(/^"|"$/g, "").trim())
          .filter(Boolean);
      }
      return [String(val)];
    },

    formatActivityItem(item) {
      if (item == null) return "";
      let str = typeof item === "string" ? item : JSON.stringify(item);
      // Replace JSON-stringified arrays inside the string with a comma-separated list
      str = str.replace(/\[([^\]]*)\]/g, (_match, inner) => {
        try {
          const parsed = JSON.parse(`[${inner}]`);
          if (Array.isArray(parsed)) return parsed.join(", ");
        } catch (e) {
          /* ignore */
        }
        return inner.replace(/"/g, "");
      });
      return str;
    },

    formatMedTime(timeStr) {
      if (!timeStr) return '';
      const [h, m] = timeStr.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m} ${ampm}`;
    },

    // Document methods
    async loadClientDocuments() {
      if (!this.clientId) return;

      this.loadingDocuments = true;
      try {
        const response = await documentsService.getClientDocuments(
          this.clientId,
        );
        this.documents = response;
      } catch (error) {
        this.$notifyError("Error loading client documents: " + error);
      } finally {
        this.loadingDocuments = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.newDocument.file = file;
        if (!this.newDocument.displayName) {
          this.newDocument.displayName = file.name;
        }
      }
    },

    ensureUrlProtocol(url) {
      // Check if URL already has a protocol
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      // Add https:// if missing
      return `https://${url}`;
    },

    async viewDocument(doc) {
      // If it's a link, open in new tab as the user requested
      if (doc.is_link) {
        const urlWithProtocol = this.ensureUrlProtocol(doc.link);
        window.open(urlWithProtocol, "_blank");
      } else {
        // If it's an uploaded file, display in the app using the same flow as InvoiceDetails
        // Extract just the filename from the link
        const filename = doc.link.split("/").pop();
        await this.viewPdf(filename, "client-document", doc.display_name);
      }
    },

    onViewCompliancePdf({ filename, displayName }) {
      this.viewPdf(filename, "client-document", displayName);
    },

    onComplianceConfigSaved() {
      if (this.$refs.complianceStatusCard) {
        this.$refs.complianceStatusCard.fetch();
      }
    },
    clientComplianceDisplayLabel(type) {
      const map = { 'Consent': 'Consent & Agreement', 'Rules': 'Rules & Expectations' };
      return map[type] || type;
    },

    async loadClientComplianceDocs() {
      if (!this.clientId) return;
      this.loadingClientCompliance = true;
      try {
        const data = await clientComplianceService.getByClient(this.clientId);
        this.clientComplianceStatus = data.complianceStatus || [];
        this.clientComplianceDocs = data.documents || [];
      } catch (e) {
        this.clientComplianceStatus = [];
        this.clientComplianceDocs = [];
      } finally {
        this.loadingClientCompliance = false;
      }
    },

    async uploadClientCompDoc() {
      if (!this.clientDocUpload.type) {
        this.$notifyError('Please select a document type');
        return;
      }
      const fileInput = this.$refs.clientCompFileInput;
      if (!fileInput || !fileInput.files || !fileInput.files[0]) {
        this.$notifyError('Please select a file');
        return;
      }
      this.uploadingClientDoc = true;
      try {
        const formData = new FormData();
        formData.append('file', fileInput.files[0]);
        formData.append('document_type', this.clientDocUpload.type);
        if (this.clientDocUpload.expirationDate) {
          formData.append('expiration_date', this.clientDocUpload.expirationDate);
        }
        if (this.clientDocUpload.complianceItemTypes.length) {
          formData.append('compliance_item_types', JSON.stringify(this.clientDocUpload.complianceItemTypes));
        }
        await clientComplianceService.upload(this.clientId, formData);
        this.$notifySuccess('Document uploaded');
        this.showClientDocUpload = false;
        this.clientDocUpload = { type: '', expirationDate: '', complianceItemTypes: [] };
        this.clientCompFileName = '';
        await this.loadClientComplianceDocs();
      } catch (e) {
        this.$notifyError('Upload failed: ' + (e.response?.data?.error || e.message));
      } finally {
        this.uploadingClientDoc = false;
      }
    },

    async viewClientCompDoc(docId) {
      try {
        const blob = await clientComplianceService.viewDocument(docId);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (e) {
        this.$notifyError('Could not open document');
      }
    },

    editClientCompDoc(item) {
      this.editingClientDoc = {
        id: item.document.id,
        type: item.type,
        expirationDate: item.document.expiration_date || '',
        status: item.document.status || 'Active',
      };
      this.showEditClientDocModal = true;
    },

    async saveEditClientDoc() {
      this.savingClientDoc = true;
      try {
        await clientComplianceService.update(this.editingClientDoc.id, {
          expiration_date: this.editingClientDoc.expirationDate || null,
          status: this.editingClientDoc.status,
        });
        this.$notifySuccess('Document updated');
        this.showEditClientDocModal = false;
        await this.loadClientComplianceDocs();
      } catch (e) {
        this.$notifyError('Update failed: ' + (e.response?.data?.error || e.message));
      } finally {
        this.savingClientDoc = false;
      }
    },

    async deleteClientCompDoc(docId, typeName) {
      try {
        await this.$confirm(`Delete ${typeName} document? This cannot be undone.`, 'Confirm Delete', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
        });
      } catch (e) {
        return;
      }
      this.loadingClientCompliance = true;
      try {
        await clientComplianceService.destroy(docId);
        this.$notifySuccess('Document deleted');
        await this.loadClientComplianceDocs();
      } catch (e) {
        this.$notifyError('Delete failed: ' + (e.response?.data?.error || e.message));
        this.loadingClientCompliance = false;
      }
    },

    async toggleClientWaiver(item) {
      try {
        if (item.status === 'waived') {
          await clientComplianceService.unwaiveType(this.clientId, item.type);
          this.$notifySuccess('Waiver removed');
        } else {
          await clientComplianceService.waiveType(this.clientId, item.type, 'Not applicable for this client');
          this.$notifySuccess('Item waived');
        }
        await this.loadClientComplianceDocs();
      } catch (e) {
        this.$notifyError('Failed: ' + (e.response?.data?.error || e.message));
      }
    },

    clientDocStatusColor(status) {
      const map = { active: '#67c23a', expired: '#f44336', missing: '#909399', waived: '#FF9800' };
      return map[status] || '#999';
    },

    clientDocStatusIcon(status) {
      const map = { active: 'fa-solid fa-check-circle', expired: 'fa-solid fa-times-circle', missing: 'fa-solid fa-circle', waived: 'fa-solid fa-minus-circle' };
      return map[status] || 'fa-solid fa-circle';
    },

    // Quarterly Reports in Compliance tab
    async loadQuarterlyReports() {
      if (!this.clientId) return;
      this.loadingQuarterlies = true;
      try {
        const data = await quarterlyReportsService.getQuarterlyReportsForClient(this.clientId);
        this.quarterlyReports = (Array.isArray(data) ? data : []).map(qr => ({ ...qr, _downloading: false }));
      } catch {
        this.quarterlyReports = [];
      } finally {
        this.loadingQuarterlies = false;
      }
    },

    // Support Strategies in Compliance tab
    async loadComplianceStrategies() {
      if (!this.clientId) return;
      this.loadingComplianceStrategies = true;
      try {
        const data = await supportStrategiesService.getSupportStrategiesForClient(this.clientId);
        this.complianceStrategies = (Array.isArray(data) ? data : []).map(ss => ({ ...ss, _downloading: false }));
      } catch {
        this.complianceStrategies = [];
      } finally {
        this.loadingComplianceStrategies = false;
      }
    },
    getComplianceStrategyDueDateClass(ss) {
      if (!ss.due_date) return "";
      if (ss.status === "final" && ss.emailHistory && ss.emailHistory.length > 0) return "text-success";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(ss.due_date + "T00:00:00");
      const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) return "text-danger font-weight-bold";
      if (daysUntil <= 7) return "text-warning font-weight-bold";
      return "";
    },
    async viewStrategyPdf(strategy) {
      this.$set(strategy, '_viewingPdf', true);
      try {
        const blob = await supportStrategiesService.downloadSupportStrategyPdf(strategy.id);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch {
        this.$notifyError('Failed to load PDF');
      } finally {
        this.$set(strategy, '_viewingPdf', false);
      }
    },
    async downloadStrategyPdf(ss) {
      this.$set(ss, '_downloading', true);
      try {
        const blob = await supportStrategiesService.downloadSupportStrategyPdf(ss.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const clientName = ss.client ? `${ss.client.first_name}_${ss.client.last_name}` : 'Unknown';
        link.download = `Support_Strategy_${clientName}_${ss.pcsp_start_date || 'undated'}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch {
        this.$notifyError('Failed to download PDF');
      } finally {
        this.$set(ss, '_downloading', false);
      }
    },

    formatQuarter(startDate, endDate) {
      if (!startDate) return '';
      const month = parseInt(startDate.split('-')[1], 10);
      const year = startDate.split('-')[0];
      const qMap = { 1: 'Q1', 4: 'Q2', 7: 'Q3', 10: 'Q4' };
      const q = qMap[month] || `${startDate} - ${endDate}`;
      return `${q} ${year}`;
    },

    parseQrServiceCodes(json) {
      try { return JSON.parse(json || '[]').join(', '); }
      catch { return ''; }
    },

    async downloadQuarterlyPdf(qr) {
      this.$set(qr, '_downloading', true);
      try {
        const data = await quarterlyReportsService.downloadQuarterlyReportPdf(qr.id);
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Quarterly_Report_${qr.start_date}_${qr.end_date}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch {
        this.$notifyError('Failed to download PDF');
      } finally {
        this.$set(qr, '_downloading', false);
      }
    },

    async viewPdf(filename, endpoint, displayName) {
      try {
        // Clean up any existing PDF URL
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl);
          this.pdfUrl = null;
        }

        this.selectedPdfName = displayName;
        this.loadingPdf = true;
        this.$modal.open("view_client_document_pdf");

        // Use the authenticated API endpoint
        const apiBaseUrl =
          process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/${endpoint}/${encodeURIComponent(filename)}`;

        // Get JWT token from localStorage
        const token = localStorage.getItem("jwtToken");

        // Fetch the PDF with authentication
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: token,
            Accept: "application/pdf",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to load document: ${response.status} ${response.statusText}`,
          );
        }

        // Convert to blob and create object URL
        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        this.pdfUrl = URL.createObjectURL(pdfBlob);
        this.loadingPdf = false;
      } catch (error) {
        this.$notifyError("Error loading document: " + error.message);
        this.$modal.close("view_client_document_pdf");
        this.loadingPdf = false;
      }
    },

    async uploadDocument() {
      if (!this.canUpload) return;

      this.uploading = true;
      try {
        if (this.newDocument.type === "file") {
          // Upload file
          const formData = new FormData();
          formData.append("file", this.newDocument.file);
          formData.append("clientId", this.clientId);
          formData.append("category", this.newDocument.category);
          formData.append("displayName", this.newDocument.displayName);
          if (this.newDocument.expirationDate) {
            formData.append("expirationDate", this.newDocument.expirationDate);
          }

          const response = await documentsService.uploadClient(formData);

          // Add the new document to the appropriate category
          const category = this.newDocument.category;
          this.documents[category].unshift(response.document);

          this.$notifySuccess("Document uploaded successfully");
        } else {
          // Add link - ensure URL has proper protocol
          const normalizedUrl = this.ensureUrlProtocol(this.newDocument.url);
          const response = await documentsService.addClientLink({
            clientId: this.clientId,
            category: this.newDocument.category,
            displayName: this.newDocument.displayName,
            url: normalizedUrl,
            expirationDate: this.newDocument.expirationDate || undefined,
          });

          // Add the new document to the appropriate category
          const category = this.newDocument.category;
          this.documents[category].unshift(response.document);

          this.$notifySuccess("Link added successfully");
        }

        // Refresh the support strategy tracker if a support strategy document was uploaded
        if (this.newDocument.category === "SupportStrategy" && this.$refs.supportStrategyTracker) {
          this.$refs.supportStrategyTracker.refresh();
        }

        // Reset form
        this.newDocument = {
          category: "",
          displayName: "",
          type: "file",
          url: "",
          file: null,
          expirationDate: null,
        };
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      } catch (error) {
        this.$notifyError(
          "Error uploading document: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.uploading = false;
      }
    },

    async deleteDocument(documentId) {
      try {
        const confirmed = await this.$confirmAction(
          () => {},
          "Are you sure you want to delete this document?",
        );

        if (!confirmed) return;

        await documentsService.deleteClientDocument(documentId);

        // Remove the document from the appropriate category
        let deletedCategory = null;
        ["CST", "Training", "BSP", "SupportStrategy", "Compliance"].forEach((category) => {
          const index = this.documents[category].findIndex(
            (doc) => doc.id === documentId,
          );
          if (index !== -1) {
            this.documents[category].splice(index, 1);
            deletedCategory = category;
          }
        });

        // Refresh the support strategy tracker if a support strategy document was deleted
        if (deletedCategory === "SupportStrategy" && this.$refs.supportStrategyTracker) {
          this.$refs.supportStrategyTracker.refresh();
        }

        this.$notifySuccess("Document deleted successfully");
      } catch (error) {
        this.$notifyError(
          "Error deleting document: " +
            (error.response?.data?.error || error.message),
        );
      }
    },

    onSupportStrategyDocumentGenerated(document) {
      this.documents.SupportStrategy.unshift(document);
      if (this.$refs.supportStrategyTracker) {
        this.$refs.supportStrategyTracker.refresh();
      }
      // Refresh goals so new goal questions from the strategy are available
      this.loadClientGoals();
    },

    // Document expiration methods
    getExpirationClass(expirationDate) {
      if (!expirationDate) return '';
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const expDate = new Date(expirationDate + 'T00:00:00');
      const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) return 'expiration-expired';
      if (diffDays <= 7) return 'expiration-urgent';
      if (diffDays <= 30) return 'expiration-warning';
      return 'expiration-ok';
    },

    getExpirationLabel(expirationDate) {
      if (!expirationDate) return '';
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const expDate = new Date(expirationDate + 'T00:00:00');
      const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
      const formatted = expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      if (diffDays <= 0) return `Expired ${formatted}`;
      if (diffDays === 1) return `Expires tomorrow (${formatted})`;
      if (diffDays <= 30) return `Expires in ${diffDays} days (${formatted})`;
      return `Expires ${formatted}`;
    },

    startEditExpiration(doc) {
      this.editingExpirationDocId = doc.id;
      this.editingExpirationDate = doc.expiration_date || null;
    },

    cancelEditExpiration() {
      this.editingExpirationDocId = null;
      this.editingExpirationDate = null;
    },

    async saveExpiration(doc) {
      try {
        const response = await documentsService.updateDocumentExpiration(
          doc.id,
          this.editingExpirationDate,
        );
        doc.expiration_date = this.editingExpirationDate;
        doc.expiration_notifications_sent = null;
        this.editingExpirationDocId = null;
        this.editingExpirationDate = null;
        this.$notifySuccess("Expiration date updated");
      } catch (error) {
        this.$notifyError(
          "Error updating expiration date: " +
            (error.response?.data?.error || error.message),
        );
      }
    },

    // Training methods
    async loadTrainingRecords() {
      this.loadingTraining = true;
      try {
        this.trainingRecords = await caregiverTrainingService.getByClient(
          this.clientId,
        );

        // Check current user's training status
        const loginStore = useLoginStore();
        const currentUserId = loginStore.user?.id;
        if (currentUserId) {
          const currentUserRecord = this.trainingRecords.find(
            (record) => record.id_users === currentUserId,
          );
          this.currentUserTrainingStatus = currentUserRecord
            ? currentUserRecord.status
            : null;
        }
      } catch (error) {
        this.$notifyError("Error loading training records: " + error);
      } finally {
        this.loadingTraining = false;
      }
    },

    openTrainingSubmissionModal() {
      this.showTrainingSubmissionModal = true;
    },

    async submitTrainingCompletion() {
      try {
        this.$spinner.show();
        await caregiverTrainingService.submit(this.clientId);
        this.$notifySuccess(
          "Training completion submitted! An admin will review your request.",
        );
        await this.loadTrainingRecords(); // Reload to update status
        this.showTrainingSubmissionModal = false;
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || error.message || error;
        this.$notifyError("Error submitting training: " + errorMessage);
      } finally {
        this.$spinner.hide();
      }
    },

    async approveTraining(record) {
      try {
        this.$spinner.show();
        await caregiverTrainingService.review(record.id, "approved");
        this.$notifySuccess("Training approved successfully");
        await this.loadTrainingRecords(); // Reload the training records
      } catch (error) {
        this.$notifyError("Error approving training: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    async rejectTraining(record) {
      try {
        this.$spinner.show();
        await caregiverTrainingService.review(record.id, "rejected");
        this.$notifySuccess("Training rejected");
        await this.loadTrainingRecords(); // Reload the training records
      } catch (error) {
        this.$notifyError("Error rejecting training: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    // User access methods
    async loadUserAccess() {
      this.loadingUserAccess = true;
      try {
        this.userAccessRecords = await userClientAccessService.getByClient(this.clientId);
      } catch (error) {
        this.$notifyError("Error loading user access: " + error);
      } finally {
        this.loadingUserAccess = false;
      }
    },

    async revokeUserAccess(record) {
      if (!record.user) return;
      const name = `${record.user.first_name} ${record.user.last_name}`;
      if (!confirm(`Revoke ${name}'s access to this client?`)) return;
      try {
        await userClientAccessService.revoke(record.user.id, this.clientId);
        this.$notifySuccess(`${name}'s access revoked`);
        await this.loadUserAccess();
      } catch (error) {
        this.$notifyError("Error revoking access: " + error);
      }
    },

    async grantUserAccessBulk() {
      if (this.selectedUsersToGrant.length === 0) return;
      this.grantingAccess = true;
      try {
        for (const userId of this.selectedUsersToGrant) {
          await userClientAccessService.grant(userId, this.clientId);
        }
        this.$notifySuccess(`Access granted to ${this.selectedUsersToGrant.length} user(s)`);
        this.showGrantUserAccessModal = false;
        this.selectedUsersToGrant = [];
        await this.loadUserAccess();
      } catch (error) {
        this.$notifyError("Error granting access: " + error);
      } finally {
        this.grantingAccess = false;
      }
    },

    formatAccessDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },
  },

  beforeDestroy() {
    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
    }
  },
};
</script>

<style scoped>
/* User Access Tab */
.user-access-section h5 {
  color: #fff;
  font-weight: 600;
}

.user-access-section .form-check-label {
  color: #ccc;
}

.upload-intake-body {
  padding: 0 1rem;
}

.upload-intake-body .text-muted {
  color: #b3a6a6;
  margin-bottom: 1rem;
}

.upload-intake-field {
  margin-bottom: 1rem;
}

.upload-intake-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #e0e0e0;
}

.upload-intake-field input[type="file"] {
  width: 100%;
  color: #ccc;
}

.grant-user-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;
  margin-bottom: 2px;
}

.grant-user-row:hover {
  background: rgba(255, 255, 255, 0.08);
}

.grant-user-row--selected {
  background: rgba(107, 166, 205, 0.2);
  border-left: 3px solid #6ba6cd;
}

.grant-user-row--selected .form-check-label {
  color: #fff;
  font-weight: 600;
}

/* Face Sheet / Overview Tab */
.face-sheet {
  max-width: 100%;
  margin: 0;
}

.face-sheet-header {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.face-sheet-photo {
  position: relative;
  cursor: pointer;
}

.face-sheet-photo .client-photo {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #447DF7;
}

.face-sheet-photo .client-photo-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: #b3a6a6;
}

.face-sheet-photo .photo-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #fff;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.face-sheet-photo:hover .photo-overlay {
  opacity: 1;
}

.face-sheet-name h2 {
  margin: 0 0 4px 0;
  font-size: 28px;
  font-weight: 600;
  color: #fff;
}

.face-sheet-name .goes-by {
  color: #b3a6a6;
  font-size: 16px;
  font-style: italic;
}

.face-sheet-badges {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.face-sheet-badges .badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.face-sheet-badges .badge-info {
  background: rgba(68, 125, 247, 0.2);
  color: #7eabff;
}

.face-sheet-badges .badge-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: #b3a6a6;
}

.face-sheet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

.face-sheet-card {
  background: rgba(37, 55, 65, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
}

.face-sheet-card-wide {
  grid-column: 1 / -1;
}

.face-sheet-card h5 {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 8px;
}

.face-sheet-card h5 i {
  margin-right: 8px;
  color: #447DF7;
}

.face-sheet-field {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.face-sheet-field label {
  color: #b3a6a6;
  font-weight: 500;
  margin: 0;
  min-width: 120px;
}

.face-sheet-field span {
  color: #e0e0e0;
  text-align: right;
}

.face-sheet-address {
  margin-bottom: 12px;
  font-size: 14px;
  color: #e0e0e0;
}

.face-sheet-address .address-label {
  font-weight: 600;
  color: #b3a6a6;
  margin-bottom: 2px;
}

.face-sheet-contacts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.face-sheet-contact {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 12px;
}

.face-sheet-contact .contact-name {
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  margin-bottom: 4px;
}

.face-sheet-contact .contact-type-badge {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 3px;
  background: rgba(68, 125, 247, 0.2);
  color: #7eabff;
  margin: 4px 0;
}

.face-sheet-contact .contact-detail {
  font-size: 13px;
  color: #b3a6a6;
}

.face-sheet-contact .contact-detail i {
  width: 16px;
  margin-right: 4px;
  color: #b3a6a6;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.back-button {
  font-size: 14px;
  padding: 8px 16px;
  height: 40px;
}

.back-button i {
  margin-right: 6px;
}

.client-name {
  margin: 0;
  flex: 0 0 auto;
}

.button-toggle-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  margin: 0;
  margin-top: -3px;
  flex-wrap: wrap;
}

.toggle-button {
  border-radius: 0;
  min-width: 120px;
  margin: 0;
}

.toggle-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.toggle-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.toggle-button:not(:first-child) {
  border-left: none;
}

:deep(.toggle-button.el-button--default) {
  background-color: #606266;
  border-color: #606266;
  color: #fff;
}

:deep(.toggle-button.el-button--default:hover) {
  background-color: #737373;
  border-color: #737373;
  color: #fff;
}

:deep(.toggle-button.el-button--primary) {
  background-color: #447DF7;
  border-color: #447DF7;
  color: #fff;
}

.client-tab-content {
  margin-top: 1rem;
}

.client-tab-pane {
  padding: 0;
  color: #fff;
  min-height: 200px;
}

.client-tab-pane > p {
  color: #fff;
  font-size: 1rem;
  margin: 0;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .button-toggle-row {
    flex-wrap: wrap;
  }

  .toggle-button {
    min-width: 100px;
    font-size: 0.875rem;
  }
}

/* PDF Modal Styles */
/* Scoped under .pdf-verification-section so the Document Modal's own
   .pdf-viewer-container rule (further down) doesn't override this one.
   95vh dialog minus header/margins (150px) and body padding (40px) —
   the PDF nearly fills the modal when scrolled into view below the form */
.pdf-verification-section .pdf-viewer-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  height: calc(95vh - 190px);
  max-height: none;
  overflow: hidden;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
}

/* No max-height/overflow here: the dialog body is the single scroll
   context — a nested form scrollbar made modals feel unscrollable,
   especially on touch devices */
.verification-form {
  width: 100%;
}

@media (max-width: 991.98px) {
  .pdf-verification-section .pdf-viewer-container {
    height: 65vh;
  }
}

.pdf-verification-section {
  max-width: 100%;
  /* The dialog body already pads 20px — no second layer */
  padding: 0;
}

/* Calendar Styles */
.modern-calendar-wrapper {
  width: 100%;
  margin: 0;
}

/* Modern Legend Row */
.modern-legend-row {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legend-groups {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6ba6cd;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.legend-group-header i {
  font-size: 0.75rem;
  width: 12px;
  text-align: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0;
  transition: color 0.2s ease;
}

.legend-item:hover {
  color: #fff;
}

.legend-color {
  width: 14px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.legend-item:hover .legend-color {
  transform: scale(1.1);
}

/* Status Colors */
.legend-color.status-unassigned {
  background: linear-gradient(
    135deg,
    rgba(32, 201, 151, 0.7),
    rgba(32, 201, 151, 0.5)
  );
}

.legend-color.status-scheduled {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  );
}

.legend-color.status-clocked-in {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  );
}

.legend-color.status-clocked-out {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  );
}

.legend-color.status-approved {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  );
}

.legend-color.status-missed {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  );
}

/* Calendar-specific styles */
:deep(.custom-calendar) {
  width: 100%;
}

:deep(.calendar-grid) {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

:deep(.day-name) {
  font-weight: bold;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

:deep(.calendar-day) {
  position: relative;
  background: transparent;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 80px;
  padding: 4px;
  cursor: pointer;
}

:deep(.calendar-day:nth-child(7n)) {
  border-right: none;
}

:deep(.calendar-day:nth-last-child(-n + 7)) {
  border-bottom: none;
}

:deep(.day-name:nth-child(7n)) {
  border-right: none;
}

:deep(.date-number) {
  font-size: 0.85rem;
  font-weight: bold;
  color: #fff;
}

:deep(.today) {
  background-color: rgba(107, 166, 205, 0.2);
  border: 2px solid #6ba6cd;
}

:deep(.other-month) {
  opacity: 0.4;
}

:deep(.shift-item) {
  margin-top: 2px;
  font-size: 0.7rem;
  color: #fff;
  padding: 4px 6px;
  min-height: 24px;
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
}

:deep(.shift-time) {
  font-weight: bold;
  flex-shrink: 0;
}

:deep(.shift-client) {
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.9;
}

:deep(.shift-caregiver) {
  font-size: 0.6rem;
  color: #e0e0e0;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

.calendar-view {
  margin-top: 1rem;
  width: 100%;
}

/* Month Navigation */
.month-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 36px;
  width: 36px;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.nav-btn i {
  font-size: 12px;
}

.current-month-display {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  min-width: 200px;
  text-align: center;
  letter-spacing: 0.5px;
}

.client-schedule-container {
  width: 100%;
  margin: 0;
  padding: 0;
}

.schedule-content {
  margin-top: 1rem;
  padding: 0;
  width: 100%;
}

@media (max-width: 768px) {
  .modern-legend-row {
    padding: 0.5rem;
  }

  .legend-groups {
    gap: 1rem;
  }

  .month-navigation {
    gap: 1rem;
    padding: 0.5rem;
  }

  .current-month-display {
    font-size: 1rem;
    min-width: 150px;
  }

  .calendar-view {
    padding: 0.25rem;
  }

  :deep(.calendar-day) {
    min-height: 80px !important;
    padding: 4px !important;
  }

  :deep(.shift-item) {
    font-size: 0.6rem !important;
    padding: 2px 3px !important;
  }

  :deep(.shift-client) {
    display: none !important;
  }

  :deep(.shift-caregiver) {
    display: none !important;
  }

  :deep(.day-name) {
    font-size: 0.65rem !important;
    padding: 6px 2px !important;
  }

  :deep(.date-number) {
    font-size: 0.75rem !important;
  }
}

@media (max-width: 480px) {
  :deep(.calendar-day) {
    min-height: 60px !important;
    padding: 2px !important;
  }

  :deep(.shift-time) {
    font-size: 0.55rem !important;
  }
}

/* Notes Tab Styles */
.client-notes-container {
  width: 100%;
  margin: 0;
  padding: 0;
}

.notes-header-actions {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.add-note-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.add-note-button i {
  font-size: 14px;
}

.note-form {
  padding: 0 1.5rem;
}

.all-notes-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 600px;
  overflow-y: auto;
}

.note-card {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.note-date-time {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-badge-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.note-type-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0;
}

.general-badge {
  background-color: rgba(59, 130, 246, 0.3);
  color: #93c5fd;
  border: 1px solid rgba(59, 130, 246, 0.5);
}

.shift-badge {
  background-color: rgba(34, 197, 94, 0.3);
  color: #86efac;
  border: 1px solid rgba(34, 197, 94, 0.5);
}

.schedule-badge {
  background-color: rgba(168, 85, 247, 0.3);
  color: #c4b5fd;
  border: 1px solid rgba(168, 85, 247, 0.5);
}

.note-header strong {
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
}

.time-range {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.caregiver-name {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  align-self: flex-end;
}

.activities-section {
  margin-bottom: 1rem;
}

.activities-section strong {
  color: #fff;
  display: block;
  margin-bottom: 0.5rem;
}

.activities-section div {
  color: rgba(255, 255, 255, 0.9);
}

.notes-section {
  margin-bottom: 0.5rem;
}

.shift-notes {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  white-space: pre-wrap;
  margin: 0;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: inherit;
}

.no-notes {
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  padding: 2rem;
}

@media (max-width: 768px) {
  .note-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .time-range {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .caregiver-name {
    align-self: flex-start;
  }
}

/* Client Details Tab Styles */
.client-details-content {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.client-details-content .address-section {
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.client-details-content .address-block {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.client-details-content .address-header {
  padding-left: 8px;
}

.client-details-content .address-header h6 {
  margin: 0;
  font-weight: 600;
  color: #6ba6cd;
  font-size: 1rem;
  letter-spacing: 0.5px;
}

.client-details-content .add-address-button-container {
  padding-left: 8px;
  margin-bottom: 1rem;
}

.client-details-content :deep(.el-form-item__label) {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
}

.client-details-content :deep(.el-input__inner),
.client-details-content :deep(.el-textarea__inner) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.client-details-content :deep(.el-input__inner:focus),
.client-details-content :deep(.el-textarea__inner:focus) {
  background-color: rgba(255, 255, 255, 0.12);
  border-color: #6ba6cd;
}

.client-details-content :deep(.el-input__inner::placeholder),
.client-details-content :deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.4);
}

/* Calendar Header Actions */
.calendar-header-actions {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.add-shift-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.add-shift-button i {
  font-size: 14px;
}

/* Goals Section */
.goals-section {
  padding: 20px;
}

.goals-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.strategy-card {
  padding: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 10px;
  transition: all 0.2s ease;
}

.strategy-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
}

.strategy-draft {
  border-left: 3px solid #e6a23c;
}

.strategy-final {
  border-left: 3px solid #67c23a;
}

.pcsp-plan-year-info {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #e0e0e0;
}

.goal-card {
  padding: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.goal-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
}

.goal-type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.goal-type-badge.yes_no {
  background: rgba(68, 125, 247, 0.15);
  color: #447DF7;
  border: 1px solid rgba(68, 125, 247, 0.3);
}

.goal-type-badge.free_response {
  background: rgba(230, 162, 60, 0.15);
  color: #e6a23c;
  border: 1px solid rgba(230, 162, 60, 0.3);
}

.goal-question {
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
}

.goal-meta {
  font-size: 12px;
}

/* Goal Form Styles */
.goal-form :deep(.el-form-item__label) {
  color: #ffffff !important;
}

.question-type-toggle-section {
  margin-bottom: 1rem;
  padding: 0;
  background-color: transparent;
  border: none;
}

.question-type-toggle-section .button-toggle-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin: 0;
}

.question-type-toggle-section .toggle-button {
  border-radius: 0;
  min-width: 140px;
  margin: 0;
}

.question-type-toggle-section .toggle-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.question-type-toggle-section .toggle-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  border-left: none;
}

.goal-form :deep(.toggle-button.el-button--default) {
  background-color: #606266;
  border-color: #606266;
  color: #fff;
}

.goal-form :deep(.toggle-button.el-button--default:hover) {
  background-color: #737373;
  border-color: #737373;
  color: #fff;
}

.goal-form :deep(.toggle-button.el-button--primary) {
  background-color: #447DF7;
  border-color: #447DF7;
  color: #fff;
}

.goal-actions {
  display: flex;
  gap: 4px;
  align-items: flex-start;
  flex-shrink: 0;
}

.goal-actions .el-button {
  padding: 4px 8px;
}

.no-goals {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 32px;
  text-align: center;
}

.no-goals p {
  margin: 0;
  opacity: 0.7;
}

/* Documents Section */
.documents-section {
  /* No padding - let inner sections extend to edges */
}

.documents-upload-section {
  background: rgba(255, 255, 255, 0.08);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.documents-upload-section h5 {
  margin-bottom: 15px;
  color: #fff;
}

.documents-upload-section label {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  margin-bottom: 5px;
  display: block;
}

.document-category-section {
  background: rgba(255, 255, 255, 0.03);
  padding: 20px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.category-header {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}

.category-header-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}

.category-header-with-action .category-header {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
  flex: 1;
}

.training-action-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.training-status-badge {
  display: flex;
  align-items: center;
}

.training-status-badge .badge {
  font-size: 14px;
  padding: 6px 12px;
}

.submit-training-button {
  white-space: nowrap;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
}

.document-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.document-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.document-info i {
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
}

.document-name {
  color: #4fc3f7;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.document-name:hover {
  color: #81d4fa;
  text-decoration: underline;
}

.document-meta {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.document-expiration {
  font-size: 12px;
  font-weight: 500;
}

.document-expiration a {
  color: inherit;
  opacity: 0.7;
}

.document-expiration a:hover {
  opacity: 1;
}

.expiration-ok {
  color: rgba(255, 255, 255, 0.6);
}

.expiration-warning {
  color: #f57c00;
}

.expiration-urgent {
  color: #ff5252;
  font-weight: 600;
}

.expiration-expired {
  color: #ff1744;
  font-weight: 700;
}

.delete-button {
  margin-left: 10px;
}

.no-documents {
  text-align: center;
  padding: 30px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

.no-documents p {
  margin: 0;
}

.compliance-table {
  font-size: 13px;
  background: transparent;
  margin-bottom: 0;
}

.compliance-table thead th {
  border-top: none;
}

.compliance-table,
.compliance-table thead th,
.compliance-table tbody td {
  background: transparent !important;
}

/* PDF Viewer Styles for Document Modal */
.pdf-viewer-container {
  height: 80vh;
  max-height: calc(85vh - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pdf-viewer-frame {
  width: 100%;
  height: 100%;
  border: none;
}

.no-pdf {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

/* Contact sections for DSPD clients */
.contact-section {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
}

.contact-section-header {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #447DF7;
}

.contact-section .text-muted p {
  margin: 0;
  font-style: italic;
  color: rgba(255, 255, 255, 0.5);
}

.medication-edit-modal :deep(.el-dialog__body) {
  height: calc(80vh - 120px) !important;
  overflow-y: auto !important;
  padding: 20px !important;
}

.med-schedule-days {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.med-day-pill {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #999;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}

.med-day-pill:hover {
  border-color: #6ba6cd;
  color: #6ba6cd;
}

.med-day-pill--active {
  background: rgba(107, 166, 205, 0.2);
  border-color: #6ba6cd;
  color: #fff;
}

.med-day-times-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.med-day-times-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  border-left: 3px solid #6ba6cd;
}

.med-day-label {
  min-width: 36px;
  font-weight: 600;
  color: #6ba6cd;
  padding-top: 4px;
}

.med-times {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.med-time-entry {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 180px;
}

.med-schedule-cell {
  font-size: 12px;
  line-height: 1.4;
}

.med-schedule-days {
  display: flex;
  gap: 2px;
  margin-bottom: 2px;
}

.med-day-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.3);
}

.med-day-dot.active {
  background-color: #409eff;
  color: #fff;
}

.med-schedule-times {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.med-time-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  background-color: rgba(64, 158, 255, 0.15);
  color: #79bbff;
  font-size: 11px;
}

/* Hide placeholder field in payer section */
:deep(.row .col-md-4:has(label:empty)) {
  visibility: hidden !important;
}

:deep(.row .col-md-4:has(input[placeholder=""]:disabled)) {
  visibility: hidden !important;
}

.compliance-checkbox-group :deep(.el-checkbox__label) {
  color: #fff;
}
</style>
